// Зеркало Go-контрактов: services/internal/bus/contracts.go и internal/domain.
//
// Поддерживается вручную: генерации типов из Go-структур пока нет, поэтому любое
// изменение контракта правится в ДВУХ местах и обязательно одним коммитом
// (docs/bus-contracts.md, правило 4). Автоматической проверки расхождения нет.
//
// ВАЖНО про деньги: цены и размеры приходят СТРОКАМИ, а не числами. Go сериализует
// money.Amount строкой намеренно — JSON-число прошло бы здесь через IEEE-754 double
// и дало бы цену, не кратную tickSize. Не делайте Number(price) для расчётов.

export const SCHEMA_VERSION = 1;

export type MessageType =
  | "place_order"
  | "cancel_order"
  | "cancel_all"
  | "command_result"
  | "order_update"
  | "trade"
  | "book_snapshot"
  | "book_delta"
  | "profile_update"
  | "follow_signal"
  | "lock_signal"
  /** Операционная тревога (веха M8). НЕ торговая карточка: поломка стека. */
  | "ops_alert"
  /** Стук трейдера «окно изменилось»: панель накопления перечитывается. */
  | "market_flow_delta"
  | "pong";

/** Общая оболочка любого сообщения — и в шине, и в сокете до браузера. */
export interface Envelope<T = unknown> {
  v: number;
  type: MessageType;
  ts: string;
  trace_id?: string;
  /**
   * ID записи в Redis-стриме; web_api проставляет при раздаче в сокет.
   * По нему UI склеивает историю и живой поток без дублей (useFeed).
   */
  stream_id?: string;
  payload: T;
}

export type Side = "BUY" | "SELL";
export type OrderType = "GTC" | "GTD" | "FOK" | "FAK";

export type OrderStatus =
  | "PENDING"
  | "LIVE"
  | "MATCHED"
  | "DELAYED"
  | "UNMATCHED"
  | "CANCELED"
  | "REJECTED"
  // Биржа сняла заявку, потому что рынок разрешился (domain.OrderCanceledResolved):
  // штатный конец мейкерской заявки на пятиминутном окне.
  | "CANCELED_MARKET_RESOLVED"
  // Биржа сняла заявку как недействительную (domain.OrderInvalid), событием отмены.
  | "INVALID";

export type TradeStatus =
  | "MATCHED"
  | "MATCHED_NOT_BROADCASTED"
  | "MINED"
  | "CONFIRMED"
  | "RETRYING"
  | "FAILED";

export type WalletTier = "unknown" | "noise" | "retail" | "sharp" | "maker";

export interface Token {
  asset_id: string;
  outcome: string;
  outcome_index: number;
}

/**
 * Расписание комиссии рынка. Зеркало fees.Schedule.
 *
 * Отсутствующее расписание и нулевая ставка означают «НЕ ЗНАЕМ», а не «бесплатно»:
 * lib/fees.ts на таком расписании отказывается считать, и комиссия на экране
 * гаснет прочерком. Ноль вместо прочерка показал бы прибыль, которой не будет.
 */
export interface FeeSchedule {
  /**
   * Расписание доехало до нас: строка рынка прочитана из справочника.
   *
   * Отличает «рынок комиссии не берёт» от «рынка мы не знаем»: `enabled` у обоих
   * ложь, но первое — ответ, а второе его отсутствие. Прочерк на кнопке означает
   * «не посчитали», ноль — «платить не придётся».
   */
  synced?: boolean;
  enabled: boolean;
  /** crypto_fees_v2 — единственное расписание, на котором формула проверена. */
  type?: string;
  /** Десятичная строка: 0.07 у crypto. */
  rate?: string;
  taker_only: boolean;
  /** Наблюдали только 1; иное значение выключает расчёт. */
  exponent?: number;
  /** В цену сделки НЕ входит: возврат мейкеру раздаётся раз в сутки. */
  rebate_rate?: string;
  taker_base_fee?: number;
  maker_base_fee?: number;
}

export interface Market {
  condition_id: string;
  question: string;
  slug: string;
  tokens: Token[];
  /** Десятичная строка. */
  tick_size: string;
  min_order_size: string;
  neg_risk: boolean;
  active: boolean;
  closed: boolean;
  /** Отсутствует, когда расписание пустое: «не знаем», см. FeeSchedule. */
  fees?: FeeSchedule;
  tracked: boolean;
  updated_at: string;
}

export interface BookLevel {
  price: string;
  size: string;
}

export interface Book {
  asset_id: string;
  market: string;
  /** По убыванию цены. */
  bids: BookLevel[];
  /** По возрастанию цены. */
  asks: BookLevel[];
  tick_size: string;
  /** Непрозрачная метка CLOB. Воспроизвести её на клиенте нельзя, см. ADR-0005. */
  hash: string;
  timestamp: string;
  /** Стакан не подтверждён свежим снапшотом: рвалось соединение или замечен рассинхрон. */
  stale: boolean;
  /** Локальные часы трейдера: биржевой timestamp для оценки свежести непригоден. */
  received_at: string;
  last_trade_price?: string;
}

/**
 * Снапшот или дельта стакана: канал `md.book.<asset_id>` и ключ `book:<asset_id>`.
 *
 * У снапшота заполнен `book`, у дельты — `changes`. `size: "0"` означает УДАЛЕНИЕ
 * уровня, а не нулевой объём, а сам размер абсолютный — это новое состояние уровня,
 * а не приращение.
 *
 * `seq` растёт на единицу по каждому активу. Канал теряет сообщения по контракту,
 * поэтому порядок восстановления такой: буферизовать канал → прочитать ключ
 * (`seq = S`) → отбросить `seq <= S` → применять по порядку. Разрыв в нумерации
 * означает пропуск: перечитать ключ. Подробности — docs/bus-contracts.md.
 */
export interface BookUpdate {
  asset_id: string;
  condition_id: string;
  book?: Book;
  changes?: BookLevelChange[];
  hash?: string;
  seq: number;
}

export interface BookLevelChange {
  side: Side;
  price: string;
  /** "0" = уровень удалён. Величина абсолютная, а не приращение. */
  size: string;
}

export interface Order {
  client_order_id: string;
  order_id?: string;
  condition_id: string;
  asset_id: string;
  side: Side;
  price: string;
  size: string;
  size_matched: string;
  status: OrderStatus;
  order_type: OrderType;
  issued_by: string;
  reason?: string;
  /** Funder аккаунта, поставившего ордер; нет — строка до бэкфилла (ADR-0034). */
  account?: string;
  /**
   * Срок GTD-ордера, как его задал ОПЕРАТОР, без вычета запаса биржи (она гасит
   * GTD на минуту раньше). Отсутствует у остальных типов.
   *
   * Панель открытых ордеров без этого поля — ловушка: GTD-ордер исчезает сам, и
   * «он был тут только что» превращается в подозрение на сбой.
   */
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

/** Наша роль в сделке. */
export type TraderSide = "TAKER" | "MAKER";

/**
 * Встречный ордер сделки: кто, сколько и по какой цене (веха M6).
 *
 * `ours` — это НАША собственная строка. Она остаётся в списке, потому что
 * именно она отвечает, сколько налили нам: price и size самой сделки описывают
 * тейкер-ордер целиком и в мейкерской сделке принадлежат чужому исходу.
 */
export interface TradeFill {
  order_id: string;
  address: string;
  size: string;
  price: string;
  asset_id?: string;
  outcome?: string;
  side: Side;
  ours?: boolean;
  /** Тир НА МОМЕНТ СДЕЛКИ; пусто — «тогда не знали», а не «unknown». */
  tier?: WalletTier;
}

export interface Trade {
  id: string;
  condition_id: string;
  asset_id: string;
  /** Сторона ТЕЙКЕР-ордера сделки. Наша сторона видна по trader_side. */
  side: Side;
  price: string;
  size: string;
  notional: string;
  status?: TradeStatus;
  tx_hash?: string;
  /**
   * Все встреченные мейкеры сделки. Контрагенты они ТОЛЬКО когда trader_side
   * равен TAKER: в мейкерской сделке это соседи по сносу, а настоящая другая
   * сторона (агрессор) на проводе отсутствует — от неё есть лишь
   * taker_order_id. См. ADR-0007 §1.
   */
  counterparties?: string[];
  trader_side?: TraderSide;
  taker_order_id?: string;
  /**
   * Кошелёк агрессора, восстановленный из квитанции транзакции (ADR-0008).
   *
   * Пусто означает РАЗНОЕ в зависимости от роли, и путать это нельзя: в
   * МЕЙКЕРСКОЙ сделке — «ещё не восстановлен» (адрес приезжает секундами позже
   * отдельной публикацией той же сделки), в ТЕЙКЕРСКОЙ — «тейкером были мы
   * сами», и адреса здесь не будет никогда.
   *
   * В мейкерской сделке это и есть НАСТОЯЩИЙ контрагент — в отличие от fills,
   * где лежат соседи по сносу.
   */
  taker_address?: string;
  /** Наш филл: размер и цена, а не тейкер-ордер целиком. */
  our_size?: string;
  our_price?: string;
  /**
   * Slug окна («btc-updown-5m-1786150800») из справочника рынков.
   *
   * Свойство РЫНКА, а не сделки: заполняется при чтении и пусто, если рынка в
   * справочнике нет (окно, через которое трейдер не прокатывался). Нужен для
   * показа — `0x6108d59a…` не говорит человеку ни про актив, ни про время окна.
   */
  slug?: string;
  /**
   * Ставка комиссии, как её сообщает ПРОВОД. Величина наблюдательная: биржа
   * присылает здесь ноль, и комиссия считается по расписанию рынка, а не по ней.
   */
  fee_rate_bps?: string;
  /**
   * Комиссия ВСЕЙ сделки — её платит тейкер, кто бы им ни был.
   *
   * Отсутствие поля и ноль — разные утверждения: отсутствие означает «расписание
   * рынка неизвестно, считать было нечем», а ноль — что комиссия действительно
   * нулевая. Показывать первое как ноль значило бы объявить сделку бесплатной
   * ровно там, где про неё ничего не известно.
   */
  fee?: string;
  /** Наша доля комиссии: равна fee у тейкера и ноль у мейкера. */
  our_fee?: string;
  fills?: TradeFill[];
  ours: boolean;
  /** Какой из наших аккаунтов участвовал; нет — строка до бэкфилла. */
  account?: string;
  timestamp: string;
}

export interface TradesResponse {
  trades: Trade[];
  truncated: boolean;
}

export interface TradeEvent {
  trade: Trade;
  counterparty_tiers?: Record<string, WalletTier>;
}

export interface OrderUpdate {
  order: Order;
}

export interface CommandResult {
  client_order_id: string;
  accepted: boolean;
  order_id?: string;
  error?: string;
  /**
   * «Ответ биржи не получен» — не отказ: ордер остаётся PENDING, судьбу
   * выясняет сверка трейдера. accepted при этом false, но показать REJECTED
   * значило бы спровоцировать второй ордер на те же деньги.
   */
  unresolved?: boolean;
  latency_ms: number;
}

/** Окно, за которое посчитаны метрики кошелька. */
export type WalletHorizon = "1h" | "24h" | "all";

/**
 * Метрики кошелька за один горизонт.
 *
 * ЕДИНИЦЫ: volume и avg_size — В ДОЛЛАРАХ (сумма нотионала и нотионал на
 * сделку), а не в share. Так меряет весь терминал с вехи M5. Имя avg_size
 * подсказывает share — и подсказывает неверно.
 */
export interface WalletMetrics {
  horizon: WalletHorizon;
  trade_count: number;
  /** Сколько из них кошелёк был тейкером: числитель доли агрессии. */
  taker_count: number;
  volume: string;
  avg_size: string;
  aggression_ratio: string;
  updated_at: string;
}

/**
 * Состояние ленивой выгрузки истории кошелька через Data API.
 *
 * Отвечает не «какой этот кошелёк», а «насколько мы вправе верить метрикам»:
 * тир, посчитанный по обрывку истории, выглядит так же уверенно, как по всей,
 * поэтому незавершённая выгрузка с ошибкой обязана быть видна на экране.
 */
export interface WalletBackfill {
  offset: number;
  done: boolean;
  attempts: number;
  last_error?: string;
  updated_at: string;
}

/**
 * Профиль кошелька-контрагента.
 *
 * Плоские метрики здесь — это горизонт 24ч: именно на сутки смотрит скальпер,
 * решая, стоит ли встречный ордер против этого кошелька. Полная таблица по всем
 * горизонтам приходит отдельным полем ответа GET /wallets/{address}.
 */
export interface WalletProfile {
  address: string;
  label?: string;
  trade_count: number;
  volume: string;
  avg_size: string;
  aggression_ratio: string;
  tier: WalletTier;
  backfilled: boolean;
  first_seen_at: string;
  last_seen_at: string;
  updated_at: string;
}

/** Тело GET /api/v1/wallets/{address}. */
export interface WalletResponse {
  profile: WalletProfile;
  /** Всегда все три горизонта, в том числе нулевые. */
  metrics: WalletMetrics[];
  backfill: WalletBackfill;
  /** Профиль пришёл из горячего ключа трейдера, а не из Postgres. */
  hot: boolean;
}

/** Тело GET /api/v1/wallets/{address}/trades. */
export interface WalletTradesResponse {
  trades: Trade[];
  truncated: boolean;
}

/** Полезная нагрузка сообщения profile_update канала md.profiles. */
export interface ProfileUpdate {
  profile: WalletProfile;
}

/** Тело POST /api/v1/orders. Цена и размер — СТРОКИ (правило 3 контракта). */
export interface PlaceOrderRequest {
  condition_id: string;
  asset_id: string;
  side: Side;
  price: string;
  size: string;
  order_type: OrderType;
  /** Только для GTD, ISO-8601. У остальных типов поле обязано отсутствовать. */
  expires_at?: string;
  /**
   * «Встань в стакан или не вставай вовсе»: биржа отвергает перекрывший стакан
   * ордер целиком вместо того, чтобы исполнить его тейкером.
   *
   * Единственный способ ПООБЕЩАТЬ роль мейкера, а не понадеяться на неё: пока
   * команда идёт через шину, уровень успевает сдвинуться, и ордер по
   * фиксированной цене время от времени платил бы комиссию там, где вся прибыль
   * сделки — цент.
   *
   * Допустим только у GTC/GTD; web_api отвергает пару с FOK/FAK.
   */
  post_only?: boolean;
}

/**
 * Ответ 202 всех трёх командных эндпоинтов.
 *
 * trace_id — ключ, по которому UI снимает состояние «отправляется»: трейдер
 * протаскивает его в конверт command_result (docs/bus-contracts.md). Именно
 * trace_id, а не client_order_id: у cancel_all ордера нет вовсе, и по
 * client_order_id такая команда не сматчилась бы никогда.
 */
export interface CommandAccepted {
  trace_id: string;
  /** Пусто у cancel_all. */
  client_order_id?: string;
  type: "place_order" | "cancel_order" | "cancel_all";
  accepted_at: string;
  /**
   * От чьего имени команда (funder). По нему панель раскладывает ответы
   * команды когортой; пуст у команд без аккаунта (пропуск сигнала).
   */
  account?: string;
}

/**
 * Цель команды: ровно одно из двух полей (зеркало commandTarget в web_api).
 *
 * Объединением, а не двумя необязательными полями: «ни того, ни другого» и
 * «оба сразу» web_api отвергает 400, и тип не даёт собрать такой запрос.
 * Умолчания нет даже при одном аккаунте (инвариант 4 roadmap-accounts):
 * второй аккаунт, появившись, молча унаследовал бы чужие нажатия.
 */
export type CommandTarget = { account: string } | { account_cohort: string };

/** Команда когорты, не ушедшая в шину. */
export interface CommandRejected {
  account: string;
  error: string;
}

/**
 * Ответ 202 постановки и «снять все»: по команде на аккаунт цели.
 *
 * Список даже при одном аккаунте: когорта раскрывается в web_api, и форма
 * ответа не должна зависеть от того, что выбрал оператор.
 */
export interface CommandsAccepted {
  commands: CommandAccepted[];
  /** Аккаунты когорты, чья команда не ушла; остальные приняты. */
  errors: CommandRejected[] | null;
}

/** Строка состава аккаунтов (GET /api/v1/accounts). */
export interface AccountRow {
  slot: string;
  /** Funder, нижний регистр — им команды и адресуются. */
  funder: string;
  /** Заявленное сервисом в sidecar: enabled | draining. */
  state: string;
  /** Применённое трейдером; null — трейдер не ответил или слот не загружен. */
  state_applied: string | null;
  /** null — применение неизвестно (трейдер молчит). */
  loaded: boolean | null;
}

/**
 * Состав аккаунтов набора.
 *
 * known=false — состав НЕИЗВЕСТЕН (нет ни sidecar'а, ни трейдера), а не пуст:
 * пустой список при known=false не значит «аккаунтов нет».
 */
export interface AccountsResponse {
  known: boolean;
  /** Откуда состав: sidecar сервиса или, без него, применённое трейдером. */
  source?: "sidecar" | "trader";
  reason?: string;
  detail?: string;
  profile_version: number | null;
  /** Ответил ли трейдер недавно: без этого loaded и state_applied — null. */
  applied_known: boolean;
  applied_at?: string;
  accounts: AccountRow[];
}

/**
 * Когорта аккаунтов (T5.5): именованный список funder'ов, понятие системы.
 * Команда когортой раскрывается в web_api в команду на каждый годный аккаунт
 * (режим each, Д-5): каждый ставит полный размер, без деления.
 */
export interface AccountCohort {
  /** a-z, 0-9, _ и -, до 32 символов. */
  name: string;
  /** Funder'ы, нижний регистр; выбывший из состава раскрытие пропускает. */
  accounts: string[];
  updated_at?: string;
}

/** Ответ GET и PUT /api/v1/account-cohorts. */
export interface AccountCohortsResponse {
  cohorts: AccountCohort[];
}

/** Подсистема, торгующая сама: у каждой — один аккаунт (Д-6, T5.4). */
export type Subsystem = "automaker" | "follow" | "lock";

/** Стратегии с привязкой strategy:<id>:account. */
export type StrategySubsystem = "decided" | "family" | "mispriced" | "consensus";

/** Привязка одной подсистемы к аккаунту. */
export interface SubsystemBinding {
  /** Funder; null — не назначен, подсистема не торгует. */
  account: string | null;
  /** Слот по составу; нет — аккаунт выбыл из набора или состав неизвестен. */
  slot?: string;
  /**
   * Нота трейдера <подсистема>_account как есть: «A01», «не назначен»,
   * «A02 (осушается)». Её говорит тот, кто по привязке торгует; нет — трейдер
   * не ответил.
   */
  note?: string;
}

/** Ответ GET и PUT /api/v1/subsystem-accounts. */
export interface SubsystemAccounts {
  automaker: SubsystemBinding;
  follow: SubsystemBinding;
  lock: SubsystemBinding;
  strategies: Partial<Record<StrategySubsystem, SubsystemBinding>>;
}

/**
 * Тело PUT /api/v1/subsystem-accounts — частичное: ключ вне тела web_api не
 * трогает. null или "" снимает привязку.
 */
export type SubsystemAccountsPatch = Partial<Record<Subsystem | StrategySubsystem, string | null>>;

/** Аккаунт в /status: состав, применение и стоп (T5.6). */
export interface AccountStatus extends AccountRow {
  /**
   * Стоп аккаунта, как его применяет трейдер (нота
   * acct.<слот>.kill_switch_account); null — трейдер не ответил или аккаунт не
   * загружен.
   */
  kill_switch: boolean | null;
  /** Ключ risk:kill_switch:<funder>, который пишет web_api; null — ключа нет. */
  kill_switch_ui: boolean | null;
}

/** Стоп одного аккаунта в ответе /risk/kill-switch. */
export interface AccountKillSwitch {
  slot: string;
  funder: string;
  kill_switch: KillSwitchState;
}

/** Ответ GET и POST /api/v1/risk/kill-switch: общий стоп и стопы аккаунтов. */
export interface KillSwitches {
  kill_switch: KillSwitchState;
  accounts: AccountKillSwitch[];
}

/** Ответ GET/POST /api/v1/risk/kill-switch. */
export interface KillSwitchState {
  on: boolean;
  /**
   * false = ключа в Redis нет: оператор ничего не нажимал, и судьбу торговли
   * определяет окружение трейдера. Это НЕ то же, что on: false.
   */
  set: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

/** Ответ GET /api/v1/orders. */
export interface OrdersResponse {
  orders: Order[];
  /**
   * Список обрезан по limit. Флаг есть только здесь: обрезанный справочник
   * рынков безвреден, а обрезанный список открытых ордеров означает, что
   * оператор считает свою позицию меньше, чем она есть.
   */
  truncated: boolean;
}

/**
 * Позиция оператора по одному исходу. Зеркало dataapi.Position.
 *
 * Не хранится нигде: таблицы позиций в системе нет. Источник —
 * data-api.polymarket.com, который индексирует блокчейн и потому ОТСТАЁТ от
 * факта исполнения на секунды. Всё, что построено на этом типе, обязано
 * показывать возраст снимка (см. PositionsResponse.as_of).
 */
export interface Position {
  asset_id: string;
  condition_id: string;
  /** В SHARE, строкой. Именно из этого числа считается размер продажи. */
  size: string;
  avg_price: string;
  cur_price: string;
  outcome: string;
  outcome_index: number;
  /** Рынок разрешился: позицию погашают, а не продают — кнопок продажи нет. */
  redeemable: boolean;
  /**
   * Откуда взят `size`.
   *
   * `chain` — баланс ERC-1155 нашего кошелька, то есть ровно то, что биржа
   * проверит при постановке SELL. `indexer` — снимок Data API, который
   * отстаёт от исполнения и держит призраков после погашения.
   *
   * Различие видно на экране, потому что от него зависит смысл НУЛЯ: у
   * индексатора ноль означает «он ещё не увидел сделку», у цепочки — «шеров на
   * кошельке нет».
   */
  source?: "chain" | "indexer";
}

/** Ответ GET /api/v1/positions. */
export interface PositionsResponse {
  positions: Position[];
  /**
   * Когда web_api получил снимок от индексатора. НЕ время исполнения: между
   * сделкой и её появлением здесь проходят секунды, и сколько именно —
   * неизмеримо. Отвечает ровно на вопрос «насколько стар снимок».
   */
  as_of?: string;
  /**
   * false = спросить было не у кого (аккаунт вне известного состава или не
   * задан адрес Data API). Это НЕ то же, что пустой список: из «позиций нет» следует «покупай»,
   * а из «неизвестно» — «стой».
   */
  known: boolean;
  /** Снимок отдан из кеша: обновить его не удалось. Возраст честно в as_of. */
  stale: boolean;
  /**
   * Размеры подтверждены балансом ERC-1155, а не только индексатором.
   *
   * Отдельно от `source` у позиций, потому что список бывает ПУСТЫМ, и там
   * различие дороже всего: «цепочка ответила, шеров нет» — основание не рисовать
   * кнопки продажи, «цепочку спросить не удалось» — основание продать по нашим
   * филлам (см. lib/position.ts).
   */
  chain_known?: boolean;
  /** Когда узел отдал балансы. Отсутствует, если chain_known = false. */
  chain_at?: string;
}

// ---------------------------------------------------------------------------
// Пробные ордера и счёт окна (ADR-0031)
// ---------------------------------------------------------------------------

/**
 * Тело POST /api/v1/paper/orders.
 *
 * Отдельный тип от PlaceOrderRequest, а не общий с необязательными полями, и
 * это то же решение, что на шине: поле, от которого зависит, тратятся ли
 * деньги, при опечатке отказывает в сторону траты. Здесь у пробы просто нет
 * полей, которыми она могла бы притвориться настоящим ордером — ни order_type,
 * ни post_only, ни expires_at: роль у неё одна, тейкерская.
 */
export interface PlacePaperOrderRequest {
  condition_id: string;
  asset_id: string;
  side: Side;
  price: string;
  size: string;
}

/** Чем кончился пробный ордер. */
export type PaperStatus = "filled" | "partial" | "rejected";

/** Строка журнала пробных ордеров. */
export interface PaperOrder {
  id: string;
  condition_id: string;
  asset_id: string;
  side: Side;
  /** ПОТОЛОК тейкера, а не цена сделки. */
  limit_price: string;
  requested_size: string;
  placed_at: string;
  status: PaperStatus;
  reason?: string;
  filled_size: string;
  /** Деньги по ЗАДЕТЫМ уровням, без комиссии. Разница с размером × лимит — это
   *  и есть проскальзывание. */
  quote: string;
  /** null — расписание комиссии рынка незнакомо. Отличается от нуля. */
  fee: string | null;
  /** Цена входа с комиссией и проскальзыванием. Снимок на момент налива. */
  effective_price: string | null;
  levels: number;
  best_bid: string | null;
  best_ask: string | null;
  /** Возраст стакана, по которому посчитан налив. Счёт на протухшем стакане
   *  систематически завышен, и без этого числа отличить измерение от
   *  самообмана нечем. */
  book_age_ms: number | null;
  /** Торговля была остановлена: сделка, снятая в этот момент, случиться не
   *  могла. Пробе стоп не мешает — это контрфактическое измерение. */
  kill_switch: boolean;
  window_end?: string;
  issued_by: string;
  trace_id?: string;
  /** Funder аккаунта, от имени которого сделана проба. */
  account?: string;
}

/**
 * Итог по одному исходу резолюции.
 *
 * pnl_usd и roi — null, когда считать было нечем: комиссия хоть одной сделки
 * неизвестна либо покупок не было. Ноль означал бы «вышли в ноль», а это
 * утверждение о деньгах.
 */
export interface PnlScenario {
  asset_id: string;
  outcome: string;
  pnl_usd: string | null;
  roi: number | null;
}

/** Позиция по одному исходу. */
export interface PnlPosition {
  asset_id: string;
  outcome: string;
  /** Нетто в share; знак значим. */
  shares: string;
  /** Средняя цена ПОКУПОК с комиссией. null — покупок не было либо комиссия
   *  неизвестна. */
  avg_entry: string | null;
  bought_size: string;
  bought_usd: string;
  sold_size: string;
  sold_usd: string;
}

/** Счёт одного режима: реального либо пробного. */
export interface PnlLedger {
  /** По исходам В ПОРЯДКЕ outcome_index — том же, что у лесенок. */
  positions: PnlPosition[];
  scenarios: PnlScenario[];
  bought_usd: string;
  sold_usd: string;
  fee_usd: string;
  /** false — расписание комиссии рынка незнакомо, и весь счёт режима гаснет
   *  прочерком. */
  fee_known: boolean;
  /** Сколько сделок сложено. Ноль означает «не торговали», а не «счёт ноль». */
  trades: number;
  /** Итог по ПОДТВЕРЖДЁННОМУ исходу. null означает «исход не подтверждён», а не
   *  «проиграли». */
  settled: PnlScenario | null;
}

/** Исход окна. */
export interface PnlResolution {
  resolved: boolean;
  winner_asset?: string;
  winner?: string;
}

/** Ответ GET /api/v1/markets/{id}/pnl. */
export interface MarketPnlResponse {
  condition_id: string;
  real: PnlLedger;
  paper: PnlLedger;
  resolution: PnlResolution;
  paper_orders: PaperOrder[];
  /** Поднята ли подсистема проб на трейдере (PAPER_ENABLED). Ложь гасит
   *  тумблер и объясняет причину вместо того, чтобы принимать нажатия, которые
   *  всё равно отвергнутся. */
  paper_enabled: boolean;
}

/** Ответ GET /api/v1/status. */
export interface ServiceStatus {
  service: string;
  milestone: string;
  /** Открытых сокетов интерфейса, то есть вкладок. Людей этим не считают: токен
   *  общий, и оператор в системе один. */
  ui_connections: number;
  /** «Прямо сейчас ордер дойдёт до биржи». Неизвестность любого слагаемого = false. */
  trading: boolean;
  /** false = /healthz трейдера не ответил; поля из него тогда null. */
  trader_reachable: boolean;
  /** ФАКТИЧЕСКИЙ стоп по данным трейдера (он ИЛИ-ит окружение и Redis); null = неизвестно. */
  kill_switch: boolean | null;
  /** RISK_KILL_SWITCH окружения трейдера. Такой стоп кнопкой в UI НЕ снимается. */
  kill_switch_env: boolean | null;
  /** Ключ risk:kill_switch, который пишет сам web_api. Известен и при мёртвом трейдере. */
  kill_switch_ui: boolean | null;
  /**
   * Аккаунты набора и стоп каждого (T5.6). Стоп аккаунта в kill_switch выше
   * НЕ подмешан: общий и аккаунтный — разные ключи с разными последствиями.
   * Нет поля — web_api старше этапа 5.
   */
  accounts?: AccountStatus[];
  /** null = неизвестно: «трейдер лежит» и «ключи не заданы» — разные диагнозы. */
  polymarket_configured: boolean | null;
  /** null = web_api не смог спросить БД; «неизвестно» и «ноль» — разные состояния. */
  tracked_markets: number | null;
  /**
   * Что именно трейдер держит пред-подписанным (веха M5).
   *
   * null означает «пред-подпись выключена целиком» (PRESIGN_ENABLED=false) либо
   * «статус ещё не пришёл». Это НЕ то же самое, что пустой список: пустой
   * список — включённая подсистема, которой пока нечего греть, и вкладка обязана
   * отправить ей свой набор.
   */
  presign: PresignStatus | null;
  /**
   * ПОТОЛОК режима копирования из окружения трейдера (FOLLOW_MODE).
   *
   * Действующий режим — строгий МИНИМУМ этого потолка и того, что выставила
   * кнопка. Без потолка на экране оператор жал бы «авто» и не понимал, почему
   * ордера не уходят. Пусто = трейдер не ответил либо подсистема не поднята.
   */
  follow_mode_env?: FollowMode;
  /**
   * Авторежим заявки на кромке у ТРЕЙДЕРА: поднят ли он и по каким числам судит.
   *
   * Отдельно от разрешения по окну (MakerArm), и это то же различие, что у
   * kill_switch_env: переключатель окна пишет web_api, а потолок живёт в
   * окружении трейдера (AUTOMAKER_ENABLED). Без него оператор взводил бы окно и
   * не понимал, почему заявки нет.
   *
   * `automaker` — «включён» либо «выключен (…)»; пусто = трейдер не ответил.
   * Порог и потолок суммы — строками, как их назвал трейдер: держать их
   * догадкой интерфейса значило бы иметь на экране одно число, а в торговле
   * другое.
   */
  /**
   * Сколько кошельков в реестре китов у трейдера, строкой.
   *
   * Нужно ровно затем, чтобы пустая лента сигналов не врала о причине: «китов не
   * встречали» и «в реестре некого встречать» — разные ответы, и второй чинится
   * порогами FOLLOW_MIN_*, а не ожиданием. Пусто = трейдер не ответил.
   */
  follow_wallets?: string;
  /**
   * Правила подсистемы копирования: имя, потолок режима, суточный потолок и
   * счётчики за этот запуск трейдера.
   *
   * Приезжают из НОТ трейдера, а не из окружения web_api: список живёт в
   * окружении трейдера, и он единственный, кто вправе называть эти значения.
   * Пусто — трейдер не ответил либо подсистема не поднята; «сценариев нет» не
   * бывает, при незаданном FOLLOW_SCENARIOS работает legacy.
   */
  follow_scenarios?: ScenarioStatus[];
  /**
   * Живой режим стратегий с портом постановки (`decided`, `family`).
   *
   * Пусто = трейдер не ответил либо ни одна подсистема не поднята. Режим едет
   * СТРОКОЙ С ПРИЧИНОЙ, как её называет трейдер: без причины «shadow»
   * одинаково выглядит при выключенной кнопке, при потолке окружения и при
   * неотвечающем Redis, а чинится это по-разному.
   */
  strategies?: StrategyStatus[];
  automaker?: string;
  automaker_bid?: string;
  automaker_max_usd?: string;
}

/** Настройка пред-подписи, как её отдаёт GET /status. Зеркало Go. */
export interface PresignStatus {
  /**
   * Суммы В ДОЛЛАРАХ, строками: считаем целочисленно, Number(price) запрещён.
   *
   * ОБЪЕДИНЕНИЕ пресетов и последней ручной суммы — всё, чем оператор может
   * нажать. Размер ордера в share выводится из суммы и цены уровня (lib/size.ts)
   * тем же правилом, каким его считает лесенка пред-подписи у трейдера.
   * Разойдись они — кэш промахивался бы при исправном коде.
   */
  sizes_usd: string[];
  /**
   * Только пресеты, без ручной суммы. Ими засевается свежая вкладка: ручная
   * сумма среди них стала бы кнопкой, которую оператор не заводил.
   */
  presets_usd: string[];
  /** Сколько шагов цены вокруг лучших цен трейдер готовит заранее. */
  levels: number;
  /**
   * Откуда взят действующий набор.
   *
   * "ui" — ключ presign:sizes_usd, который пишет эта же панель; "env" — переменная
   * PRESIGN_SIZES_USD, то есть трейдер о наборе вкладки НЕ ЗНАЕТ. Второе — не
   * ошибка, а повод отправить набор: так лечится потеря ключа (чистый Redis,
   * новый стенд), которая иначе осталась бы незамеченной до правки пресетов.
   */
  source: PresignSource;
}

export type PresignSource = "ui" | "env";

/** Тело POST /api/v1/presign/sizes. */
export interface WarmSizesRequest {
  /** Что греть: пресеты вместе с последней ручной суммой. */
  sizes_usd: string[];
  /** Только пресеты — их web_api вернёт свежей вкладке для засева. */
  presets_usd: string[];
}

/** Ответ POST /api/v1/presign/sizes. */
export interface WarmSizesResponse {
  presign: PresignStatus;
  /**
   * Что трейдер греть НЕ будет и почему: сумма выше лимита ордера, набор длиннее
   * потолка. Пусто, когда принято всё. Молча выброшенная сумма выглядела бы на
   * экране обычной холодной кнопкой, и оператор искал бы причину в прогреве.
   */
  notices: string[] | null;
}

/** Справедливая цена одного исхода по модели. */
export interface FairPrice {
  outcome: string;
  outcome_index: number;
  /**
   * Десятичная строка в [0, 1]. Сумма исходов ровно единица по построению.
   *
   * Считается ЗАМКОМ исхода: `1 − prob_flip` у стороны, куда идёт дело, и
   * `prob_flip` у второй (webapi.fairUpOf). До 2026-08-27 здесь был Блэк —
   * Шоулз с нормалью и безусловной дисперсией; заменено, потому что по этому
   * числу теперь идёт теневой сигнал расхождения, а панель и сигнал обязаны
   * смотреть на одно.
   */
  price: string;
  /**
   * То же число по ПРЕЖНЕЙ модели — нормаль и безусловная дисперсия.
   *
   * Едет рядом, потому что им посчитаны две недели журнала прогноза: оператор,
   * разбирающий вчерашнюю строку, обязан видеть то, что видел вчера. Пусто —
   * «прежняя модель на этих входах неприменима», а не цена ноль.
   */
  price_normal?: string;
}

/**
 * Оценка рынка серии: страйк, индекс, отклонение и модель Блэка — Шоулза.
 *
 * Зеркало webapi.valuationResponse. Форма повторяет PositionsResponse и по той
 * же причине: `known` отделяет «данных нет» от «отклонение равно нулю». Из
 * первого следует «стой», из второго — «рынок на страйке», и подменять одно
 * другим на терминале с реальными деньгами нельзя.
 *
 * ЕДИНИЦЫ: strike, index, deviation и fair[].price — СТРОКИ (правило 3
 * контракта). А deviation_pct и sigma_annual — числа, и это не оплошность:
 * процент и волатильность не деньги, шага цены не имеют и в ордер не идут.
 */
export interface Valuation {
  asset?: string;
  /**
   * Чем измерено: `crypto_prices_twap_thirty` и подобное. Источник раздаёт
   * несколько похожих метрик, различающихся только именем, а значение внутри
   * минуты расходится на 0.03 доллара по ETH и на 3.73 по BTC — поэтому метрика
   * видна на экране.
   */
  source?: string;
  /**
   * Окно усреднения метрики, по которой рынок РЕЗОЛВИТСЯ, секунды.
   *
   * Берётся из самого рынка (`cryptoMarketConfig.twapLookbackSeconds`), а не из
   * потока: у пятиминутных окон это 30, и подстановка чужого окна смещала бы всю
   * оценку — тихо и правдоподобно.
   */
  window_s?: number;

  /** Цена базового актива на начало окна. Отсутствует, если её не записали. */
  strike?: string;
  /** Текущее значение индекса. */
  index?: string;
  /** Метка ИСТОЧНИКА, а не момент ответа: по ней считается возраст цены. */
  index_at?: string;

  /**
   * Метка источника у значения, взятого за страйк, и опоздание относительно
   * начала окна в миллисекундах.
   *
   * Ноль опоздания означает «измерено ровно на секунду начала окна» — то есть тем
   * значением, по которому рынок и резолвится. Источник изредка пропускает
   * секунду, страйк тогда берётся следующий, и оценка окна оказывается смещена в
   * одну сторону, оставаясь правдоподобной. Пустая метка — страйк записан до
   * миграции 00007.
   */
  strike_sample_at?: string;
  strike_lag_ms?: number;

  /**
   * Тот же страйк по версии САМОЙ БИРЖИ и расхождение с нашим.
   *
   * Пусто означает «не сверялось», а НЕ «сошлось»: маршрут сверки
   * недокументирован, ограничивает частоту и может молчать. Показывать
   * несверенный страйк подтверждённым нельзя.
   *
   * `strike_from_exchange` — страйк вообще не снят с потока, а дозаписан оттуда
   * же: начала окна мы не застали.
   */
  strike_exchange?: string;
  strike_exchange_diff?: string;
  strike_from_exchange?: boolean;
  /**
   * Страйк ещё не зафиксирован, и в `strike` стоит его ПРОГНОЗ: уже прошедшие
   * секунды окна страйка плюс последняя цена на месте ненаступивших. Всё, что от
   * страйка зависит (отклонение, модель), до открытия считается от прогноза.
   */
  strike_forecast?: boolean;

  deviation?: string;
  deviation_pct: number;

  /** Годовая волатильность базового актива. Ноль = «ещё не оценена». */
  sigma_annual: number;
  sigma_samples: number;

  window_start?: string;
  window_end?: string;
  /** Остаток окна. ОТРИЦАТЕЛЬНЫЙ у закончившегося — знак значим. */
  seconds_left: number;

  /** Справедливые цены исходов. Пусто, когда known === false. */
  fair?: FairPrice[];

  known: boolean;
  /** Значение индекса старше десяти секунд: числа честные, но старые. */
  stale: boolean;
  /** Почему оценки нет — словами. Показывается вместо чисел. */
  reason?: string;

  /** Сверка нашего пересчёта метрики с числом источника. Нет сырого ряда — нет поля. */
  check?: TwapCheck;
  /** Замок исхода окна. Нет хвоста ряда или страйка — нет поля. */
  lock?: WindowLock;
  /**
   * Правда о ЗАКОНЧИВШЕМСЯ окне: чем оно кончилось. У идущего поля нет.
   *
   * Его наличие и есть признак «карточка показывает окно, а не текущую
   * секунду»: при нём `check` и `fair` не приезжают вовсе, а `index` и
   * `deviation` описывают конец окна, а не «сейчас».
   */
  final?: WindowFinal;
  /**
   * Окно ДО фиксации страйка. Взаимоисключающе с `lock`: до открытия
   * перевернуть исход можно всегда, и спрашивать замок не о чем.
   */
  preopen?: WindowPreopen;
}

/**
 * Предстарт окна. Зеркало webapi.windowPreopen, модель — lock.ComputePreopen.
 *
 * Страйк — та же метрика, что итог, с меткой начала окна: отчёт TWAP-W с меткой
 * t0 усредняет сырой ряд по `[t0−W−2, t0−3]`. Пока эти секунды идут, стакан
 * переоценивается вслед за формирующимся страйком, и до их начала ход цены на
 * исход не влияет вовсе — он сдвигает страйк и итог на одну величину.
 */
export interface WindowPreopen {
  /** Покрытие окна страйка и его потерянные секунды. */
  strike_have: number;
  strike_want: number;
  strike_lost?: number;
  /** Сколько окна страйка ещё не наступило. */
  strike_unknown_ms: number;
  /** Через сколько окно страйка НАЧНЁТСЯ. Нет поля — уже идёт. */
  strike_dead_prefix_ms?: number;
  /** Разброс прогноза страйка. Нет поля — σ ещё не оценена. */
  strike_sigma?: string;
  /** Итог окна, если цена замрёт, и его запас над прогнозом страйка. */
  projected?: string;
  /** Запас едет ВСЕГДА: до окна страйка он ровно ноль — «стороны ещё нет». */
  cushion: string;
  /** Разброс разности «итог − страйк» и вероятность, что ведущая сторона проиграет. */
  sigma_left?: string;
  prob_flip?: number;
  /** Те же числа по ряду, достроенному спотом биржи. */
  ahead?: {
    strike?: string;
    projected?: string;
    cushion: string;
    sigma_left?: string;
    prob_flip?: number;
  };
}

/**
 * Чем кончилось окно. Зеркало webapi.windowFinal.
 *
 * # Почему он ЗАМЕНЯЕТ прогноз, а не стоит рядом
 *
 * Потому что сверка и модель отвечают на вопросы, которых у закрытого окна нет
 * («где метрика окажется через три секунды», «куда пойдёт цена»), и считаются
 * они от ТЕКУЩЕЙ секунды. Страница, открытая через час, показывала бы по ним
 * движение цены, к этому окну отношения не имеющее, — с подписью «фора 1.1 с»,
 * то есть уверенно и неверно.
 */
export interface WindowFinal {
  /**
   * Значение метрики с меткой КОНЦА ОКНА — то самое число, по которому рынок и
   * резолвится. Пусто означает «итог не записан»: трейдер не котировал это окно
   * либо поднялся после его конца. Тогда прочерк, а не живой индекс.
   */
  published?: string;
  /** Пол пересчёта: то же число по полному ряду. Справочное. */
  replay?: string;
  /** Снос со знаком: итог минус страйк. Он и назначает сторону. */
  drift?: string;
  /**
   * Биржа подтвердила исход. Ложь — это НЕ «проиграл», а «мы ещё не знаем»:
   * подтверждение доезжает до двух минут после конца окна, и всё это время
   * сторона на экране только расчётная, по знаку сноса.
   */
  resolved: boolean;
  /** Имя победившего исхода («Up», «Down») и его токен. */
  winner?: string;
  winner_asset?: string;
  settled_at?: string;
}

/**
 * Сверка: одна и та же величина, посчитанная двумя путями.
 *
 * Отчёт TWAP опаздывает на три секунды — значение с меткой t это среднее сырого
 * ряда по `[t−W−2, t−3]`, — поэтому то же число собирается самим раньше, чем его
 * опубликует источник. Сверка отвечает, вправе ли мы своим числом пользоваться.
 */
export interface TwapCheck {
  /** Сырой индекс без усреднения и его СОБСТВЕННАЯ метка: ряды разные. */
  raw?: string;
  raw_at?: string;
  /** Наш пересчёт метрики и его разность с опубликованным значением. */
  own?: string;
  diff?: string;
  /** Покрытие окна пересчёта: сколько секунд нашлось из скольких нужных. */
  own_have?: number;
  own_want?: number;
  /**
   * Отчёт, которого источник ЕЩЁ НЕ ОПУБЛИКОВАЛ, и его метка.
   *
   * Здесь и живёт фора: `own` её не показывает по построению — он считается на
   * метку опубликованного значения, то есть на тот же момент, и обязан с ним
   * совпадать. А доступны нам метки новее: отчёту с меткой t нужен сырой ряд
   * только до t−3. Это не прогноз — все входы уже существуют.
   *
   * `ahead_diff` — насколько это число отличается от текущего у источника, то
   * есть на сколько оно у него вот-вот сдвинется.
   */
  ahead?: string;
  ahead_at?: string;
  ahead_diff?: string;
  ahead_have?: number;
  /** Накопленная точность и сколько отчётов сверено на ПОЛНОМ окне. */
  median?: string;
  p90?: string;
  full?: number;
  exact?: number;
  /**
   * За какой срок посчитана точность, секунды.
   *
   * Без него дробь «289 из 348» не значит ничего: знаменатель зависит от того,
   * сколько ряда накоплено, и молча растёт после старта трейдера.
   */
  span_s?: number;

  /**
   * Достройка сырого ряда спотом биржи. Отсутствует, когда подсистема выключена.
   *
   * Отличать «поля нет» от «состояние диверг» обязательно: первое означает, что
   * достройки не считают вовсе, второе — что её посчитали и отказались
   * публиковать, потому что она разошлась с проводом.
   */
  forecast?: IndexForecast;

  /**
   * Куда придёт МЕТРИКА, если сырая цена замрёт на последнем известном значении.
   * Ключ — горизонт в секундах сверх `ahead`, то есть сверх того, что мы уже
   * знаем без единого предположения.
   *
   * Отвечает на вопрос «сколько сноса УЖЕ заложено»: разница между
   * опубликованным числом и сырым случится, даже если рынок больше не
   * шевельнётся. Предел тривиален (среднее постоянного ряда равно этой
   * постоянной, то есть `raw`), а вот путь — нет: он идёт с перелётом, потому
   * что форма зависит от того, какие секунды ПОКИДАЮТ окно усреднения.
   */
  frozen?: Record<string, string>;
}

/** Состояние достройки. Строки приходят с сервера как есть. */
export type ForecastState = "грею" | "живой" | "диверг" | "молчит";

/**
 * Достройка: сырой ряд, продолженный оценкой по споту биржи.
 *
 * Едет РЯДОМ с `ahead`, никогда вместо. Сегодняшнее число обязано остаться на
 * экране, иначе поправка перестанет читаться как поправка.
 *
 * Спот приходит по тому же проводу примерно на секунду с лишним раньше сырого
 * индекса, поэтому ряд можно достроить — не предсказать цену, а восстановить
 * уже случившееся, но ещё не доехавшее. Замер: RMSE уровня индекса $4.30 → $2.15.
 */
export interface IndexForecast {
  /**
   * Отчёт по ДОСТРОЕННОМУ ряду и его метка.
   *
   * Отсутствуют, когда оценки сейчас нет: подсистема греется, разошлась или
   * спот молчит. Причина — в `state`, и показать надо её, а не прочерк: пустое
   * место на месте числа читается как ноль, а ноль здесь — это цена.
   */
  price?: string;
  at?: string;
  /** Сколько секунд окна нашлось. Знаменатель тот же, что у `own_want`. */
  have?: number;

  /**
   * ТОЛЬКО оценочные точки хвоста, идущие после последней настоящей.
   *
   * Настоящие лежат в `WindowLock`-расчёте сервера и здесь не дублируются:
   * клиенту ряд нужен не для арифметики, а чтобы показать, где кончается провод.
   */
  tail?: IndexSample[];
  /** Длина `tail` — сколько секунд хвоста являются оценкой. */
  estimated?: number;

  /** Базис `сырой − спот` на опорной паре и метка этой пары. */
  basis?: string;
  basis_at?: string;
  /** Лаг агрегации и доля приращения спота, которой построена оценка. */
  lag_ms?: number;
  slope?: number;

  /**
   * Последнее значение опорного ряда и ЕГО валюта.
   *
   * Валюта обязательна: спот котирован в USDT, индекс — в USD, между ними около
   * 63 долларов. Без неё уровень спота читался бы как расхождение с индексом.
   */
  spot_price?: string;
  spot_at?: string;
  spot_quote?: string;

  state: ForecastState;

  /**
   * Самосверка: каждая оценка через ~1.1 с встречается со своим настоящим
   * кадром, и разница копится в скользящее окно. Она же и выключатель.
   */
  err_median?: string;
  err_p90?: string;
  err_checks?: number;

  /**
   * Ошибка «ничего не делать» на ТЕХ ЖЕ сверках и разложение самосверки по
   * ПЛЕЧУ оценки.
   *
   * Первое даёт ошибке единицу измерения: в долларах и в базисных пунктах она
   * движется от цены актива и от волатильности часа, а в долях от этой планки —
   * нет. Замер по двум записям провода: доллары выросли в 2.6 раза, а доля от
   * планки УМЕНЬШИЛАСЬ (0.62 → 0.48 у BTC).
   *
   * Второе говорит, не сместился ли МИКС плеч: ошибка на плече 1 с и на 3 с
   * отличается втрое, и без разложения движение микса читается как движение
   * модели.
   */
  err_base?: string;
  err_by_lever?: ForecastLever[];

  /**
   * Измеренная живьём фора, миллисекунды: на сколько раньше оценка называет
   * значение, чем его приносит провод.
   *
   * По СТРОГОМУ краю интервала лага: `ℓ` внутри [0, 1] неразличим, и обещать
   * запас, которого может не быть, нельзя.
   *
   * Считается от прихода спотового кадра, а не от сборки раздачи: та идёт
   * тикером раз в секунду и задерживает `ahead` ровно так же, а фора — это
   * разница между ними.
   */
  gain_ms?: number;
}

/** Самосверка на одном плече оценки. */
export interface ForecastLever {
  /** Плечо в миллисекундах. Короче секунды не бывает: провод прорежен до 1 Гц. */
  lever_ms: number;
  /** Сверок в группе — обязателен рядом с медианой, иначе число не читается. */
  checks: number;
  median?: string;
}

/** Одно значение ряда индекса. */
export interface IndexSample {
  at: string;
  price: string;
}

/**
 * Замок: насколько исход окна ещё зависит от рынка.
 *
 * ВАЖНО: всё это утверждения о числе, которое раздаёт Polymarket. Что оно
 * совпадает с подписанным отчётом Chainlink, не проверено, и говорить об этом
 * рядом с алармом обязательно.
 */
export interface WindowLock {
  /**
   * Сколько миллисекунд итогового окна ещё не наступило по МОДАЛЬНОЙ задержке.
   * Ноль — величине перевернуть нечем.
   *
   * По ней считается ВЕЛИЧИНА требуемого движения: у 84 % отчётов окно итога
   * кончается на `t−3`, и лишняя секунда занижала бы требуемый скачок, то есть
   * переворот выглядел бы легче, чем он есть.
   */
  unknown_ms: number;
  /**
   * То же по МИНИМАЛЬНОЙ задержке: по ней судится ЗАПЕРТОСТЬ исхода.
   *
   * Между ними полоса ровно в одну секунду, где два утверждения расходятся:
   * величине перевернуть уже нечем, а исход ещё НЕ определён — у каждого
   * шестого отчёта секунда `T1−2` в итог входит. Это не рассогласование, а
   * разная цена ошибки, и сводить их к одному числу нельзя.
   */
  unknown_settled_ms: number;
  /**
   * Сколько миллисекунд ход обязан ПРОДЕРЖАТЬСЯ, чтобы вообще попасть в итог.
   *
   * Ноль (поля нет) означает «итоговое окно уже идёт». Пока оно впереди, ход,
   * откатившийся за это время, вносит в исход РОВНО НОЛЬ: при W = 60 и τ = 90
   * бесполезен любой ход короче 27 секунд.
   *
   * Приходит с сервера, а не считается здесь: прежде панель вычитала задержку
   * сама и тем держала копию `twap.Delay` в TypeScript.
   */
  dead_prefix_ms?: number;
  /** Прогноз итога, если цена замрёт, и его запас над страйком. */
  projected?: string;
  cushion?: string;
  /** На сколько должна уйти цена и УДЕРЖАТЬСЯ весь остаток, чтобы исход перевернулся. */
  required?: string;
  /** false — арифметическая невозможность, а не малая вероятность. */
  possible: boolean;
  /** Разброс остатка (модельный) и наблюдённый ПИК за тот же горизонт. */
  sigma_left?: string;
  max_move?: string;
  max_move_horizon_s?: number;
  /**
   * ДОСТИЖИМОЕ среднее отклонение (p90) за тот же горизонт — и второе число
   * рядом с требуемым скачком.
   *
   * Именно оно соизмеримо с `required`: тот есть требование к СРЕДНЕМУ по
   * неизвестным секундам, а не к уровню. Ход вдвое больший, продержавшийся
   * половину остатка, даёт то же самое; мигнувший пик не даёт почти ничего.
   * Сравнение требования с `max_move` — категориальная ошибка: замер даёт
   * отношение пик/среднее 1.5–2.0 по p90.
   */
  mean_move?: string;
  mean_move_horizon_s?: number;

  /**
   * То же требование на часах БИРЖИ и целевая цена в валюте самой площадки.
   *
   * Движение спота попадает в индекс через лаг агрегации `ℓ`, значит на часах
   * биржи неизвестных секунд на одну меньше, и требуемое движение там во
   * столько же раз больше: `M_бир = M_инд · u/(u−ℓ)`. Курс USDT/USD для этого
   * НЕ нужен — цель считается приращением от цены той же площадки, и базис в
   * разности сокращается целиком.
   *
   * Показывается оператору с ИЗМЕРЕННЫМ лагом: это его вопрос. А `state`
   * считается по СТРОГОМУ краю `ℓ = 0` и этих полей не читает: поправка
   * увеличивает требование, а чем оно больше, тем охотнее поднимается «замок
   * почти закрыт», то есть тем охотнее система приглашает купить у единицы.
   */
  required_venue?: string;
  venue_target?: string;
  venue_price?: string;
  venue_quote?: string;
  /**
   * Ход, начатый СЕЙЧАС, в индекс уже не доедет.
   *
   * ПОДСКАЗКА, а не тревога: утверждение «перевернуть нечем» она не двигает —
   * то приглашает ставку у единицы и считается по самой осторожной комбинации.
   */
  late_on_venue?: boolean;
  /**
   * Вероятность переворота по модели: хвост Стьюдента с ν = 2 от `|запас| / σ`.
   *
   * Не нормаль, и это замер, а не вкус: на 16 146 снимках нормаль обещала 0.02 %
   * переворотов там, где случалось 1.8 %, — и врала тем сильнее, чем увереннее
   * была модель. Вывод поправки — в pricing.FlipProb.
   *
   * Отсутствие означает «не считается» (разброс остатка не оценён), а не «ноль».
   * ПОМЕЧЕНА «не сверено» до первой сходящейся калибровки задним числом: σ
   * оценивается по ряду средних, и правильность этой поправки проверяется
   * только отчётом на живых данных. Права поднимать действие не имеет —
   * решение её не читает.
   */
  prob_flip?: number;
  /** Итоговое окно набралось целиком, без дыр и без будущего. */
  complete: boolean;
  /**
   * Сколько секунд итогового окна ПОТЕРЯНО: прошли, а в ряду их нет.
   *
   * Отдельно от complete: почти всё время окна оно неполно просто потому, что
   * ещё не наступило, и предупреждать об этом — значит держать тревожную
   * надпись на экране постоянно.
   */
  lost?: number;
  /** Исход определён: перевернуть нечем И окно полное. */
  settled: boolean;
  /** Какой именно исход, когда settled. */
  outcome?: string;
  /**
   * РЕШЕНИЕ по замку: что происходит, словами, и чем это меряли.
   *
   * Считает сервер (services/internal/lock), и то же самое решение принимает
   * подсистема алертов в трейдере. Две реализации разошлись бы в оценке одного
   * окна на первой же правке — тихо, потому что оба ответа выглядят
   * правдоподобно.
   */
  state: LockState;
  label: string;
  basis?: string;
  /** Каким режимом порога получено решение. */
  mode: LockMode;

  /**
   * Тот же замок, посчитанный по ДОСТРОЕННОМУ ряду. Отсутствует, когда
   * достройки нет или она себя выключила.
   *
   * Это ТЕНЬ и остаётся тенью: решения (`state`, `label`, `settled`, `outcome`)
   * здесь нет и не будет — их считает только настоящий ряд. Ценность в другом:
   * почти всё окно требуемый скачок равен `|индекс − страйк|`, то есть его
   * точность равна точности знания уровня, а её достройка режет вдвое ($4.30 →
   * $2.15 RMSE). Поэтому поправка показывается РЯДОМ с сегодняшним числом.
   */
  ahead?: LockAhead;
}

/**
 * Поправка замка по достроенному ряду — только числа, никаких решений.
 *
 * Отсутствие решения тут структурное, а не по недосмотру: один неверный замок
 * на цене 0.99 съедает девяносто девять верных, и право двигать аларм оценка не
 * получает вовсе.
 */
export interface LockAhead {
  projected?: string;
  cushion?: string;
  required?: string;
  possible: boolean;
  /**
   * Разброс остатка и вероятность переворота — ПО ЗАПАСУ ТЕНИ.
   *
   * Обязаны ехать вместе с `cushion`, иначе строка панели противоречит сама
   * себе: вероятность считается от `|запас| / σ`, а запас у настоящего ряда и у
   * достроенного в быстром движении расходятся до смены знака. Пока их не было,
   * рядом стояли «запас −0.57» и «переворот 35 %» — то есть «ведёт Down» и «Up
   * выигрывает с вероятностью 65 %».
   *
   * Пометка «не сверено» относится к ним ровно так же, как к базовым: закон
   * хвоста один и тот же, и калибровка у него общая.
   */
  sigma_left?: string;
  prob_flip?: number;
  complete: boolean;
  lost?: number;
}

/** Тело GET /api/v1/markets/{conditionID}/valuation. */
export interface ValuationResponse {
  valuation: Valuation;
}

/** Сообщения, отправляемые из UI в сокет. */
export type ClientMessage =
  | { type: "subscribe"; markets: string[] }
  | { type: "unsubscribe"; markets: string[] }
  | { type: "ping" };

/** Подписка на все рынки сразу (общая лента). */
export const ALL_MARKETS = "*";

/**
 * Зеркало webapi.tapeStatsResponse: сколько сделок по рынку прошло через НАШУ
 * ленту и сколько их насчитал сам Polymarket.
 *
 * ЕДИНИЦЫ — главное здесь, и они РАЗНЫЕ. «$… Vol.» в интерфейсе биржи это Σ size,
 * то есть ШТУКИ КОНТРАКТОВ, несмотря на знак доллара: на закрытых окнах
 * `gamma.volume` совпадал с Σ size по всем сделкам Data API до восьмого знака, а
 * Σ (size × price) был меньше в полтора-два раза. Поэтому наш итог показывается
 * ДВУМЯ числами: сравнивать с чужим можно только первое.
 */
export interface TapeStats {
  condition_id: string;

  /** Наша лента: публичных принтов, Σ share и Σ денег. */
  trades: number;
  shares?: string;
  notional?: string;

  /**
   * Наши сделки — ОТДЕЛЬНО от публичных принтов: они едут в ту же ленту из
   * приватного канала, и сложенные вместе посчитались бы дважды. Величины здесь
   * наши (our_size/our_price), а не сделка целиком.
   */
  our_trades: number;
  our_shares?: string;
  our_notional?: string;

  /**
   * Разбивка публичных принтов по исходам.
   *
   * По кадрам `last_trade_price` нельзя решить, две это сделки или одна,
   * показанная с двух сторон: идентификатора сделки у события нет. Разбивка
   * делает двойной счёт видимым как две почти равные половины.
   */
  tokens?: TapeTokenStats[];

  /**
   * С какого момента идёт счёт. Обязателен для чтения итога: трейдер
   * подписывается на рынок за минуты до окна, а сделки по нему идут и раньше —
   * без этой метки неполный итог читался бы как потеря сделок.
   */
  since?: string;
  updated_at?: string;

  /** Объём по версии Polymarket, в ШТУКАХ контрактов, и возраст этого числа. */
  exchange?: string;
  /** У живого пятиминутного окна отстаёт на МИНУТЫ — показывать возраст обязательно. */
  exchange_at?: string;

  known: boolean;
  stale: boolean;
  reason?: string;
}

export interface TapeTokenStats {
  asset_id: string;
  trades: number;
  shares: string;
}

/** Тело GET /api/v1/markets/{conditionID}/tape. */
export interface TapeStatsResponse {
  tape: TapeStats;
}

/**
 * Накопление одного кошелька по одному outcome-токену.
 *
 * Зеркало marketflow.Token. Величины ПОДПИСАННЫЕ: покупка плюсом, продажа
 * минусом. Отрицательное `net` законно и означает «продал здесь больше, чем
 * купил» — шеры могли прийти минтом пары, а минт сделкой не является и в
 * истории сделок не виден.
 */
export interface ParticipantToken {
  asset_id: string;
  net: string;
  /** Часть net, набранная АГРЕССИЕЙ: кошелёк снимал заявки. */
  taker: string;
  /**
   * Часть net, набранная СТОЯНИЕМ В СТАКАНЕ, то есть net − taker.
   *
   * Получается вычитанием двух выборок и никак иначе: у строки истории сделок
   * нет признака «тейкер». Поэтому при `truncated` это число НЕ менее точное, а
   * неверное, и показывать его нельзя (см. ParticipantsStats.truncated).
   */
  maker: string;

  /** Обороты в share, без знака. Нужны ради средней цены входа. */
  bought: string;
  sold: string;
  bought_usd: string;
  sold_usd: string;

  trades: number;
  taker_trades: number;
}

/**
 * Что кошелёк делает на КОРОТКИХ рынках вообще. Зеркало domain.WalletFlow.
 *
 * Отвечает на другой вопрос, чем остальная строка: та говорит о набранном в ЭТОМ
 * окне, а это — о том, кто перед нами. «Набрал 700 Down» значит разное у
 * кошелька, который делает это тридцать раз в день и в плюсе, и у зашедшего
 * впервые.
 *
 * Все суммы — В ДОЛЛАРАХ, строками, как везде в контракте.
 */
export interface WalletFlow {
  address: string;
  /** Разных коротких окон, задетых за период. */
  markets: number;
  trades: number;
  /** Из них агрессией. Признака «тейкер» в строке источника нет — это вторая выборка. */
  taker_trades: number;
  /** Знаменатель РОЯ: сколько денег занесено в рынок покупками. */
  bought_usd: string;
  sold_usd: string;
  /**
   * Комиссия тейкера, уже вычтенная из pnl_usd. Отдаётся отдельно ради разбора —
   * и ради сверки: профиль Polymarket показывает прибыль БЕЗ неё, поэтому там то
   * же число будет больше ровно на эту величину (сверка 2026-08-12: наш +702.20
   * за сутки против +2230.34 у биржи при комиссии 1526.98).
   */
  taker_fee_usd: string;
  /** Σ(продажи − покупки) + погашение победившего токена − комиссия. */
  pnl_usd: string;
  /**
   * Сколько СУТОК попало в свод — поле честности этого блока.
   *
   * Это НЕ длина запрошенного окна: у нового кошелька за «30 суток» приедут одни,
   * потому что глубже обход не заглядывал (за раз он берёт двое суток, а глубже
   * история не влезает в потолок offset источника — замер: 5957 строк за сутки
   * против потолка 10 000). Прочитать такой свод как месячный — ровно та ошибка,
   * ради которой поле и заведено, поэтому глубина стоит рядом с числом на экране.
   */
  days: number;
  from?: string;
  /**
   * В свод входят НЕЗАКОНЧЕННЫЕ сутки, то есть сегодняшние.
   *
   * У пятиминутных серий дневной PnL набирается за часы, поэтому свод, взятый в
   * 03:00 UTC, описывает три часа торгов, а выглядит суточным.
   */
  partial?: boolean;
  /**
   * Сколько суток окна записаны ПРОЧЕРКОМ и в суммы выше не вошли.
   *
   * Такие сутки не «менее точные», а неверные: выборки полной и тейкерской
   * истории разошлись по глубине, и комиссия оказалась начислена на сделки,
   * которых нет в обороте. Пнл, комиссия и роль считаются без них, а обороты,
   * сделки и `days` — по всем суткам, — то есть деньги свода описывают меньше
   * суток, чем говорят глубина и оборот, и молчать об этом нельзя.
   */
  unusable_days?: number;
}

/**
 * Сколько кошельку ДОПЛАТИЛИ помимо сделок. Зеркало domain.WalletIncentives.
 *
 * # Почему это отдельное число, а не слагаемое pnl_usd
 *
 * Потому что «наторговал» и «доплатили» — разные величины, и слитые в одну
 * колонку они не дают ответить, на чём кошелёк живёт. Замер 2026-08-18:
 * программа возвращает 64 % ВСЕЙ комиссии рынка, и с её учётом «чистые
 * агрессоры» за неделю не −$197 тыс., а +$30.7 тыс. То есть знак результата у
 * целого лагеря задаётся именно этим слагаемым, а кошелёк, живущий на возврате,
 * по одному pnl_usd выглядит убыточным.
 *
 * Отсутствие блока означает «выплат не видели»: либо не спрашивали, либо
 * спросили, а переводов за окно не было — хранятся только пришедшие переводы,
 * и второй случай от первого не отличить.
 */
export interface WalletIncentives {
  address: string;
  /** Сумма всех типов за окно, В ДОЛЛАРАХ. */
  usd: string;
  /** Сколько переводов в неё сложено. Без него сверку не с чем сопоставить. */
  payouts: number;
  /**
   * Разбивка по типам: TAKER_REBATE, MAKER_REBATE, REWARD.
   *
   * Нужна не для красоты: возврат тейкеру и возврат мейкеру описывают
   * ПРОТИВОПОЛОЖНЫЕ способы торговать, и кошелёк, живущий на первом, — не тот же
   * участник, что живущий на втором.
   */
  by_type?: Record<string, string>;
  /** За сколько суток окна выплаты вообще приходили. */
  days: number;
}

/**
 * Окно, за которое считается блок flow.
 *
 * Календарные сутки UTC, а не «последние N часов»: бакеты в базе суточные, и
 * «последние 72 часа» разрезали бы самые старые сутки пополам. «all» — вся
 * НАКОПЛЕННАЯ глубина, а не вся история кошелька: сутки старше срока хранения
 * убирает уборщик, а глубже двух суток за один обход не заглянуть.
 */
export type FlowWindow = "today" | "3d" | "7d" | "30d" | "all";

/** Один кошелёк в таблице накопления. Зеркало webapi.participantRow. */
export interface ParticipantRow {
  wallet: string;
  tokens: ParticipantToken[];
  /** Имя из списка держателей. Пустое штатно: кошелёк мог раздать позицию. */
  name?: string;
  /** Наш кошелёк. */
  ours?: boolean;
  /**
   * НАША квалификация кошелька, если он нам знаком.
   *
   * Отсутствие означает «профиля нет»: тир считается по нашим встречам, а
   * участник рынка мог с нами ни разу не столкнуться. Это не то же самое, что тир
   * `unknown` («посмотрели и не поняли»), поэтому unknown сюда не приезжает, а
   * пустое поле показывается ОТСУТСТВИЕМ значка — не бледным значком в каждой
   * строке, каким панель и выглядела до 2026-08-12.
   */
  tier?: WalletTier;
  /**
   * Подгруппа сводки при УМОЛЧАЛЬНОЙ ступени порога оборота — тем же правилом
   * и из того же прохода, что блок «группы». Отсутствует у «остальных» и когда
   * сводки нет вовсе: чип не рисуется, причина — под таблицей сводки.
   */
  profile?: ParticipantCohortKind;
  /**
   * Статистика коротких рынков. ОТСУТСТВИЕ поля означает «кошелёк не обходили»
   * — оборот ниже порога либо очередь до него не дошла, — и это НЕ то же самое,
   * что «наторговал ноль». На экране разница видна как прочерк против нуля.
   */
  flow?: WalletFlow;
  /**
   * Выплаты кошельку за то же окно: возврат комиссии и награды. ОТСУТСТВИЕ поля
   * — «выплат не видели», а не уверенное «биржа не платит».
   */
  incentives?: WalletIncentives;
  /**
   * Что кошелёк взял У НАС в этом рынке. Приезжает только в режиме «против нас».
   *
   * Отсутствие означает «мы с ним здесь не торговали»: в этом режиме таких строк
   * в таблице и не бывает, а в обычном поле не приезжает вовсе.
   */
  against_us?: AgainstUs;
  /**
   * Что он набрал ПОСЛЕ последней встречи с нами — мера сопротивления.
   *
   * Три состояния, и они РАЗНЫЕ:
   *
   *   - поля нет — обход свода про эту встречу ещё не знал (филл свежее снимка).
   *     Это незнание, и на экране прочерк;
   *   - `tokens` пуст — после встречи кошелёк в рынок НЕ ПРИХОДИЛ. Это знание и
   *     главный ответ режима: против такого можно стоять до экспирации;
   *   - `tokens` непуст — вот что он добрал и какой ролью. Добор ТЕЙКЕРОМ и
   *     означает «пришёл защищать позицию».
   */
  after?: { wallet: string; tokens: ParticipantToken[]; name?: string };
  /**
   * Итог кошелька по погашению ЭТОГО окна. Отсутствие означает «не знаем», а не
   * ноль: ноль сказал бы «вышел в ноль». Причина — одна на таблицу, в
   * ParticipantsStats.settled_reason.
   */
  settled?: WalletSettlement;
  /**
   * РУЧНЫЕ пометки оператора об этом кошельке. Зеркало webapi.participantRow.Tags.
   *
   * Отдельно от `tier` и `profile`, хотя все три показываются чипами в одной
   * ячейке, и различие принципиальное: те две ВЫВЕДЕНЫ обходами и переписываются
   * ими, а эти произнёс человек — их не выводит ничто, и проверить их нечем.
   *
   * Отсутствие поля и пустой список означают здесь одно и то же — «не помечен».
   * Это не как у `flow` и `after`, где молчание и пустота разные ответы: пометку
   * ставит человек, и другого смысла у её отсутствия нет.
   */
  tags?: string[];
}

/**
 * Итог кошелька по погашению окна. Зеркало webapi.walletSettlement.
 *
 * # Чем он отличается от колонки `flow.pnl_usd` рядом
 *
 * Тем, ЧТО описывает. `flow` — свод кошелька по ВСЕМ коротким рынкам за
 * выбранное окно суток, ответ на вопрос «кто он вообще». Здесь — его результат
 * в том окне, которое оператор смотрит. Числа расходятся законно и на порядки,
 * поэтому колонки стоят в разных группах таблицы.
 */
export interface WalletSettlement {
  /** Σ(продажи − покупки) + погашение победившего токена − комиссия. */
  pnl_usd: string;
  fee_usd: string;
  /**
   * Тот же итог ДО вычета комиссии: профиль Polymarket показывает именно его, и
   * без разбора рядом расхождение выглядит ошибкой счёта.
   */
  pnl_gross_usd: string;
}

/** Исход, которым посчитана колонка итога. Зеркало webapi.marketSettlement. */
export interface MarketSettlement {
  /** Биржа подтвердила исход. Ложь — «ещё не знаем». */
  resolved: boolean;
  winner_asset?: string;
  /** Имя исхода. Пусто — токена нет в справочнике рынка. */
  winner?: string;
}

/**
 * Встречи одного кошелька с нами. Зеркало webapi.againstUs и storage.AgainstUs.
 *
 * Две половины НЕ складываются в одно число намеренно: кошелёк, который только
 * впитывает наш налив, и кошелёк, который нас снимает, требуют разного поведения.
 */
export interface AgainstUs {
  /** Он ПРИНЯЛ наш налив: мы били, он стоял в стакане. Share и доллары. */
  took: string;
  took_usd: string;
  /** Он СНЯЛ нас: мы стояли, бил он. Размер здесь НАШ — его на проводе нет. */
  hit: string;
  hit_usd: string;

  /** Наших сделок, разделённых с ним. Не встречных строк: их бывает две на одну. */
  trades: number;
  first_at: string;
  last_at: string;

  /**
   * Якорь последней встречи — НАША сторона в ней.
   *
   * По нему считается, куда ушла цена с тех пор: «против нас» определяется
   * относительно нашей позиции, а не его. Пустой токен означает, что наш филл
   * ещё не посчитан, и разность тогда не считается вовсе.
   */
  last_asset_id?: string;
  last_side: Side;
  last_price?: string;
}

/** Держатель шеров СЕЙЧАС. Зеркало dataapi.Holder. */
export interface Holder {
  wallet: string;
  asset_id: string;
  /**
   * БАЛАНС в share, а не накопление сделками: сюда входят шеры, полученные
   * минтом пары. Расхождение с накоплением по одному кошельку — само сигнал.
   */
  amount: string;
  outcome_index: number;
  name?: string;
  pseudonym?: string;
}

/** Держатели одного исхода. Группировка задана источником. */
export interface TokenHolders {
  asset_id: string;
  holders: Holder[];
  /**
   * Сколько строк ПРИСЛАЛ источник — на этом стоит признак насыщения выдачи.
   *
   * Потолок он считает по токену, и список из ровно `limit` строк означает
   * «верхушка», а не «состав». Без этого числа отсутствие кошелька в списке
   * читалось бы как «не держит», тогда как он может просто не попасть в топ.
   */
  rows?: number;
}

/** Тег и сколько кошельков им помечено. Зеркало storage.WalletTagCount. */
export interface WalletTagCount {
  tag: string;
  /**
   * Число помеченных кошельков. Едет рядом с именем не для красоты: «картель · 3»
   * отличает живую пометку от опечатки, поставленной однажды и забытой.
   */
  wallets: number;
}

/** Тело GET /api/v1/wallets/tags. */
export interface WalletTagsResponse {
  tags: WalletTagCount[];
}

/**
 * Тело POST /api/v1/wallets/tags.
 *
 * Две половины, и обе нужны: `wallets` перерисовывает чипы в строках (ключ —
 * адрес в нижнем регистре), `tags` — список фильтра, где у тега изменилось число
 * кошельков, а новый тег появился вовсе.
 */
export interface WalletTagsApplyResponse {
  wallets: Record<string, string[]>;
  /**
   * Словарь ПОСЛЕ правки. `null` означает «сервер не смог его перечитать»,
   * пустой массив — «пометок не осталось ни одной», и разница здесь не
   * формальная: снятие последнего тега стирает его из словаря, и прочитай эта
   * сторона пустоту как отказ — список фильтра остался бы со снятой пометкой до
   * следующего опроса.
   */
  tags: WalletTagCount[] | null;
}

/**
 * Кто сколько набрал в рынке и какой ролью.
 *
 * Зеркало webapi.participantsResponse. Полей честности много, и каждое закрывает
 * свой способ соврать этой таблицей — см. комментарии.
 */
/**
 * Имя подгруппы на проводе. Префикс — знак: `plus_*` плюсовые, `minus_*`
 * минусовые, `rest` остальные. Знак выводится разбором префикса (lib/cohorts
 * signOf), второй таблицы «какая подгруппа какого знака» здесь нет.
 *
 * Плюсовые делятся на «торгом» (пнл > 0 и без выплат) по роли и на живущих на
 * выплатах (пнл ≤ 0, а с выплатами плюс); минусовые — по роли. Роль — доля
 * тейкерских сделок по всей глубине против порогов из ответа.
 */
export type ParticipantCohortKind =
  | "plus_taker"
  | "plus_maker"
  | "plus_mixed"
  | "plus_payouts"
  | "minus_taker"
  | "minus_maker"
  | "minus_mixed"
  | "rest";

/**
 * Одна подгруппа кошельков рынка.
 *
 * Зеркало webapi.participantCohort. Группа назначается кошельку за ВСЮ
 * накопленную глубину и от `?flow=` не зависит: на экране это два разных окна, и
 * смешать их — главный способ соврать этим блоком.
 */
export interface ParticipantCohort {
  kind: ParticipantCohortKind;

  /** Кошельков в группе. Σ по ВСЕМ группам ОБЯЗАНА равняться wallets ответа. */
  wallets: number;
  /**
   * Накопление группы в ЭТОМ рынке, строка на токен.
   *
   * Набор токенов ОДИН И ТОТ ЖЕ у всех групп, включая пустые: иначе столбцы на
   * экране разъезжались бы между опросами.
   */
  tokens: ParticipantCohortToken[];

  /**
   * Знаковый перевес группы между исходами. Пусто — рынок не бинарный, сводить
   * нечем: прочерк, а не ноль.
   */
  edge?: CohortEdge;

  /**
   * У скольких кошельков группы свод вообще есть.
   *
   * У «остальных» это МЕНЬШЕ wallets: у необойдённых чисел нет, и деньги группы
   * посчитаны не по всем её кошелькам.
   */
  known: number;

  /**
   * Роли кошельков группы: тейкеров, мейкеров, смешанных. Складываются в
   * wallets. У ролевых подгрупп тривиальны, содержательны у живущих на
   * выплатах — там роль в имя не зашита.
   *
   * Отсутствует у «остальных», и это не ноль: у необойдённых сделок нет вовсе,
   * а у кошельков с непригодными сутками доля агрессии посчитана по разным
   * наборам суток.
   */
  roles?: CohortRoles;

  /**
   * Победы по окнам НАШЕГО архива. Отсутствие — «не читали» (отказ базы), нули
   * — «читали, окон нет». Это не перевес (edge).
   */
  wins?: CohortWins;

  /**
   * Почему кошелёк не отнесён ни к плюсовым, ни к минусовым. Причины взаимно
   * исключающие, поэтому их сумма равна wallets у «остальных» и нулю у двух
   * других групп.
   *
   * Разделять обязательно: `unwalked` — это НЕЗНАНИЕ (обхода не было, кошелёк
   * может оказаться кем угодно), а `below_turnover` — знание («посмотрели,
   * мелкий»). Слитые в одно, они дали бы уверенное «мелочь», которого у нас нет.
   */
  ours: number;
  unwalked: number;
  unusable_days: number;
  /**
   * Сутки, которые обход не дочитал до конца.
   *
   * Отдельно от `unusable_days`, потому что дефект другой: там «денег не
   * посчитали», здесь «потерян самый старый кусок дня неизвестного размера».
   * Ошибка урезания НЕ в сторону строгости — оно уменьшает измеренный убыток, то
   * есть плохой кошелёк выглядит лучше, чем он есть.
   */
  truncated_days: number;
  below_turnover: number;
  flat: number;

  /**
   * Покрытие выплат: у скольких кошельков группы их СПРАШИВАЛИ и у скольких они
   * НАШЛИСЬ. Первое всегда не меньше второго.
   *
   * Различие не украшение: «спросили, и переводов не было» — знание, а «не
   * спрашивали» — незнание, которое рассосётся обходом. По самой таблице выплат
   * эти два случая неразличимы, и строка кошелька их не различает — а группа
   * различает, и в этом её ценность.
   */
  incentives_asked: number;
  incentives_seen: number;

  /**
   * Глубина свода по кошелькам группы, у которых обход БЫЛ. Ноль — не было ни у
   * кого.
   *
   * Медиана, а не среднее: старожилы тянут среднее вверх и прячут хвост
   * однодневок, а один однодневка обнулил бы минимум.
   */
  days_median: number;
  days_min: number;
  days_max: number;
  /**
   * Сколько кошельков группы классифицированы по ОДНОМУ обходу, то есть их знак
   * есть один день торгов, а не история.
   */
  one_pass: number;

  /**
   * Деньги группы на ВСЕХ коротких рынках за всю глубину.
   *
   * Пнл и выплаты стоят ПОРОЗНЬ, хотя знак группы получен их суммой: сумма —
   * одно число со знаком, а вопрос «на чём эта группа живёт» отвечается двумя.
   */
  turnover_usd: string;
  pnl_usd: string;
  incentives_usd: string;
}

/** Роли кошельков группы. */
export interface CohortRoles {
  taker: number;
  maker: number;
  mixed: number;
}

/**
 * Победы группы по окнам архива.
 *
 * `wallets` и `windows` — покрытие: у скольких кошельков группы архив видел хоть
 * одно окно и сколько окон всего. Доля `won / windows` без них читалась бы как
 * утверждение о группе, тогда как она утверждение о тех, кого архив застал.
 *
 * `judged` и `guessing` — счётчик «угадывают»: кошельки с окнами не ниже порога
 * `win_min_windows` и среди них те, у кого доля побед не ниже `win_min_share`.
 * Знаменатель счётчика — `judged`, а не `wallets`: два окна из двух — не мнение.
 */
export interface CohortWins {
  wallets: number;
  windows: number;
  won: number;
  judged: number;
  guessing: number;
}

/**
 * Накопление ГРУППЫ по одному токену.
 *
 * Те же поля, что у ParticipantToken, но БЕЗ счётчиков сделок: Σ trades по
 * группе двоит сделку, обе стороны которой попали в одну группу.
 */
export interface ParticipantCohortToken {
  asset_id: string;
  net: string;
  /**
   * Роль приезжает ВСЕГДА. Гасит её при `truncated` интерфейс — там же, где
   * гасит у строки кошелька: два места, решающие, чему верить, однажды решили бы
   * по-разному.
   */
  taker: string;
  maker: string;
  bought: string;
  sold: string;
  bought_usd: string;
  sold_usd: string;
}

/**
 * Знаковый перевес группы между двумя исходами.
 *
 * Отвечает на вопрос «есть ли у группы мнение о направлении», тогда как колонки
 * шеров отвечают «что именно она держит». Расходятся они законно: кошелёк,
 * набравший ОБА исхода, по нетто на токен выглядит набравшим дважды, а перевеса
 * не даёт вовсе.
 */
export interface CohortEdge {
  /** Куплено минус продано, в шерах: чем группа получит на погашении. */
  shares: string;
  /** Вложено минус изъято, в долларах. НЕ шеры, умноженные на цену. */
  usd: string;
  /**
   * Σ|перевес| ПО КОШЕЛЬКАМ группы. Меньше |usd| не бывает; равенство означает
   * «все смотрят в одну сторону», а большое превышение — «участники гасят друг
   * друга». Сырое число, а не отношение: порог «мнения нет» не изобретаем.
   */
  abs_usd: string;
  /** Имён исходов тут нет — их подставляет интерфейс из справочника рынка. */
  first_asset_id: string;
  second_asset_id: string;
}

/** Разбиение кошельков рынка при ОДНОЙ ступени порога оборота. */
export interface ParticipantCohortSplit {
  /** Порог оборота за всю глубину, в долларах строкой. «0» — без порога. */
  min_turnover_usd: string;
  groups: ParticipantCohort[];
}

/**
 * Сводка по группам кошельков — зеркало webapi.participantCohorts.
 *
 * Все ступени лестницы порогов приезжают ОДНИМ ответом, и порог не уходит в
 * запрос вовсе. Иначе он попал бы в ключ опроса, и между нажатием и ответом на
 * экране стояла бы сводка по прежнему порогу под новой кнопкой — третье такое
 * расхождение в одной панели.
 */
export interface ParticipantCohorts {
  /** Окно КЛАССИФИКАЦИИ — эхо. Всегда «all», и приезжает всё равно. */
  window: FlowWindow;
  /** Какую ступень считать умолчанием, когда оператор не выбирал. */
  default_min_turnover_usd: string;
  /**
   * Глубина ОДНОГО обхода в сутках. Приезжает, а не зашита здесь: границу задаёт
   * обходчик, и вторая копия числа разошлась бы с первой молча.
   */
  one_pass_days: number;
  /**
   * Границы роли и «угадывающих» — эхо настройки, как one_pass_days: формула
   * под именем подгруппы печатает порог из ответа, а не константу. Равные
   * границы роли означают, что смешанных нет по построению, — их строки
   * прячутся по этому признаку, а не по нулю кошельков.
   */
  maker_max_aggression: string;
  taker_min_aggression: string;
  win_min_share: string;
  win_min_windows: number;
  splits: ParticipantCohortSplit[];
}

/**
 * Ход накопления во времени — зеркало webapi.timelineResponse.
 *
 * # Чем отличается от сводки групп
 *
 * Сводка отвечает «сколько группа набрала» ЧИСЛОМ НА СЕЙЧАС, ряд — «как она
 * набирала». Источник у них ОДИН И ТОТ ЖЕ (строки окна из снимка панели), и это
 * не совпадение, а требование: последняя точка ряда обязана совпасть с числом в
 * таблице над графиком, иначе график кончался бы не там, где стоит его итог.
 *
 * # Почему цена приезжает отдельным рядом, а не в тех же точках
 *
 * Потому что источники разные: накопление — сделки из ленты, котировка — журнал
 * вершины стакана, который пишет трейдер (bus.BookLogKey). У них разные дыры:
 * ленты может не быть при живом стакане и наоборот. Слитые в один ряд, они
 * заставили бы интерфейс изобретать точку там, где известна половина.
 */
export interface Timeline {
  condition_id: string;
  /** Ноль оси. Пусто — у рынка нет окна серии, и отсчитывать не от чего. */
  window_start?: string;
  window_end?: string;
  /**
   * Левый край оси в секундах от начала окна, неположительный: предстарт стоит
   * на оси. Приходит с сервера, чтобы точки легли на те же деления, по которым
   * сервер их раскладывал.
   */
  axis_from_s?: number;
  step_s: number;
  /** Эхо фильтра состава — ТОГО ЖЕ, что режет таблицу накопления. */
  tags?: string[];
  notags?: string[];
  groups?: ParticipantCohortKind[];
  notgroups?: ParticipantCohortKind[];
  /**
   * `picked_asked` — сколько адресов пришло отметками, `picked` — сколько
   * кошельков снимка попало в состав. Разные числа: отметки живут дольше окна.
   */
  picked_asked?: number;
  picked?: number;
  /** Почему линии отбора нет. Пусто — состав отобран. */
  pick_reason?: string;
  /** К первому исходу знак перевеса положителен. Имена подставляет интерфейс. */
  first_asset_id?: string;
  second_asset_id?: string;
  /**
   * Набранное ДО открытия окна, в ШЕРАХ: значение линии на нуле оси.
   *
   * Отдельным полем, а не отрицательными секундами: досделочные сорок минут
   * раздавили бы на экране пять минут самого окна.
   */
  before_open: string;
  before_open_all: string;
  /** То же по рынку в долларах. */
  before_open_all_usd?: string;

  points: TimelinePoint[];
  quotes: TimelineQuote[];
  /** Почему ряда котировки нет либо он короче ряда накопления. */
  quotes_reason?: string;
  /**
   * Индекс и оценка окна по той же сетке. Необязательное — у ответа старого
   * web_api поля нет, и панель тогда просто не рисует полотен индекса.
   */
  index?: TimelineIndexPoint[];
  /** Почему ряда индекса нет. */
  index_reason?: string;
  /** Сдвиг спота к шкале индекса (`сырой − спот`) одним числом на окно; нет — линии биржи нет. */
  index_spot_basis?: string;
  /** Почему раздача по группам не посчитана: линии группы тогда нет. */
  groups_reason?: string;
  /**
   * Возраст раздачи по группам. Панель считает её каждый опрос, ряд — раз в
   * минуту, и расхождение чипа с графиком возможно ровно на этот возраст.
   */
  groups_age_ms?: number;

  /** Знаменатель: «линия ровная» на трёх сделках и на трёх тысячах — разное. */
  trades: number;
  wallets: number;

  as_of: string;
  source?: "feed" | "dataapi";
  next_in_ms: number;
  /**
   * Состояние окна трейдера, по которому построен ряд, — те же числа и тот же
   * смысл, что у панели (ParticipantsStats.epoch/seq): стук не новее их ряд
   * не перечитывает.
   */
  epoch?: number;
  seq?: number;

  known: boolean;
  truncated?: boolean;
  partial?: boolean;
  reason?: string;
}

/**
 * Одна корзина сетки. Тихая корзина ПРИСУТСТВУЕТ с прежним накоплением.
 *
 * Величин на точку три, и они отвечают на разные вопросы. `net` — «в какую
 * сторону и сколько» в шерах (столько группа получит на погашении), `edge` —
 * «сколько за это отдано» в долларах, `avg` — «по какой цене набирали». На
 * дешёвой стороне шеры и доллары расходятся на порядок, и подменить одно другим
 * значит соврать о размере ставки.
 */
export interface TimelinePoint {
  /** Секунды от начала окна. */
  t: number;
  /** Нетто ОТОБРАННЫХ в шерах, со знаком к первому исходу. */
  net?: string;
  /** Их средняя цена покупок на оси первого исхода. Пусто — покупок не было. */
  avg?: string;
  /** Их перевес в долларах. */
  edge?: string;
  /**
   * Разрез нетто: исход × роль, тот же, что колонки «Тейкером» и «Мейкером» в
   * таблице накопления. В ПРОЕКЦИИ на ось перевеса — составляющие второго
   * исхода идут со знаком минус, потому что тянут перевес вниз. Сумма четырёх
   * равна `net`, иначе разложение не было бы разложением.
   */
  up_taker?: string;
  up_maker?: string;
  down_taker?: string;
  down_maker?: string;
  /** То же по всему рынку. */
  all_net: string;
  all_avg?: string;
  all_edge: string;
  /**
   * Перевес рынка, разложенный ПО РОЛИ: тейкеры и мейкеры.
   *
   * По роли, а не по исходу × роль, как у отобранного состава: по рынку целиком
   * разрез по исходу упирается в чеканку комплектов (покупка Up идёт в паре с
   * покупкой Down), и составляющие там на порядок больше своей разницы. Сумма
   * двух ролей — ноль: у мейкеров ровно противоположное тейкерам.
   */
  all_taker?: string;
  all_maker?: string;
  /**
   * ОБОРОТ корзины по всему рынку, без направления: Σ размеров всех записанных
   * исполнений.
   *
   * Без направления потому, что в журнале лежат ОБЕ ноги каждой сделки, и по
   * рынку целиком стороны гасят друг друга. Оборот при этом честен и отвечает
   * на свой вопрос: когда затишье, а когда шёл поток.
   */
  all_flow?: string;
  /**
   * Крупнейшее ОДИНОЧНОЕ исполнение корзины по рынку, со знаком. Одна строка —
   * одно действие одного кошелька, и направление у неё своё.
   */
  all_top?: string;
  /** Сделок В ЭТОЙ корзине, а не нарастающим итогом. */
  trades: number;
  /**
   * Поток ЗА КОРЗИНУ: когда пришла ставка и какого объёма.
   *
   * Стороны порознь и обе неотрицательны: сведи их — и две встречные ставки
   * одной секунды погасили бы друг друга, то есть крупная ставка исчезла бы с
   * графика ровно в тот момент, ради которого он и заведён. Пусто — ставок в
   * эту секунду не было вовсе.
   */
  in_up?: string;
  in_down?: string;
  /**
   * Самая крупная ОДИНОЧНАЯ сделка корзины, со знаком проекции. Отличает «одну
   * крупную ставку» от «многих мелких»: по высоте столбика они неразличимы.
   */
  top?: string;
}

/**
 * Вершина стакана в корзине.
 *
 * Корзины без образца в ряду НЕТ ВОВСЕ — в отличие от накопления, где тихая
 * секунда рисуется ровной линией. Разница содержательная: накопление между
 * сделками не меняется, а котировка между образцами неизвестна, и соединённый
 * пробел был бы нарисованным движением цены, которого мы не наблюдали.
 */
export interface TimelineQuote {
  t: number;
  up_bid?: string;
  up_ask?: string;
  down_bid?: string;
  down_ask?: string;
  /** Возраст стакана в образце: по этому ряду судят, что чему предшествовало. */
  age_ms: number;
}

/**
 * Индекс и оценка окна в корзине оси — ровно то, что в ту секунду показывала
 * карточка рынка. Корзины без записи журнала нет вовсе, как и у котировки.
 */
export interface TimelineIndexPoint {
  t: number;
  /** Опубликованная метрика (S на карточке). */
  index: string;
  /** Возраст опубликованного значения в момент записи. */
  age_ms: number;
  /** Наш отчёт, которого источник ещё не опубликовал (Ŝ, метка t−3). */
  ahead?: string;
  /** Тот же отчёт по достроенному ряду и число дописанных оценкой секунд. */
  fwd?: string;
  fwd_estimated?: number;
  /** Страйк этой секунды: ПРОГНОЗ до фиксации (strike_forecast), записанный после. */
  strike?: string;
  strike_forecast?: boolean;
  /** Разброс прогноза страйка. */
  strike_sigma?: string;
  /** S минус страйк. */
  deviation?: string;
  /** Итог при замершей цене минус страйк; «0» — стороны ещё нет, а не пропуск. */
  cushion?: string;
  /** Тот же запас по достроенному ряду. */
  cushion_ahead?: string;
  /** Сырой ряд Chainlink без усреднения — последний известный в эту секунду. */
  raw?: string;
  /** Спот биржи в USDT — последний известный; на шкалу индекса его кладёт index_spot_basis. */
  spot?: string;
}

export interface TimelineResponse {
  timeline: Timeline;
}

/**
 * Уведомление «окно изменилось» из канала md.flow.<condition_id>.
 *
 * Не данные, а приглашение перечитать панель: свод считается на сервере под
 * параметрами запроса (окно статистики, режим, фильтры), которых у сокета нет.
 */
export interface FlowNotice {
  condition_id: string;
  epoch: number;
  seq: number;
  published_at: string;
}

export interface ParticipantsStats {
  condition_id: string;

  rows: ParticipantRow[];
  /** Исход, которым посчитана колонка итога. Пусто — не спрашивали. */
  resolution?: MarketSettlement;
  /**
   * Почему итога нет НИ У КОГО: исход не подтверждён, выборка обрезана либо
   * расписание комиссии неизвестно. Причина у всех кошельков окна общая,
   * поэтому одна строка на таблицу, а не повторённая в каждой строке.
   */
  settled_reason?: string;
  holders?: TokenHolders[];

  /** Сделок по рынку: строк тейкерской выборки, по строке на транзакцию. */
  trades: number;
  /** Кошельков ВСЕГО, до отсечки rows. Без него сотня строк выглядит составом. */
  wallets: number;
  /**
   * Кошельков в окне ДО ЛЮБОГО фильтра.
   *
   * Заведено вместе с фильтром по тегам, потому что `wallets` выше под фильтром
   * меняет смысл — он описывает таблицу, которая сейчас на экране. Без второго
   * числа «34 кошелька» под фильтром читалось бы как утверждение о рынке, тогда
   * как это утверждение о помеченном меньшинстве.
   *
   * Необязательное: ответ старого сервера этого поля не несёт.
   */
  wallets_total?: number;

  /**
   * Эхо фильтра по РУЧНЫМ пометкам: кого оставили и кого убрали.
   *
   * Эхо, а не «что нажато», по той же причине, что flow_window и against: между
   * нажатием и ответом на экране стоит прежняя таблица, и подписать её новым
   * фильтром значило бы соврать про состав строк.
   */
  tags_include?: string[];
  tags_exclude?: string[];
  /**
   * Почему пометок нет ни у одной строки: нет базы либо отказ чтения. Пусто
   * означает «прочитали, помеченных нет» — а это другой факт.
   */
  tags_reason?: string;

  /**
   * Эхо фильтра по ПОДГРУППЕ сводки — «показаны только плюсовые тейкеры».
   *
   * Отдельно от tags_include, потому что величины разные: пометку произнёс
   * человек, подгруппу вывел обход, и на экране они отличаются подсказкой чипа.
   *
   * Приезжает, только если фильтр ПРИМЕНЁН. Подгруппы не бывает в режиме
   * «против нас» и при отказе базы; нажатие тогда ничего не отфильтровало, и
   * подпись «показаны плюсовые тейкеры» над полным составом рынка была бы худшим
   * из ответов — вместо неё приезжает groups_reason.
   */
  groups_include?: ParticipantCohortKind[];
  groups_exclude?: ParticipantCohortKind[];
  /** Почему нажатый фильтр по подгруппам не применён. */
  groups_reason?: string;

  /**
   * Сводка по подгруппам кошельков: плюсовые по роли и источнику дохода,
   * минусовые по роли, остальные.
   *
   * Считается по ВСЕМ кошелькам снимка, а не по отсечённым rows: блок заведён
   * ради накопления, рассыпанного по десяткам мелких кошельков, и агрегат по
   * сотне крупнейших воспроизвёл бы ровно тот дефект.
   *
   * Отсутствие — «не считали»: сервер до вехи, отказ базы либо режим «против
   * нас». Почему именно — в cohorts_reason.
   */
  cohorts?: ParticipantCohorts;
  cohorts_reason?: string;

  /** Когда web_api получил снимок. Не время сделок. */
  as_of: string;
  /**
   * Чем получен снимок: живой лентой активности трейдера или индексатором.
   *
   * Возраст данных без источника не читается вовсе: «4 мин» на индексаторе —
   * норма его устройства, на ленте — отказ. Поэтому бейдж печатает источник
   * словом, а планка тревоги у них разная (см. ageLabel).
   */
  source?: "feed" | "dataapi";
  /**
   * Матчи ленты, не подтверждённые расчётом в цепочке и потому не вошедшие в
   * свод, и сколько строк было до отсева. На индексаторе оба нуля: там строка
   * появляется только после расчёта.
   */
  removed?: number;
  fills_n?: number;
  /**
   * Окно наблюдается НЕ С НАЧАЛА: трейдер поднялся, когда оно уже шло.
   *
   * Истории у ленты нет вовсе, поэтому сделки до подключения не восстановятся
   * никак, и сверка выборок (`balanced`) на таком окне не сходится ЗАКОННО.
   * Без этого признака собственный запуск читался бы как поломка источника.
   */
  partial?: boolean;

  /**
   * Самая свежая сделка ИСТОЧНИКА — главное поле честности.
   *
   * У индексатора она отстаёт минутами и отстаёт РЫВКАМИ (замер: шесть опросов
   * за 31 секунду, метка не сдвинулась ни разу). На живом пятиминутном окне это
   * значит, что таблица описывает ПРОШЕДШЕЕ окно, и возраст обязан быть на
   * экране. У ленты она отстаёт на секунду, и та же минута означает уже другое —
   * что трейдер замолчал.
   */
  newest?: string;
  oldest?: string;

  /**
   * Через сколько миллисекунд снимок станет негодным, то есть когда спрашивать
   * снова. НЕ возраст данных: такт нашего конвейера, а не свежесть источника.
   *
   * Такт задаёт СЕРВЕР, потому что срок годности снимка плавает по цене обхода:
   * пустое пятиминутное окно стоит трёх запросов к Data API, полное окно BTC —
   * дюжины. Пока такт был константой на обеих сторонах, фазы двух таймеров жили
   * независимо, и замер 2026-08-11 показал цену: возраст данных пилой рос
   * 2 → 19 → 36 → 52 секунды на НЕИЗМЕННОМ снимке.
   *
   * Необязательное: ответ старого сервера этого поля не несёт, и разбирать такой
   * случай приходится (см. nextPollMs в lib/participants.ts).
   */
  next_in_ms?: number;

  /**
   * Состояние окна трейдера, по которому собран свод: те же числа, что в
   * стуке md.flow.<condition_id> (FlowNotice). Вкладка не перечитывает панель
   * по стуку, который не новее этих (lib/participants.ts: flowNoticeIsNews).
   * Отсутствуют на индексаторе Data API и у старого сервера — тогда
   * перечитываем по любому стуку, как раньше.
   */
  epoch?: number;
  seq?: number;

  /**
   * Окно, за которое посчитан блок flow каждой строки, — эхо запроса.
   *
   * Подписывать колонки надо ИМЕННО им, а не тем, что выбрано в интерфейсе:
   * ответ приходит асинхронно, и подпись «за 30 суток» иначе встала бы над
   * числами, посчитанными за неделю. Необязательное: ответ старого сервера этого
   * поля не несёт.
   */
  flow_window?: FlowWindow;

  /**
   * Панель показывает ТОЛЬКО наших контрагентов — эхо запроса, как flow_window.
   *
   * Подписывать таблицу надо им, а не нажатой кнопкой: ответ приходит позже
   * нажатия, и «против нас» иначе встало бы над строками всего рынка.
   */
  against?: boolean;
  /**
   * Со сколькими кошельками мы вообще торговали в этом рынке — по НАШЕМУ журналу.
   *
   * Отдельно от wallets, потому что числа расходятся, и расхождение
   * содержательно: контрагент, известный журналу и отсутствующий в своде,
   * означает, что свод обрезан потолком (см. truncated) либо индексатор до этой
   * сделки ещё не дошёл.
   */
  against_wallets?: number;
  /**
   * Наши мейкерские сделки в рынке без восстановленного агрессора.
   *
   * НЕВИДИМЫЕ ВСТРЕЧИ: кошелька у них нет, и в таблицу они не попадают вовсе.
   * Показывать это обязательно — иначе свод молча занижен ровно у тех, кто нас
   * бьёт.
   */
  against_pending?: number;

  /**
   * Последняя цена ПРИНТА по каждому токену рынка, ключ — asset_id.
   *
   * Нужна ради одной колонки: куда ушла цена с момента нашей встречи. Разность
   * считается здесь, а не на сервере, потому что оба её слагаемых уже в ответе, а
   * второе место расчёта разошлось бы с первым.
   */
  last_price?: Record<string, string>;

  known: boolean;
  stale: boolean;
  /**
   * Выборка обрезана потолком источника. Роль при этом НЕВЕРНА, а не менее
   * точна: замер на обрезанном рынке дал расхождение сумм в 137 %.
   */
  truncated: boolean;
  /** Инвариант источника сошёлся: Σ полной выборки примерно вдвое больше. */
  balanced: boolean;
  reason?: string;
  holders_reason?: string;
}

/**
 * Разрешение авторежима заявки на кромке по ОДНОМУ окну.
 *
 * Зеркало webapi.makerArmState.
 *
 * # Почему по окну, а не одним тумблером
 *
 * Окна живут пять минут и сменяются сами. Глобальный тумблер означал бы решение,
 * принятое однажды и действующее на рынках, которых в тот момент ещё не
 * существовало. Здесь разрешение ключуется рынком: у нового окна его просто нет,
 * и каждое окно начинается ВЫКЛЮЧЕННЫМ.
 *
 * # Чего здесь НЕТ
 *
 * Потолка окружения (AUTOMAKER_ENABLED) и порога разрешённости. Они живут у
 * трейдера, а web_api их не знает by design; в интерфейс они приезжают нотами
 * `automaker` и `automaker_bid` в GET /status.
 */
export interface MakerArm {
  condition_id: string;
  enabled: boolean;
  /** Сумма ОДНОЙ заявки, в долларах. «0» — не задана. */
  usd: string;
  /**
   * Оператор трогал это окно. Отличает «выключил» от «не включал»: на экране
   * это одно положение переключателя, но второе означает ещё и «ключа нет».
   */
  set: boolean;
  armed_at?: string;
  armed_by?: string;
}

/** Тело GET и POST /api/v1/markets/{conditionID}/automaker. */
export interface MakerArmResponse {
  automaker: MakerArm;
}

/** Тело GET /api/v1/markets/{conditionID}/participants. */
export interface ParticipantsResponse {
  participants: ParticipantsStats;
}

/**
 * Роль кошелька в одной сделке. Зеркало webapi.tradeRole.
 *
 * Пустая строка — третье значение, а не «забыли». Признака «тейкер» у строки
 * источника нет, и роль восстанавливается по транзакции: тейкерская выборка
 * отдаёт ровно одну строку на транзакцию — её агрессора. Но когда кошелёк стоял
 * в транзакции несколькими ордерами, разложить его строки поимённо нечем, и
 * прочерк честнее правдоподобной ошибки.
 */
export type TradeRole = "taker" | "maker" | "";

/** Одна сделка кошелька в окне. Зеркало webapi.walletTrade. */
export interface MarketWalletTrade {
  at: string;
  asset_id: string;
  side: Side;
  size: string;
  price: string;
  /** Нотионал строки. Считает сервер: два умножения разошлись бы. */
  usd: string;
  role?: TradeRole;
  /**
   * По нему видно, что десяток строк подряд — это ОДНА сделка, разложенная
   * биржей на встречные ордера, а не десять решений кошелька.
   */
  tx_hash?: string;
}

/**
 * Тело GET /api/v1/markets/{conditionID}/participants/{wallet}/trades.
 *
 * Отвечает на вопрос, которого не знает панель накопления: не «сколько он
 * набрал», а КАК — зашёл на первой секунде или дождался последней минуты, долил
 * разом или размазал по сотне филлов, брал агрессией или стоял в стакане.
 */
export interface MarketWalletTrades {
  condition_id: string;
  wallet: string;
  trades: MarketWalletTrade[];
  /** Строк больше, чем отдано. Это НАШ потолок, а не потолок источника. */
  truncated: boolean;
  /** Роль разложена по всем строкам. Ложь — см. TradeRole. */
  roles_known: boolean;
  as_of: string;
  /**
   * Чем получен список: живой лентой или индексатором.
   *
   * На ленте `roles_known` истинен всегда: роль там помечена в самой строке
   * (комиссия приходит ровно у агрессора), а у индексатора она восстанавливается
   * сравнением двух выборок и при нескольких ордерах кошелька неизвестна.
   */
  source?: "feed" | "dataapi";
  /** Почему списка нет, словами. */
  reason?: string;
}

export interface MarketWalletTradesResponse {
  wallet_trades: MarketWalletTrades;
}

/**
 * Один кошелёк в своде встреч. Зеркало webapi.counterpartyRow.
 *
 * Отвечает на вопрос о КОШЕЛЬКЕ, а не о сделке: «я накликал тысячу тейкером —
 * принял её один бот или тридцать разных, и кто стоит против меня каждый день».
 */
export interface CounterpartyRow {
  wallet: string;
  /**
   * Как кошелёк подписан на самой бирже. Пусто — имени не знаем: его нет у части
   * кошельков вовсе, а обход профайлера мог до него ещё не дойти.
   */
  name?: string;
  /** Он ПРИНЯЛ наш налив (мы били). Он СНЯЛ нас (мы стояли). Не складывать. */
  took: string;
  took_usd: string;
  hit: string;
  hit_usd: string;

  /** Наших сделок с ним и по скольким рынкам мы его встречали. */
  trades: number;
  markets: number;

  /**
   * НАШ результат встреч с ним, в долларах и со знаком.
   *
   * Считается по погашению окна: короткий рынок бинарен и гасится в 0 или 1,
   * поэтому у закрытого окна это точное число, а не оценка. Встреча на таком
   * рынке — игра с нулевой суммой, так что наш плюс здесь и есть его минус.
   *
   * settled_trades — по скольким из trades он посчитан. Меньшее число означает
   * непогашенные окна, и без него сумма читалась бы как итог ВСЕХ встреч.
   * won/lost отвечают на другой вопрос: не «сколько», а «как часто».
   */
  pnl_usd: string;
  settled_trades: number;
  won: number;
  lost: number;

  first_at: string;
  last_at: string;

  /** Пусто — профиля нет: тир считается по нашим встречам, а он мог быть первым. */
  tier?: WalletTier;
  /** Отсутствие означает «не обходили», а не «наторговал ноль». */
  flow?: WalletFlow;
  /**
   * Сколько ему доплатили помимо сделок за то же окно.
   *
   * Здесь у колонки есть смысл, которого нет на других экранах: строка отвечает
   * на вопрос «кто из нас прав», и наш плюс против его минуса перестаёт быть
   * игрой с нулевой суммой ровно потому, что третья сторона доплачивает обоим.
   */
  incentives?: WalletIncentives;
}

/** Тело GET /api/v1/counterparties. Зеркало webapi.counterpartiesResponse. */
export interface CounterpartiesStats {
  rows: CounterpartyRow[];
  /**
   * Наши мейкерские сделки в окне без восстановленного агрессора — невидимые
   * встречи. При выключенном восстановлении свод состоит из одной половины, и
   * молчать об этом нельзя.
   */
  pending_takers: number;

  /** Эхо запроса: этим подписывается таблица, а умолчания задаёт сервер. */
  since_hours: number;
  market?: string;
  flow_window: FlowWindow;

  truncated: boolean;
}

export interface CounterpartiesResponse {
  counterparties: CounterpartiesStats;
}

// ---------------------------------------------------------------------------
// Копирование ставок умных кошельков (ADR-0010)
// ---------------------------------------------------------------------------

/**
 * Режим копирования. Порядок значим: действующий режим — строгий МИНИМУМ из
 * потолка окружения трейдера и того, что выставила кнопка.
 *
 * Поднять выше потолка интерфейс не может по построению, и это не ограничение
 * клиента, а свойство сервера: трейдер считает минимум сам. Та же асимметрия,
 * что у аварийного стопа.
 */
export type FollowMode = "off" | "shadow" | "alert" | "auto";

/** Что стало с сигналом. Зеркало domain.SignalOutcome. */
export type SignalOutcome =
  /** Записан и посчитан, ордера не предполагалось: теневой режим. */
  | "shadow"
  /** Показан оператору и ЖДЁТ его решения — единственное состояние с кнопками. */
  | "alerted"
  /** Ордер отправлен: оператором либо авторежимом. */
  | "placed"
  /** Оператор нажал «пропустить». */
  | "skipped"
  /** Оператор не ответил до срока. */
  | "expired"
  /** Погашен правилом; каким именно — в поле rule. */
  | "suppressed";

/** Снимок квалификации кита НА МОМЕНТ сигнала. */
export interface WhaleStats {
  /**
   * Средняя игра на ОКНО: оборот, делённый на число рынков.
   *
   * На окно, а не на сделку: вопрос звучит «сколько он играет на одном рынке», а
   * не «каким куском он его набирает». Дробление входа — техника исполнения.
   */
  avg_usd: string;
  /**
   * По скольким окнам посчитана avg_usd. Знаменатель едет вместе с частным по
   * тому же правилу, что глубина рядом с PnL.
   *
   * Ноль означает «основание неизвестно»: строка сигнала старше миграции 00015,
   * и средняя в ней посчитана по СДЕЛКАМ.
   */
  markets: number;
  /**
   * PnL ЗА ВЫЧЕТОМ тейкерской комиссии, как везде у нас. Профиль Polymarket
   * показывает то же число без вычета и потому всегда больше.
   */
  pnl_usd: string;
  /**
   * ROI со знаком: pnl_usd, делённый на ВЛОЖЕННОЕ за окно оценки.
   *
   * Знаменатель — вложенное, а не оборот: десять процентов оборота на коротком
   * бинарном рынке — совсем другая редкость, чем десять процентов вложенного.
   * Выплаты (возврат комиссии) в числитель НЕ входят: это плата за услугу
   * рынку, а не мнение о цене.
   *
   * Ноль означает «основание неизвестно»: строка старше миграции 00021, и отбор
   * в ней шёл по PnL, а не по ROI.
   */
  roi: string;
  trades: number;
  /** За сколько суток посчитано. Глубина у каждого кошелька своя. */
  days: number;
  /** Сутки покрыты обходом не целиком: занижает и PnL, и число ставок. */
  partial?: boolean;
}

/**
 * Крупная ставка умного кошелька и наше решение по ней.
 *
 * Одним типом на все состояния карточки: появилась, взята, пропущена, сгорела.
 * Потребитель ЗАМЕЩАЕТ запись по id — то же правило, что у order_update.
 */
export interface FollowSignal {
  id: string;
  detected_at: string;
  /**
   * Машинное имя сценария, породившего строку.
   *
   * По одному удару кита законно рождается несколько сигналов — по одному на
   * сценарий, который на него откликнулся. Без имени они выглядели бы дублями.
   * `legacy` — единственное правило, которым подсистема работала до появления
   * списка сценариев.
   */
  scenario: string;
  /** Кит, в нижнем регистре. */
  wallet: string;
  /**
   * Как кит подписан на бирже. Пусто — подписи не знаем, и тогда на экране
   * адрес: подставлять его сюда нельзя, иначе «имени нет» станет неотличимо от
   * «имя такое».
   */
  wallet_name?: string;
  condition_id: string;
  asset_id: string;
  side: Side;
  tx_hash: string;
  slug?: string;
  question?: string;
  /** Человеческое имя исхода из справочника. Пусто — справочник токена не знает. */
  outcome_name?: string;
  window_end: string;

  /** Ставка кита: сумма и ЭФФЕКТИВНАЯ цена его ордера (quote / shares). */
  whale_usd: string;
  whale_price: string;
  /**
   * Знаковый перевес кита на окне в момент срабатывания, если он считался.
   *
   * Пусто означает «триггером была одна транзакция», а не «перевес нулевой»:
   * нулевой перевес — это мейкер, набравший оба исхода поровну, и сигнала он не
   * рождает вовсе.
   */
  accum_usd?: string;

  /**
   * Токен и сторона НАШЕГО ордера.
   *
   * Отдельно от asset_id и side, потому что сценарий умеет ставить ПРОТИВ кита:
   * тогда мы покупаем комплемент, и это РАЗНЫЕ токены. У следования совпадают.
   */
  plan_asset_id: string;
  plan_side: Side;
  /**
   * Наш план. У следования это цена кита, приведённая к шагу вниз; у фейда —
   * цена из стакана КОМПЛЕМЕНТА, а не 1 − цена кита: два стакана бинарного
   * рынка не складываются в единицу.
   */
  plan_price: string;
  plan_usd: string;
  plan_size: string;
  /** Срок ордера. Пусто — ордера не предполагалось (сигнал погашен правилом). */
  expires_at: string;
  /**
   * До какого момента нажатие «выставить» ещё имеет смысл — это НЕ срок ордера.
   *
   * Раньше `expires_at` на запас, без которого биржа гасит GTD-ордер прежде
   * срока: такой ордер риск-гейт отвергает целиком, и сервер отказывает по
   * сигналу задолго до того, как тот сгорит. Отсчёт карточки идёт до этого
   * момента, а не до `expires_at`.
   *
   * Отсутствует — отправитель старой версии; тогда срок считается сами (см.
   * lib/signals.ts, decideBy).
   */
  decide_by?: string;
  /**
   * По какой цене мы вошли бы ТЕЙКЕРОМ в момент сигнала: вершина стакана.
   * Отсутствует — стакана в тот момент не было, и вторая гипотеза не считается.
   */
  taker_price?: string;

  mode: FollowMode;
  outcome: SignalOutcome;
  /** Машинное имя правила, погасившего сигнал. */
  rule?: string;
  client_order_id?: string;
  /** Ордер пересёк стакан и исполнился тейкером — то есть заплатил комиссию. */
  crossed?: boolean;

  whale: WhaleStats;

  /**
   * Исход окна подтверждён. Отсутствует — «ещё не сводили», false — «Gamma не
   * подтвердила исход». Слитые в одно, они назвали бы проигрышем окно, о
   * котором мы ничего не знаем.
   */
  resolved?: boolean;
  won?: boolean;
  /**
   * Исполнился бы мейкерский ордер. ВЕРХНЯЯ ГРАНИЦА: очередь в стакане не
   * моделируется, и на нашем уровне вполне могли стоять раньше нас.
   */
  maker_filled?: boolean;
  /**
   * Минимум цены принта по НАШЕМУ токену за жизнь ордера.
   *
   * Отвечает на то, чего maker_filled не слышит: тот про ОДНУ цену — нашу, — а
   * спрашивают про любую. Мы стоим покупкой, и нас исполняет тот, кто согласен
   * продать по нашей цене или дешевле, значит бид по цене X исполнился бы ровно
   * тогда, когда min_print_price <= X. Отсюда вся лестница входа задним числом.
   *
   * Отсутствует — «не наблюдали»: строка старше миграции 00025, трейдер
   * перезапустился посреди жизни ордера либо принтов по токену не было. Это НЕ
   * «не исполнился бы», и складывать эти два случая нельзя.
   */
  min_print_price?: string;
  shadow_maker_pnl?: string;
  shadow_taker_pnl?: string;
  real_pnl?: string;
  settled_at?: string;
}

/**
 * Сводка по сигналам — то, ради чего работа и затевалась.
 *
 * Отвечает на два вопроса сразу: плюсовая ли гипотеза вообще и правильный ли
 * выбран вход. Числа лежат рядом намеренно.
 */
export interface FollowSummary {
  since?: string;
  signals: number;
  /** Сколько сведено: у остальных окно не погасилось либо исход не подтверждён. */
  settled: number;
  /** Сколько мейкерских входов исполнилось бы. Верхняя граница. */
  maker_filled: number;
  placed: number;
  won: number;
  /**
   * По скольким сведённым посчитана ТЕЙКЕРСКАЯ гипотеза. Меньше settled, если в
   * момент сигнала не было стакана: две суммы, посчитанные по разному числу
   * сигналов, сравнивать нельзя, не сказав об этом.
   */
  taker_priced: number;
  /**
   * По скольким ВЫСТАВЛЕННЫМ ордерам фактический результат посчитан. Меньше
   * placed на те, чьё окно ещё не свелось и чьи ордера не исполнились: real_pnl
   * считается ровно по ним.
   */
  real_filled: number;

  shadow_maker_pnl: string;
  shadow_taker_pnl: string;
  real_pnl: string;
}

/** Состояние режима по данным web_api. */
export interface FollowModeState {
  mode?: FollowMode;
  /**
   * Ключа в Redis НЕТ: оператор ничего не выставлял. Это не то же самое, что
   * mode: "off" — «выключено из интерфейса». При отсутствии ключа режим целиком
   * определяет окружение трейдера.
   */
  set: boolean;
  updated_at: string | null;
  updated_by: string | null;
}

export interface FollowSignalEvent {
  signal: FollowSignal;
}

// ---------------------------------------------------------------------------
// Замок исхода окна
// ---------------------------------------------------------------------------

/**
 * Состояние замка. Торговых из трёх — два.
 *
 * Считает его СЕРВЕР (services/internal/lock), а не браузер, и это не перенос
 * ради переноса: то же решение принимает подсистема алертов в трейдере, поднимая
 * карточку со сделкой. Посчитай его вторая реализация здесь — панель и карточка
 * разошлись бы в оценке ОДНОГО И ТОГО ЖЕ окна на первой же правке, причём тихо:
 * оба ответа выглядят правдоподобно.
 */
export type LockState = "none" | "safe" | "settled";

/** Чем меряется «замок вот-вот захлопнётся». Выбор оператора, а не константа. */
export type LockMode = "mean" | "empirical" | "sigma" | "fixed";

/** Какой ролью входим в сделку. */
export type LockKind = "maker" | "taker";

/** Что стало с карточкой замка. */
export type LockOutcome =
  "alerted" | "placed" | "skipped" | "expired" | "revoked";

/**
 * Готовое предложение по одному действию, посчитанное трейдером по ЖИВОМУ
 * стакану.
 *
 * Причина отказа лежит ЗДЕСЬ, а не заменяет план: карточка обязана показать,
 * почему одна кнопка не годится, продолжая предлагать вторую.
 */
export interface LockPlan {
  kind: LockKind;
  /** Лимитная цена ордера: у мейкера цена постановки, у тейкера потолок. */
  price?: string;
  /** Размер в share. */
  size?: string;
  /**
   * Сколько денег уйдёт по стакану, без комиссии. У тейкера это сумма по
   * ЗАДЕТЫМ УРОВНЯМ, а не size × price: лимит — потолок, а исполнение идёт по
   * встречным уровням, и улучшение биржа отдаёт тейкеру.
   */
  quote?: string;
  fee?: string;
  /**
   * false — расписание рынка незнакомо. Ноль показывать в этом случае нельзя:
   * он неотличим от «комиссии нет», и прибыль тогда тоже молчит.
   */
  fee_known: boolean;
  /** Прибыль при погашении в нашу сторону: size − quote − fee. */
  pnl?: string;
  /** Сколько уровней стакана задето. Отвечает на «насколько глубоко пришлось лезть». */
  levels?: number;
  /** Бюджет не выбран: стакан кончился раньше денег. */
  partial?: boolean;
  /** Почему плана нет. Пусто у годного. */
  reason?: string;
}

/**
 * Карточка замка: состояние окна и что с ним можно сделать.
 *
 * Одна на окно. Состояние safe → settled меняется В ТОЙ ЖЕ записи с тем же id —
 * это одно и то же окно, про которое мы узнали больше, — и потребитель замещает
 * запись по id, как у follow_signal.
 */
export interface LockSignal {
  id: string;
  detected_at: string;
  /** Когда пересчитаны планы. Отдельно от detected_at: карточка живёт минутами. */
  updated_at: string;

  condition_id: string;
  slug?: string;
  question?: string;
  asset?: string;
  window_end: string;

  state: LockState;
  /** Состояние словами и чем мерили порог — считает сервер вместе с решением. */
  label: string;
  basis?: string;
  mode: LockMode;

  /**
   * Было ли окно «почти решённым» уже за 45 секунд до конца.
   *
   * Значений ТРИ, и `undefined` — не «нет»: на отметке трейдер это окно мог не
   * наблюдать вовсе. Замер 2026-08-25: у окон, ясных заранее, ведущая сторона
   * проигрывала в 0.09 % случаев, у прояснившихся под конец — в 1.34 % при
   * безубытке 1.00 %. Две карточки одного состояния — два разных товара.
   */
  early_clear?: boolean;

  /** Сторона, на которую предлагается ставить. */
  outcome_name: string;
  asset_id: string;

  strike?: string;
  projected?: string;
  cushion?: string;
  required?: string;
  /** Остаток по модальной задержке (величина) и по минимальной (запертость). */
  unknown_ms: number;
  unknown_settled_ms: number;
  /** Сколько ход обязан продержаться, чтобы попасть в итог. */
  dead_prefix_ms?: number;
  lost?: number;

  budget_usd: string;
  max_price: string;

  /**
   * Предложения. Отсутствие — это «роли в этом состоянии нет вовсе» (у settled
   * мейкера нет по смыслу), и от плана с причиной отказа оно отличается: в
   * первом случае кнопки нет, во втором она есть и объясняет, почему не годится.
   */
  maker?: LockPlan;
  taker?: LockPlan;
  /** Почему планов нет вовсе: стакан не подтверждён либо не приехал. */
  book_problem?: string;

  expires_at: string;
  outcome: LockOutcome;
  taken_kind?: LockKind;
  client_order_id?: string;
  /** Почему ордер не ушёл. Карточка при этом вернулась в ожидание. */
  error?: string;
}

export interface LockSignalEvent {
  signal: LockSignal;
}

/** Настройки замка, как их выставил оператор. */
export interface LockSettings {
  mode?: LockMode;
  /** false — ключа нет, и решает умолчание окружения трейдера. */
  mode_set: boolean;
  order_usd?: string;
  size_set: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface LockSignalsResponse {
  signals: LockSignal[];
  /**
   * false — подсистема молчит: выключена либо трейдер не поднят. Пустой список
   * при этом НЕ означает «замков нет», и различать это обязан экран.
   */
  known: boolean;
  reason?: string;
}


/**
 * Ступень программы возврата тейкерской комиссии.
 *
 * Зеркало webapi.rebatesTier. Имена ступеней приходят по-английски намеренно:
 * оператор сверяет их с бейджем на своём профиле Polymarket, и перевод сделал
 * бы сверку на шаг длиннее.
 */
export interface RebatesTier {
  level: number;
  name: string;
  /** Доля УПЛАЧЕННОЙ комиссии, возвращаемая на этой ступени, в базисных пунктах. */
  rebate_bps: number;
  threshold_usd?: string;
  /**
   * Запас взвешенного объёма над порогом ступени. Без `?`: ноль означает
   * «стоим ровно на пороге» — самое тревожное значение, не прочерк.
   */
  headroom_usd: string;
}

/** Зеркало webapi.rebatesNext: чего стоит следующая ступень. */
export interface RebatesNext {
  level: number;
  name: string;
  rebate_bps: number;
  threshold_usd?: string;
  gap_usd?: string;
  gap_fee_usd?: string;
  gap_notional_usd?: string;
  /**
   * Сколько комиссии в сутки надо платить, чтобы ступень ДЕРЖАЛАСЬ.
   * Главное число блока: окно скользящее, и набранный разом разрыв вытечет.
   */
  daily_fee_usd?: string;
  bonus_usd?: string;
  gain_at_pace_usd?: string;
  gain_at_required_usd?: string;
  extra_fee_usd?: string;
  reachable: boolean;
}

/** Зеркало webapi.rebatesHold: чего стоит удержать нынешнюю ступень. */
export interface RebatesHold {
  daily_fee_usd?: string;
  safe: boolean;
  /** Когда ступень уйдёт, если перестать торговать. Пусто — не провалится. */
  lost_at?: string;
}

/** Зеркало webapi.rebatesPaid: фактически пришедшие выплаты. */
export interface RebatesPaid {
  known: boolean;
  reason?: string;
  taker_rebate_usd?: string;
  maker_rebate_usd?: string;
  reward_usd?: string;
  total_usd?: string;
  payouts: number;
  /**
   * Сколько возврата обещает модель за то же окно: сумма по суткам «ставка
   * ступени ЭТИХ суток × комиссия этих суток». Сверка идёт с ним, а не с
   * частным «пришло / уплачено»: внутри окна ступень меняется, и частное даёт
   * величину, которой нет ни в одной строке программы.
   *
   * Разовые бонусы сюда не входят, поэтому это НИЖНЯЯ граница.
   */
  expected_usd?: string;
  expected_days: number;
  expected_known: boolean;
  implied_bps?: number;
  implied_known: boolean;
  nearest_tier?: string;
}

/** Зеркало webapi.rebatesDay: одни сутки ряда. */
export interface RebatesDay {
  day: string;
  trades: number;
  /**
   * Без `?`: у обойденных суток ноль — это ОТВЕТ («не торговали»), и
   * пропущенное поле превратило бы его в прочерк («не знаем»).
   */
  fee_usd: string;
  weighted_usd: string;
  /** Ложь — вес категории неизвестен: на экране прочерк, не ноль. */
  known: boolean;
  /** Ложь — сутки ещё не обходили. Это НЕ то же, что «сделок не было». */
  walked: boolean;
  partial?: boolean;
}

/** Зеркало webapi.rebatesGaps: чего расчёт не знает. */
export interface RebatesGaps {
  missing_days: number;
  unknown_days: number;
  partial_days: number;
  bonuses_are_guesses: boolean;
}

/** Зеркало webapi.rebatesResponse. */
export interface Rebates {
  wallet: string;
  window_days: number;
  as_of: string;
  known: boolean;
  reason?: string;
  /** Ложь — объём ЗАНИЖЕН: в окне есть дыры, обрезанные или непосчитанные сутки. */
  complete: boolean;
  /** Когда объём окна последний раз сверялся с биржей; нет — окно не обходили. */
  walked_at?: string;
  weighted_usd?: string;
  fee_usd?: string;
  tier: RebatesTier;
  next?: RebatesNext;
  hold: RebatesHold;
  pace_usd?: string;
  pace_days: number;
  /** Ноль — ответ «завтра из окна не выпадет ничего», а не отсутствие числа. */
  drop_off_usd: string;
  wv_per_dollar?: string;
  avg_price?: string;
  paid: RebatesPaid;
  days: RebatesDay[];
  gaps: RebatesGaps;
}

export interface RebatesResponse {
  rebates: Rebates;
}

/**
 * Свободный залог (pUSD) аккаунта — то, что биржа зовёт «Cash».
 *
 * При `known: false` число и дата — последнего удачного снятия, если оно было:
 * старое число с честной датой полезнее прочерка.
 */
export interface AccountBalance {
  wallet: string;
  known: boolean;
  reason?: string;
  usd: string;
  as_of?: string;
}

export interface AccountBalanceResponse {
  balance: AccountBalance;
}

/** Один сценарий подсистемы копирования, как его видит трейдер. */
/**
 * Живой режим одной стратегии, как его называет ТРЕЙДЕР.
 *
 * Приезжает нотами, а не считается в браузере: действующий режим — строгий
 * минимум потолка окружения и ключей оператора, а потолок живёт в окружении
 * трейдера. Посчитай интерфейс минимум сам — на экране появилось бы второе
 * определение действующего режима, и разошлось бы оно с тем, по которому уходят
 * деньги.
 */
export interface StrategyStatus {
  id: string;
  /** Действующий режим вместе с причиной: «shadow / taker (потолок окружения)». */
  mode: string;
  /**
   * ПОТОЛОК окружения: «shadow / none». Что вообще возможно без выката.
   * Кнопка, которую потолок не пропустит, обязана быть видна выключенной.
   */
  mode_env?: string;
  /** Потолок окружения: сколько стоит теневой замер одной строкой. */
  order_usd?: string;
  /**
   * ДЕЙСТВУЮЩАЯ сумма ставки, после понижения кнопкой.
   *
   * Отправляется обратно с командой нажатия, и трейдер сверяет её на равенство:
   * правка в соседней вкладке не имеет права превратить нажатие на «$25» в
   * ордер на другую сумму. Считать этот минимум в браузере нельзя — тогда обе
   * стороны сверяли бы одно и то же по-разному.
   */
  order_live?: string;
  /**
   * ДЕЙСТВУЮЩАЯ клетка журнала строкой «источник@цена»; пусто там, где клетки
   * нет (у семей её нет вовсе).
   *
   * Нужна полосе алертов: по одному окну почти решённое пишет до десяти строк, а
   * ордер разрешён по одной клетке. Без неё полоса подняла бы десять предложений,
   * девять из которых трейдер отверг бы правилом `cell`.
   */
  live_cell?: string;
  claimed?: string;
  /** Сколько ордеров отправлено за жизнь процесса трейдера. */
  placed?: string;
  /** Сколько раз окно оказалось уже занятым: штатный исход, а не ошибка. */
  taken?: string;
}

/** Настройки живого режима стратегии — то, что выставил ОПЕРАТОР. */
export interface StrategySettings {
  strategy: string;
  mode?: StrategyMode;
  mode_set: boolean;
  /** Общий выключатель всех стратегий разом. Тоже участвует в минимуме. */
  mode_all?: StrategyMode;
  mode_all_set: boolean;
  role?: StrategyRole;
  role_set: boolean;
  order_usd?: string;
  order_usd_set: boolean;
  daily_cap_usd?: string;
  daily_cap_usd_set: boolean;
  price?: string;
  price_set: boolean;
  source?: string;
  source_set: boolean;
  updated_at?: string;
  updated_by?: string;
}

/**
 * Что именно правит одно нажатие. Каждое поле НЕОБЯЗАТЕЛЬНО, и присылаются
 * только тронутые: отправь мы объект целиком — правка суммы стирала бы режим,
 * выставленный в соседней вкладке.
 */
export interface StrategySettingsPatch {
  mode?: StrategyMode;
  role?: StrategyRole;
  /** Суммы СТРОКАМИ: считаем целочисленно, Number(price) запрещён. */
  order_usd?: string;
  daily_cap_usd?: string;
  price?: string;
  source?: string;
}

/**
 * Режим стратегии. Те же четыре значения и тот же порядок, что у копирования:
 * лесенка в Go одна на всех (domain.StrategyMode), и второе перечисление здесь
 * разошлось бы с ней молча.
 */
export type StrategyMode = FollowMode;

/**
 * Какие роли входа РАЗРЕШЕНЫ стратегии.
 *
 * Не то же, что роль одного ордера: `both` — это разрешение, а не третий способ
 * войти. Какой ролью идти, решает подсистема по плану (или оператор нажатием).
 */
export type StrategyRole = "none" | "maker" | "taker" | "both";

export interface ScenarioStatus {
  id: string;
  /**
   * ПОТОЛОК режима сценария: минимум FOLLOW_MODE и mode сценария. Кнопка умеет
   * только понизить, и без потолка оператор жал бы «авто» и не понимал, почему
   * ордера не уходят.
   */
  mode_env?: string;
  daily_cap?: string;
  signals?: string;
  placed?: string;
}

/**
 * Уровень операционной тревоги. Три, а не пять: приёмников два, и различать
 * большим числом нечем.
 *
 *   info — новость, а не поломка: биржа сообщила ставку комиссии, достройка
 *          индекса выключила себя штатной защитой, оператор взвёл стоп;
 *   warn — подсистема деградировала, деньги прямо сейчас не в опасности;
 *   crit — терминал не видит рынка, своих ордеров или самого трейдера.
 */
export type OpsSeverity = "info" | "warn" | "crit";

/**
 * Одно событие подсистемы операционных тревог (веха M8).
 *
 * Тревога и её СНЯТИЕ несут ОДИН id: карточка замещается по нему, а не
 * дублируется. Ровно та же идиома, что у карточки замка и сигнала кита, и по
 * той же причине — порядка доставки у Pub/Sub нет.
 */
export interface OpsAlert {
  id: string;
  /** Идентификатор правила: он же ключ для OPS_MUTE и имя в логе. */
  rule_id: string;
  /** Экземпляр шаблонного правила: актив у index_ahead_*. */
  instance?: string;
  severity: OpsSeverity;
  /** «тревога» | «снято» | «заглушено» | «потеряна нота». */
  state: string;
  at: string;
  since: string;
  /** Сколько держалась тревога, у снятия. */
  duration_s?: number;
  /** СЫРОЕ значение ноты: то, что не врёт даже там, где разбор чего-то не понял. */
  value?: string;
  text: string;
  /** Чей /healthz: trader либо web_api. */
  source?: string;
}

/** Конверт карточки тревоги в сокете (bus.TypeOpsAlert). */
export interface OpsAlertEvent {
  alert: OpsAlert;
  /** Сколько секунд карточке жить, если тревогу так и не сняли. */
  ttl_s: number;
}

/**
 * Ответ GET /ops/alerts.
 *
 * known различает ДВА вида пустоты, и это существенно: выключенная подсистема и
 * исправная система дают одинаково пустой список, а означают противоположное.
 * Первое чинят, второе — то, чего и ждут.
 */
export interface OpsAlertsResponse {
  known: boolean;
  firing: OpsAlert[];
  recent: OpsAlert[];
}

/**
 * Плотность стакана биржи расчёта.
 *
 * Раздел `/cex/depth`. Отвечает на «во что обошлось бы сдвинуть Binance» и
 * показывает, вспухла ли одна из сторон.
 *
 * Чего он НЕ отвечает: связи с крупными ордерами Polymarket. Она измерена, и её
 * нет — ранговая связь перекоса сторон с направлением тейкерского потока PM
 * `+0.007` на BTC и `−0.050` на ETH при `n ≈ 2000` на актив [замер 2026-08-26].
 * Панель поэтому показывает ПРИБОР, а не сигнал, и подписывать её иначе нельзя.
 */
export interface CexMark {
  /** Отметка в базисных пунктах: 0.5, 1, 2, 5, 10. */
  bp: number;
  /** Накопленный объём в пределах отметки, доллары. */
  bid_usd?: string;
  ask_usd?: string;
  /**
   * Отметку СПРАШИВАЛИ.
   *
   * Без флага пустая дальняя отметка читалась бы как «денег там нет» вместо
   * «лестница не дотянулась»: мелкий опрос покрывает 3 бп на BTC, а отметки 5 и
   * 10 бп живут на 0.2 Гц. Это противоположные новости.
   */
  have_bid: boolean;
  have_ask: boolean;
}

/** Стена: одна сторона вспухла относительно СОБСТВЕННОГО фона. */
export interface CexWall {
  side: "bid" | "ask";
  /** Во сколько раз плотность выше фона той же стороны. */
  ratio: number;
  usd: string;
  base_usd: string;
  base_secs: number;
}

export interface CexSymbol {
  symbol: string;
  venue?: string;

  at?: string;
  age_s: number;
  /** Последняя секунда старше пятнадцати секунд. */
  stale: boolean;

  bid?: string;
  ask?: string;
  mid?: string;

  marks: CexMark[];

  /**
   * Перекос сторон на 1 бп, `[−1, +1]`. Плюс — денег больше на бидах.
   *
   * `undefined` означает «судить нечем», и это НЕ ноль: ноль означал бы, что
   * стороны равны.
   */
  imbalance?: number;
  /** На какой отметке считан перекос. */
  imbalance_bp: number;

  /** Стена, если найдена. */
  wall?: CexWall;
  /**
   * Стену БЫЛО чем судить. Отдельно от самой стены: короткий фон и ровный
   * стакан — разные ответы, и молчание по обеим причинам выглядело бы одинаково.
   */
  wall_known: boolean;

  /** Сколько секунд ряда нашлось за отрезок и какой длины отрезок. */
  secs: number;
  span_s: number;
  /** Размах и снос середины за отрезок, базисные пункты. */
  range_bp: number;
  drift_bp: number;

  /** Перекос по времени для спарклайна; null — пробел ряда. */
  series: (number | null)[];

  rev?: string;
}

export interface CexDepthResponse {
  symbols: CexSymbol[];
  /** Сборщик пишет ряд. false — раздел показать нечего, причина в reason. */
  known: boolean;
  reason?: string;
}

/**
 * Сводка теневого сигнала «покупка почти решённого» по ОДНОМУ источнику.
 *
 * # Почему источников два и складывать их нельзя
 *
 * Замок исхода считается по ряду цен, и рядов два: `pm` — тот, что публикует
 * Polymarket (база отсчёта, никакой модели), `model` — тот же расчёт по ряду,
 * достроенному спотом биржи, на 1–3 с раньше. Вопрос ровно один: стоит ли
 * модель тех секунд, которые она даёт. Общий итог по обоим отвечал бы на
 * вопрос, которого никто не задавал.
 */
export interface DecidedSource {
  /** `pm` либо `model`. */
  source: string;
  /**
   * Ступень лесенки цен — часть ключа наравне с источником.
   *
   * Сводить строки с разными ценами нельзя: на отбор окон цена не влияет вовсе
   * (замок считает исход, а не цену), зато целиком определяет ИСПОЛНИМОСТЬ, и
   * среднее между ступенями не описывает ни одну.
   *
   * По каждому отобранному окну пишется по строке НА КАЖДУЮ ступень — с одного
   * снимка стакана, в одну миллисекунду, с одной форой, — поэтому между
   * строками лесенки остаётся ровно одна разница, и это цена. Ступени вложены:
   * что берётся по 0.95, берётся и по 0.99, так что читают лесенку разностями
   * соседей.
   *
   * ПРОВЕРЯТЬ СОПОСТАВИМОСТЬ ОБЯЗАТЕЛЬНО (`ladderWord`): у настоящей лесенки
   * `signals` совпадает у всех ступеней одного источника. Расхождение означает,
   * что ступени набраны на разных окнах — так выглядели строки до лесенки, когда
   * цена была одним числом в настройке и её смена резала журнал на два отрезка
   * времени. Такие строки живут в сводке весь срок хранения и сравнению между
   * ценами не подлежат.
   */
  limit_price: string;
  /** Сколько окон отобрано, по скольким известен исход, в скольких угадали. */
  signals: number;
  settled: number;
  won: number;
  /**
   * В скольких окнах план вообще был ВОЗМОЖЕН — главная пара чисел сводки.
   *
   * Отбор окон от цены входа не зависит вовсе (замок считает исход, а не цену),
   * а исполнимость зависит целиком. Без этих двух «сигналов много» читалось бы
   * как «сделок много».
   */
  maker_feasible: number;
  taker_feasible: number;
  /** Сколько share нашлось в стакане дешевле цены входа. */
  taker_shares: string;
  /** По скольким строкам лента досмотрена до конца. Знаменатель двух следующих. */
  observed: number;
  /**
   * Сколько прошло по ленте дешевле нашей цены и сколько из этого досталось бы
   * нам после очереди впереди. Разница — цена места в очереди.
   */
  tape_shares: string;
  fill_shares: string;
  /** Медианная очередь впереди заявки, share. */
  queue_median: string;
  /**
   * Теневой счёт по сведённым строкам и ПО СКОЛЬКИМ он посчитан.
   *
   * Знаменатель обязателен. Ноль в счёте бывает двух родов — «заявка стояла,
   * никто не налил» (результат замера) и «считать было нечем» (заявку нельзя
   * было поставить либо ленту не наблюдали), — и сумма их складывает. Без
   * знаменателя пустое измерение выглядит как измеренный ноль.
   */
  maker_pnl: string;
  taker_pnl: string;
  maker_priced: number;
  taker_priced: number;
  /** Медианная фора: за сколько до конца окна сработало, миллисекунды. */
  lead_median_ms: number;

  /**
   * Тейкерская половина — та, по которой принимают решение.
   *
   * `won`/`settled` выше меряют ОТБОР: все отобранные окна, включая те, где
   * купить было нечего. Мейкеру другого ответа и не дать (налили 16 заявок из
   * 1366 — считать не по чему), а у тейкера вход состоялся, и вопрос к нему
   * другой: что вышло у того, кто вошёл. Знаменатели расходятся на порядок, и
   * общая доля прячет ту, которая нужна.
   */
  taker_settled: number;
  taker_won: number;
  /**
   * Оборот и комиссия по тем же входам.
   *
   * Оборот обязателен рядом с суммой: +$110 сами по себе не отличают удачу на
   * $10 тыс. от кромки на $1.3 тыс. Комиссия отдельно, а не вычтенная молча —
   * ставка живёт на марже в несколько центов, и доля комиссии в ней решает,
   * остаётся ли ставка ставкой.
   */
  taker_quote: string;
  taker_fee: string;
  /**
   * Сколько входов упёрлись в стакан, не набрав бюджета.
   *
   * Ноль означает «потолок объёма НЕ ИЗМЕРЕН», а не «объёма нет»: на нашем
   * бюджете стакан ни разу не кончился, значит потолок лежит выше и мы его не
   * видели. Прочитать это как измеренный потолок — ровно та ошибка, из-за
   * которой ставку масштабируют в пустоту.
   */
  taker_partial: number;
  /**
   * Сумма цены входа и z-оценка доли угаданного против неё.
   *
   * Доля сама по себе не значит ничего: 112 угаданных из 118 — это кромка при
   * цене 0.84 и её отсутствие при 0.95. Z считает сервер; поля нет, когда
   * считать не из чего, и это НЕ ноль.
   */
  taker_price_sum: string;
  taker_price_z?: number;

  /**
   * Ворота согласия со стаканом — ОБЕ стороны разреза.
   *
   * Правило придерживает вход, у которого цена прохода ниже порога: подсистема
   * покупает уже случившийся исход, и если он случился, стакан видит это ценой.
   * Цена в цент означает, что рынок уверен в обратном, — тогда спорят замок и
   * стакан, а ставка становится лотереей на нашу модель.
   *
   * Придержанные строки считаются ПОЛНОСТЬЮ и приезжают сюда же. Это не
   * избыточность: их счёт — единственный ответ на «что ворота сэкономили», и
   * без него правило нельзя ни подтвердить, ни снять. Отрицательный
   * `suppressed_pnl` означает, что ворота работают; положительный — что они
   * стоят денег.
   *
   * Обе половины считаются В СВЕЖЕЙ половине разреза по возрасту стакана (см.
   * ниже): разрезы независимы, и ворота на всей выборке вернули бы под таблицу
   * счёт по ценам, которых на бирже не было.
   */
  suppressed: number;
  suppressed_won: number;
  suppressed_pnl: string;
  /** Чистая половина — то, чем подсистема живёт после включения ворот. */
  clean_settled: number;
  clean_won: number;
  clean_pnl: string;
  clean_quote: string;

  /**
   * Проба глубины: сколько стакан отдал бы бюджетом, заведомо бо́льшим торгового.
   *
   * Заведена потому, что `taker_partial` отвечает на вопрос о потолке только
   * когда он не ноль, а он ноль всегда: бюджет упирался в себя, а не в стакан.
   * Частное `probe_quote / probe_budget` и есть ответ «во сколько раз можно
   * поднять ставку»: около единицы — потолок выше пробы и снова не измерен.
   *
   * `probe_partial` — в скольких пробах стакан КОНЧИЛСЯ раньше денег, то есть
   * единственный измеренный потолок, который у подсистемы бывает.
   */
  probed: number;
  probe_budget: string;
  probe_quote: string;
  probe_partial: number;

  /**
   * Возраст снимка стакана — ОБЕ половины разреза.
   *
   * # Зачем половин две, а не одна честная
   *
   * Теневой счёт подсистемы растёт с возрастом НАШЕГО снимка книги, и растёт не
   * немного: на снимках моложе порога доходность около нуля, на снимках в
   * секунду-три — восемьдесят процентов. Второе невозможно физически (покупка
   * по ≤ 0.999 с выплатой $1 не даёт и процента) и означает ровно одно: цена
   * взята из книги, которой на бирже уже нет. 97 % всего теневого счёта лежало
   * на протухших снимках.
   *
   * Поэтому результатом считается ТОЛЬКО свежая половина, а протухшая едет
   * рядом мерой артефакта. Спрятать её было бы второй неправдой: без неё
   * вопрос «куда делись прежние плюсы» стал бы неотвечаемым, а разрез —
   * неопровержимым. Тот же приём и тот же довод, что у ворот выше.
   *
   * `fresh_price_z` отсутствует, когда считать не из чего, и это НЕ ноль.
   */
  fresh_settled: number;
  fresh_won: number;
  fresh_quote: string;
  fresh_pnl: string;
  fresh_priced: number;
  fresh_price_sum: string;
  fresh_price_z?: number;
  /** Протухшая половина — мера артефакта, а не результат. */
  stale_settled: number;
  stale_won: number;
  stale_pnl: string;
  stale_price_sum: string;
}

/** План входа в теневой строке. Причина отказа — половина ответа, а не пустота. */
export interface DecidedPlan {
  kind: string;
  price?: string;
  size?: string;
  quote?: string;
  fee?: string;
  fee_known: boolean;
  pnl?: string;
  levels?: number;
  /** Сколько share стоит в очереди ВПЕРЕДИ нашей заявки. Только у мейкера. */
  queue_ahead?: string;
  partial?: boolean;
  reason?: string;
}

/** Строка теневого журнала: один вход одним источником по одному окну. */
export interface DecidedSignal {
  id: string;
  source: string;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset?: string;
  window_end: string;
  window_s: number;
  /** За сколько до конца окна сработало. Знаковое: рынок открыт и после конца. */
  lead_ms: number;
  state: string;
  label?: string;
  mode: string;
  early_clear?: boolean;
  outcome_name: string;
  asset_id: string;
  strike?: string;
  projected?: string;
  cushion?: string;
  required?: string;
  lost?: number;
  /** Сколько секунд хвоста были оценкой модели. У `pm` ноль по построению. */
  estimated?: number;
  limit_price: string;
  budget_usd: string;
  maker: DecidedPlan;
  taker: DecidedPlan;
  /**
   * Проба глубины: тот же тейкерский проход бюджетом `probe_budget_usd`.
   *
   * Поля нет, когда пробы не заказывали. `probe.partial` означает измеренный
   * потолок: дешевле лимита в стакане стояло ровно `probe.size`.
   */
  probe?: DecidedPlan;
  probe_budget_usd?: string;
  /** Вершина стакана нашей стороны. Прочерк — стороны книги не было, и это не 0. */
  book_bid?: string;
  book_ask?: string;
  book_problem?: string;
  book_age_ms?: number;
  /**
   * Почему ворота придержали план. Пустое поле — сигнал чистый.
   *
   * Строка пишется независимо от правила: гаси оно запись, и «ворота не
   * пропустили ни одного проигрыша» перестало бы отличаться от «ворота не
   * пропустили ничего».
   */
  rule?: string;
  /** Наблюдение ленты. Прочерк — «не наблюдали», и это не ноль. */
  maker_tape_shares?: string;
  maker_fill?: string;
  resolved: boolean;
  won: boolean;
  maker_pnl?: string;
  taker_pnl?: string;
  settled_at?: string;
}

/**
 * Сводка теневого сигнала «расхождение модели и стакана» по ОДНОЙ паре
 * «источник × роль».
 *
 * # Почему четыре строки, а не итог
 *
 * Ключ группировки — пара, и усреднять по любой её половине нельзя. Источники
 * (`pm` против `model`) отвечают на вопрос «стоит ли достройка тех секунд,
 * которые она даёт»; роли — на «чем вообще берут»: тейкер платит спред и
 * комиссию, но исполняется наверняка, мейкер не платит ничего и исполняется
 * редко и неблагоприятно. Общий итог ответил бы на вопрос, которого никто не
 * задавал, и спрятал бы оба, ради которых подсистема заведена.
 *
 * ЕДИНИЦЫ: денежные поля — СТРОКИ (правило 3 контракта).
 */
export interface MispricedGroup {
  source: string;
  role: string;
  /** Сколько заявок выставлено, по скольким известен исход, сколько угадано. */
  signals: number;
  settled: number;
  won: number;
  /**
   * По скольким заявкам исполнение случилось и сколько из них выиграло.
   *
   * ГЛАВНАЯ пара. Доля выигранных среди ВСЕХ выставленных — качество отбора;
   * доля среди ИСПОЛНИВШИХСЯ — то же качество после того, как рынок выбрал, какие
   * из наших заявок исполнить. Разница между ними и есть неблагоприятный отбор:
   * мейкера наливают охотнее там, где он неправ.
   */
  filled: number;
  won_filled: number;
  /**
   * Теневой счёт и ПО СКОЛЬКИМ строкам он посчитан.
   *
   * Знаменатель обязателен: ноль в счёте бывает измеренный («заявка стояла,
   * никто не налил») и пустой («считать было нечем»).
   */
  pnl: string;
  priced: number;
  /**
   * Медианная кромка по цене ИСПОЛНЕНИЯ и она же против СЕРЕДИНЫ стакана.
   *
   * Разница двух медиан — цена спреда с комиссией, то есть число, которое либо
   * оправдывает затею, либо обнуляет её целиком.
   */
  edge_median: string;
  edge_mid_median: string;
  /** Сколько share нашлось: у тейкера в стакане, у мейкера по ленте. */
  shares: string;
  /** Сколько заявок снято и сколько они жили по медиане. */
  cancelled: number;
  live_median_ms: number;
  /** Медианная фора до конца окна, миллисекунды. */
  lead_median_ms: number;
}

/**
 * Одна корзина разреза счёта пары «источник × роль».
 *
 * Отвечает на вопрос, который сводка закрыть не может: ИЗ ЧЕГО сложился её
 * счёт. Корзины каждой оси в сумме дают сводку своей пары — разрез
 * перекладывает счёт, а не пересчитывает его.
 */
export interface MispricedSlice {
  source: string;
  role: string;
  /** Ось разреза: minute — минута захода от конца окна, price — цена плана. */
  cut: "minute" | "price";
  /**
   * Номер корзины. У минуты 1 — последняя минута окна, 2 — предпоследняя, ноль
   * и меньше — заход после конца (рынок остаётся открытым, и это законно). У
   * цены — десятая доля: 6 значит план ценой [0.6, 0.7).
   */
  bucket: number;
  /** Заявок, по скольким случилось исполнение, известен исход, угадано. */
  signals: number;
  filled: number;
  settled: number;
  won: number;
  /** Счёт корзины и его знаменатель — те же правила, что у сводки. */
  pnl: string;
  priced: number;
}

/**
 * Клетка сетки порога кромки: ступень × свежесть снимка × источник.
 *
 * # Почему свежесть в КЛЮЧЕ, а не рядом
 *
 * Ступень без неё врёт. Замер на боевой базе 2026-09-01 (11 400 сведённых
 * тейкерских строк): сетка порога без разреза по возрасту снимка растёт
 * монотонно — ROI +6 % на ступени 0.02 и +62 % на 0.20, — и читается как «порог
 * мал». Весь этот рост лежит в снимках старше 200 мс, а на свежих каждая
 * ступень отрицательна и портится с ростом порога: порог отбирает не кромку, а
 * нашу задержку. Доля протухших снимков среди срабатываний растёт с 5.6 % на
 * ступени 0.02 до 49.1 % на 0.20.
 *
 * Поэтому ступень в ответе всегда разложена по корзинам, и склеить их обратно
 * контракт не даёт намеренно.
 *
 * # Почему клетки НЕ складываются
 *
 * Ступени вложены: заявка с кромкой 0.12 попала в каждую ступень до 0.10. Сумма
 * по столбцу «ступень» посчитала бы её восемь раз. Это отличие от MispricedSlice,
 * где корзины непересекающиеся и в сумме дают сводку.
 */
export interface MispricedGridCell {
  source: string;
  /** Ступень порога: в клетке заявки с кромкой НЕ НИЖЕ неё. */
  step: string;
  /**
   * Корзина возраста снимка стакана:
   *
   *   - `live` — моложе 50 мс. Живой вход берёт только такие
   *     (MISPRICED_LIVE_MAX_BOOK_AGE), и судить о деньгах можно по ним одним;
   *   - `warm` — 50–200 мс: живой вход отказал бы, счёт уже смещён;
   *   - `stale` — старше 200 мс: счёт принадлежит нашей задержке, а не рынку;
   *   - `unknown` — возраста нет. Отдельно от свежих: неизвестности нельзя
   *     приписывать лучшую корзину.
   */
  freshness: "live" | "warm" | "stale" | "unknown";
  /** Заявок прошло бы порог, по скольким известен исход, в скольких угадано. */
  signals: number;
  settled: number;
  won: number;
  /** Счёт клетки и его знаменатель — те же правила, что у сводки. */
  pnl: string;
  priced: number;
  /**
   * Во сколько обошёлся бы вход по плану.
   *
   * Знаменатель ROI, и без него ступени несравнимы: число входов у них
   * отличается на порядок, а большой порог всегда «меньше потерял».
   */
  spent: string;
  /** Медианная кромка по цене исполнения внутри клетки. */
  edge_median: string;
  /**
   * Запас в сигмах: доля угаданного против цены входа, считает сервер. null —
   * «считать не из чего», и это НЕ ноль.
   *
   * Без него ступень выбиралась бы по максимуму ROI, а максимум по лестнице
   * достаётся самой редкой ступени просто за редкость: на суточном журнале
   * верхние ступени набирают по девять заявок, и +61 % на них неотличимы от
   * нуля. Планка «зелёное только от двух сигм» — общая на весь экран.
   */
  z: number | null;
}

/**
 * Сетка целиком: клетки и границы журнала.
 *
 * margin_min и margin_max — какой порог РЕАЛЬНО стоял за срок. Без них верхние
 * ступени лгут молча: журнал усечён порогом, и ступень ниже margin_max описывает
 * не «что дал бы этот порог», а «что дал бы порог margin_max».
 */
export interface MispricedGridResponse {
  hours: number;
  cells: MispricedGridCell[];
  margin_min: string;
  margin_max: string;
}

/** План входа в строке расхождения. Строки без плана не бывает. */
export interface MispricedPlan {
  kind: string;
  price?: string;
  size?: string;
  quote?: string;
  fee?: string;
  fee_known: boolean;
  pnl?: string;
  levels?: number;
  queue_ahead?: string;
  partial?: boolean;
  reason?: string;
}

/** Строка теневого журнала расхождения: одна заявка. */
export interface MispricedSignal {
  id: string;
  source: string;
  role: string;
  /** Которая по счёту заявка по этому окну, источнику, стороне и роли. */
  attempt: number;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset?: string;
  window_end: string;
  window_s: number;
  lead_ms: number;
  mode: string;
  state: string;
  outcome_name: string;
  asset_id: string;
  /** Справедливая цена этой стороны по ЗАМКУ: `1 − prob_flip` у ведущей. */
  fair: string;
  prob_flip: number;
  sigma_left?: string;
  strike?: string;
  projected?: string;
  cushion?: string;
  estimated?: number;
  /** Вершина стакана этой стороны. Пусто — стороны в стакане нет. */
  book_bid?: string;
  book_ask?: string;
  book_age_ms?: number;
  /**
   * Расхождение против СЕРЕДИНЫ стакана (то, что видит оператор на панели) и
   * против цены ИСПОЛНЕНИЯ (то, что делает деньги).
   */
  edge_mid?: string;
  edge_exec: string;
  /** Пороги, действовавшие в момент строки: калибровка их подвинет. */
  enter_edge?: string;
  exit_edge?: string;
  margin?: string;
  budget_usd: string;
  plan: MispricedPlan;
  /** Когда и почему заявку сняли. Пусто — стояла до конца наблюдения. */
  cancelled_at?: string;
  cancel_reason?: string;
  live_ms?: number;
  /** Объём ленты по нашей цене и наша доля после очереди впереди. */
  tape_shares?: string;
  fill?: string;
  resolved: boolean;
  won: boolean;
  shadow_pnl?: string;
  settled_at?: string;
}

/**
 * Сводка теневой ставки по перевесу группы «плюсовых» за одно ведро минуты
 * окна.
 *
 * Ключ группировки — ИМЕННО минута, и усреднять по ней нельзя: базовая линия
 * «цена против страйка» сама растёт с минутой — 64–65 % угаданного на первой,
 * 69–70 % на второй, 80 % на третьей, 91–92 % на четвёртой. Общий итог
 * сравнивался бы с планкой, которой нет ни у одного ведра.
 */
export interface CohortBucket {
  /**
   * Ступень лесенки в базисных пунктах ОБОРОТА ОКНА (400 = 4 %).
   *
   * Первый ключ группировки. Замер 2026-09-01 показал, что перевес после входа
   * растёт втрое, и вопрос «остаётся ли кромка выше по лестнице» на строке,
   * усреднённой по ступеням, не задаётся вовсе.
   */
  threshold_bp: number;
  bucket: string;
  /**
   * Зачётные строки и погашенные правилом — ПОРОЗНЬ.
   *
   * Вопросы у них разные: первые отвечают «есть ли кромка», вторые — «сколько мы
   * пропустили и почему». Усреднять по ним нельзя.
   */
  signals: number;
  suppressed: number;
  settled: number;
  won: number;

  /**
   * Сумма цены тейкерского входа и z-оценка доли угаданного против неё.
   *
   * Кромка объявляется не знаком разности, а её оценкой: замер на этом же
   * проекте показал, что фильтры, подобранные по одной и той же выборке,
   * поднимают PnL с −$147 до +$30 при |z| не выше 1.1 на каждом шаге.
   *
   * `price_z` отсутствует, когда считать не из чего. Это НЕ ноль: ноль читался
   * бы как «измерили и получили ровно среднее».
   */
  price_sum: string;
  price_z?: number;
  priced: number;
  /**
   * То же против ФИЗИКИ. Обязательный второй знаменатель: сигнал, бьющий цену и
   * не бьющий физику, означает, что когорта покупает сторону, которая и так
   * выигрывала.
   */
  phys_sum: string;
  phys_z?: number;
  phys_known: number;

  /**
   * То же сравнение против цены НА МОМЕНТ ПОДТВЕРЖДЕНИЯ — то есть с учётом
   * платы за ожидание.
   *
   * Ради этой пары чисел веха и делалась. Вся информация журнала сидит в том,
   * что группа делает ПОСЛЕ входа (у строк с развернувшимся перевесом угадано
   * 1.1 %, у доросших — 99.6 %), но обе величины дописывает закрытие окна.
   * Здесь тот же вопрос задан вперёд, и ждать больше не бесплатно.
   *
   * `grew` — подвыборка «перевес к подтверждению вырос»: проверяемая гипотеза
   * целиком.
   */
  confirmed: number;
  won_confirmed: number;
  confirm_sum: string;
  confirm_z?: number;
  grew: number;
  won_grew: number;
  grew_sum: string;
  grew_z?: number;

  /**
   * Разрез по доле крупнейшего участника выше половины.
   *
   * РАЗРЕЗ, а не гейт: до 2026-09-01 такие строки гасились правилом, которое
   * съедало 294 строки из 1220 при кромке у погашенных +1.3 п.п. против
   * −2.0 п.п. у зачётных.
   */
  concentrated: number;
  won_concentrated: number;
  conc_sum: string;
  conc_z?: number;

  /**
   * Прямая мера неблагоприятного отбора мейкера: доля выигранных среди
   * исполнившихся против доли среди всех. `min_print_known` — знаменатель:
   * прочерк в лестнице означает «не наблюдали», а не «не исполнился бы».
   */
  filled: number;
  won_filled: number;
  min_print_known: number;

  /** Медианы, а не средние: одна строка на $40 тыс. даёт среднее, которого не бывает. */
  tilt_median: string;
  /**
   * Медианная доля самого крупного участника в перевесе — КАНАРЕЙКА всей
   * затеи: устойчиво больше половины означает, что меряется один кит, а не
   * группа.
   */
  top_share_median: string;
  wallets_median: number;
  /**
   * Медианный оборот окна — знаменатель ступени. Без него доля нечитаема в
   * деньгах: «4 %» на BTC 5m и на ETH 5m это $2000 и $170.
   */
  window_gross_median: string;
  reversals: number;

  /** Контрольные величины: обязаны сходиться с долями выше, а не заменять их. */
  maker_pnl: string;
  taker_pnl: string;
}

/** Одна теневая ставка по перевесу группы. */
export interface CohortSignal {
  id: string;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset: string;
  window_end: string;
  window_s: number;
  /** За сколько до конца окна сработало. Знаковое: рынок торгуется и после конца. */
  lead_ms: number;
  asset_id: string;
  outcome_name?: string;

  /** Перевес группы в момент срабатывания и его разложение. */
  tilt_usd: string;
  abs_tilt_usd: string;
  top_wallet_usd: string;
  group_gross_usd: string;
  wallets_long: number;
  wallets_short: number;
  wallets_seen: number;
  /** Пик и конец перевеса за наблюдение. Прочерк — наблюдение не доехало. */
  tilt_peak_usd?: string;
  tilt_end_usd?: string;

  /**
   * Ступень лесенки в базисных пунктах ОБОРОТА ОКНА и её знаменатель.
   *
   * Ноль в ступени означает строку абсолютной выборки до 2026-09-01; в сводку
   * такие не входят.
   */
  threshold_bp: number;
  window_gross_usd: string;

  /**
   * Точка подтверждения: что было через COHORT_CONFIRM_AFTER после входа.
   *
   * Прочерк во всей группе означает «точку не наблюдали» (роллевер, рестарт), и
   * это НЕ «перевес не удержался». `confirm_price` — плата за ожидание.
   */
  confirm_at?: string;
  confirm_lead_ms?: number;
  confirm_tilt_usd?: string;
  confirm_window_gross_usd?: string;
  confirm_price?: string;
  confirm_size?: string;
  /** Почему цены подтверждения нет. Пусто — цена есть. */
  confirm_reason?: string;

  /** Пороги СНИМКОМ: смена порога открывает новую выборку, а не дописывается в старую. */
  enter_usd: string;
  exit_usd: string;
  cohort_min_turnover_usd: string;
  cohort_window_days: number;
  roster_size: number;
  roster_at?: string;

  /**
   * Вероятность победы НАШЕЙ стороны по замку. Прочерк означает «замка не
   * наблюдали», и это НЕ 0.5: такая строка в сравнение с физикой не входит.
   */
  p_phys?: string;
  phys_age_ms?: number;
  model_rev?: string;
  strike?: string;
  projected?: string;
  state: string;
  mode: string;

  /** Мейкерская гипотеза. Нулевая цена означает «стакана не было». */
  plan_price: string;
  plan_size: string;
  plan_usd: string;
  plan_queue_ahead: string;
  plan_ttl_s: number;

  /** Тейкерская гипотеза. Прочерк — предложения не было. */
  taker_price?: string;
  taker_fee?: string;
  /**
   * Почему цены нет, когда её нет. Пусто — цена есть.
   *
   * Из 421 строки без цены на замере 2026-09-01: 312 не имели АСКА вовсе
   * (рыночный факт почти решённого окна), 71 не читала стакан, 38 имели аск при
   * нулевом размере прохода. Три разных события выглядели одним прочерком.
   */
  taker_reason?: string;
  book_bid?: string;
  book_ask?: string;
  book_age_ms?: number;

  /** Наблюдение лестницы входа. Прочерк — ленту не наблюдали. */
  maker_filled?: boolean;
  min_print_price?: string;

  /** Пусто — строка зачётная. Иначе машинное имя правила, погасившего её. */
  rule?: string;
  reversal?: boolean;

  resolved: boolean;
  won: boolean;
  shadow_maker_pnl?: string;
  shadow_taker_pnl?: string;
  settled_at?: string;
}

/**
 * Чем связаны два кошелька семьи — зеркало domain.FamilyLinkSource.
 *
 * `second` — совпадение секунды входа против собственного нуля пары.
 * `name` — сходство имён публичного профиля либо прямое указание адреса
 * другого кошелька архива вместо имени. Ловит семьи, которых секунда не видит
 * вовсе (оператор поделил рынок по длительности между двумя именами).
 */
export type FamilyLinkSource = "second" | "name";

/**
 * Улика связи двух кошельков со всеми числами, по которым решение можно
 * перепроверить чужими руками — зеркало domain.FamilyLink.
 *
 * Пара всегда упорядочена (`addr_a < addr_b`), а `source` — часть КЛЮЧА: одна
 * пара законно связана и секундой, и именем сразу, и обе улики стоит видеть
 * рядом, а не одну поверх другой.
 */
export interface FamilyLink {
  addr_a: string;
  addr_b: string;
  source: FamilyLinkSource;

  /** Общих окон у пары и из них тех, где оба вошли в пределах допуска. */
  shared: number;
  hits: number;
  /**
   * Столько совпадений дал бы НУЛЬ: моменты одного из пары случайно
   * переставлены между их же общими окнами. Дробное — это среднее.
   */
  expected: string;
  /** Доля общих окон, где стороны совпали. Прочерк — направленных окон не было. */
  agree?: string;
  /**
   * Медиана отношения меньшей ставки к большей на совпавших секундах. ВТОРОЙ
   * независимый признак оператора: у найденных семей 0.8–1.0, у случайного
   * совпадения — какое угодно.
   */
  size_ratio?: string;
  /** Хвост Пуассона после поправки Бенджамини—Хохберга. Прочерк — улика не статистическая (имя). */
  q?: string;

  /** Имена СНИМКОМ: профиль переименовывают, а улика обязана остаться проверяемой. */
  name_a?: string;
  name_b?: string;
  similarity?: string;

  first_seen_at: string;
  updated_at: string;
}

/** Кошелёк в составе семьи — зеркало domain.FamilyMember. */
export interface FamilyMember {
  address: string;
  /** Когда кошелёк ВПЕРВЫЕ увиден в этой семье. Переносится через ревизии. */
  since: string;
}

/**
 * Семья и вердикт по ней на одной ревизии состава — зеркало domain.Family.
 *
 * Отвергнутая семья (`in_pool: false`) приезжает наравне с пущенными: без неё
 * нельзя ответить, почему пул именно такой.
 */
export interface Family {
  family_id: string;
  members: FamilyMember[];

  edges: number;
  /**
   * Доля имеющихся рёбер от всех возможных внутри компоненты. ГЛАВНЫЙ сторож
   * против страты: у настоящих семей из двух-трёх плотность равна единице.
   */
  density: string;

  /**
   * Остаток над физикой: чем семья оказалась лучше нашего же расчёта.
   * Суммы, а не средние — частное за одни сутки нельзя сложить с частным за
   * другие.
   */
  windows: number;
  resid_sum: string;
  p_sum: string;
  /** Σ(y − p_phys) / sqrt(Σ p(1−p)). Прочерк — дисперсии не набралось, и это НЕ ноль. */
  resid_z?: string;
  notional_usd: string;
  /** Прочерк — семья ещё ни разу не торговала в наблюдаемой глубине. */
  last_trade_at?: string;

  in_pool: boolean;
  /** Машинное имя правила, не пустившего семью в пул. Пусто — семья в пуле. */
  reason?: string;
}

/**
 * Состав пула на одну ревизию вместе с порогами, под которыми он собран —
 * зеркало domain.FamilyRoster.
 *
 * Состав неизменяем ВНУТРИ ревизии: меняющийся на ходу список менял бы
 * определение выборки, и строки сигнала под разным составом нельзя было бы
 * складывать.
 */
export interface FamilyRoster {
  rev: number;
  decided_at: string;
  /** ВСЕ найденные семьи, включая не пущенные в пул. */
  families: Family[];

  /** Пороги снимком, все до одного. Смена порога открывает НОВУЮ выборку. */
  min_resid_z: string;
  exit_resid_z: string;
  min_windows: number;
  min_density: string;
  pool_days: number;
  idle_days: number;
}

/**
 * Теневая ставка по консенсусу одной семьи на одном окне — зеркало
 * domain.FamilySignal.
 *
 * Единица строки — ОКНО × СТОРОНА: вторая строка окна означает РАЗВОРОТ семьи
 * (см. `reversal`), а не повторный удар той же ставки.
 */
export interface FamilySignal {
  id: string;
  /** Когда консенсус ВПЕРВЫЕ собрался. Следующие сделки семьи к этому ничего не добавляют. */
  detected_at: string;

  condition_id: string;
  slug?: string;
  asset: string;
  window_end: string;
  /** Ширина метрики резолва СНИМКОМ: биржа меняла её на ходу. */
  window_s: number;
  /** За сколько до конца окна сработало. Знаковое: рынок торгуется и после конца. */
  lead_ms: number;

  asset_id: string;
  outcome_name?: string;

  /** Что именно сработало. */
  family_id: string;
  /** Ревизия состава, под которой снята строка. */
  roster_rev: number;
  wallets_agreed: number;
  wallets_seen: number;
  /**
   * Сколько РАЗНЫХ семей пула сошлось на этой стороне. Разрез сводки, а не
   * второй журнал: вне выборки правило «две семьи» физику не бьёт.
   */
  families_agreed: number;

  first_wallet: string;
  first_at: string;
  /** От первой сделки семьи до нашего решения — половина задержки это время блока Polygon. */
  trigger_lag_ms: number;

  family_usd: string;
  /** Доля крупнейшего кошелька семьи в family_usd. Больше половины — меряется один кит. */
  top_wallet_share?: string;
  /** Снимок отбора: чем семья заслужила место в пуле на момент решения. */
  pool_resid_z?: string;
  pool_windows: number;

  /** Пороги СНИМКОМ. */
  min_wallets: number;
  min_resid_z: string;
  tol_s: number;
  window_days: number;

  /**
   * Что знала ФИЗИКА в тот же момент. Прочерк в `p_phys` означает «замка не
   * наблюдали», и это НЕ 0.5: строка в сравнение с физикой не входит.
   */
  p_phys?: string;
  phys_age_ms?: number;
  model_rev?: string;
  strike?: string;
  projected?: string;

  state: string;
  /** Правило порога ЗАМКА, а не режим подсистемы: режима у неё нет и быть не может. */
  lock_mode?: string;

  /** ДВЕ гипотезы входа. Нулевая цена мейкера означает «стакана не было». */
  plan_price: string;
  plan_size: string;
  plan_usd: string;
  plan_queue_ahead: string;
  plan_ttl_s: number;

  /** Прочерк — стакана не было, и такая строка в тейкерскую сумму не входит вовсе. */
  taker_price?: string;
  /** Прочерк — расписание комиссии незнакомо; ноль законен и означает «комиссии нет». */
  taker_fee?: string;
  book_bid?: string;
  book_ask?: string;
  book_age_ms?: number;

  /** Наблюдение мейкерской гипотезы. Прочерк — ленту не наблюдали. */
  maker_filled?: boolean;
  min_print_price?: string;

  /** Пусто — строка зачётная. Иначе машинное имя правила, погасившего её. */
  rule?: string;
  reversal?: boolean;

  /** resolved = false означает «исход не подтверждён», а НЕ «проиграли». */
  resolved: boolean;
  won: boolean;
  /** Прочерк — «считать было нечем»; ноль законен и означает «стояли и не налили». */
  shadow_maker_pnl?: string;
  shadow_taker_pnl?: string;
  settled_at?: string;
}

/**
 * Одна ревизия состава в окне сводки — зеркало domain.FamilyRevRows.
 *
 * Нужна, чтобы смесь отборов была видна ЧИСЛОМ: сводка за сутки складывает
 * строки стольких пулов, сколько раз за окно пересобрался состав (замер
 * 2026-09-01 на боевой базе: одиннадцать ревизий за трое суток).
 *
 * `signals` — ЗАЧЁТНЫЕ строки ревизии, `rows` — все, включая погашенные
 * правилами: ревизия, целиком отсеянная воротами, обязана быть видна, а не
 * выглядеть отсутствующей.
 */
export interface FamilyRevRows {
  rev: number;
  signals: number;
  rows: number;
  last_at: string;
}

/**
 * Сводка по консенсусу семей за период — зеркало domain.FamilySummary.
 *
 * Считается ТОЛЬКО по строкам без `rule`: погашенная правилом строка не
 * участвовала в отборе, и подмешать её в сумму значило бы судить кромку по
 * строкам, которых сам отбор не касался.
 *
 * ДВА знаменателя намеренно: строка без `taker_price` не входит в
 * `sum_taker_price`, строка без `p_phys` не входит в `sum_p_phys`, и
 * `taker_priced` с `phys_priced` поэтому различаются — кромка над ценой без
 * кромки над физикой означает покупку стороны, которая и так выигрывала.
 */
export interface FamilySummary {
  since?: string;
  /**
   * Ревизия состава, которой порезана ЭТА сводка. Ноль означает «все ревизии
   * окна», то есть смесь разных отборов: состав пула неизменяем внутри
   * ревизии, но сама ревизия меняется чаще, чем набирается счёт приёмки.
   */
  rev: number;
  signals: number;
  /** Сколько из зачётных сведено С ПОДТВЕРЖДЁННЫМ исходом. */
  settled: number;
  won: number;
  /** Сколько мейкерских входов исполнилось бы. Верхняя граница: очередь не моделируется. */
  maker_filled: number;

  /** По скольким сведённым строкам известна цена тейкерского входа. Меньше `settled`, если стакана не было. */
  taker_priced: number;
  /**
   * Угадано СРЕДИ ТЕХ ЖЕ строк, где известна цена — а не `won` целиком.
   *
   * Строка без стакана попадает в `won`, но не в `taker_priced`: сравнить
   * общий `won` с `sum_taker_price` значило бы делить числитель одного
   * множества на знаменатель другого, и кромка сдвинулась бы ровно на долю
   * таких строк.
   */
  won_taker_priced: number;
  sum_taker_price: string;
  /**
   * Σ p(1−p) по тем же строкам — ТОЧНАЯ дисперсия разности «угадано минус
   * Σ цены» под нулём «кошелёк откалиброван на цене входа». Считанная на
   * сервере из каждой строки, а не `n·p̂(1−p̂)`: это верхняя граница по
   * Йенсену, она занижает z тем сильнее, чем шире разброс цен, а зона входа
   * здесь 0.10–0.90.
   */
  sum_taker_pq: string;
  /** Прочерк в `p_phys` означает «замка не наблюдали», а не 0.5 — знаменатель СВОЙ и обычно другой, чем у цены. */
  phys_priced: number;
  /** То же, что `won_taker_priced`, но для физики. */
  won_phys_priced: number;
  sum_p_phys: string;
  /** То же, что `sum_taker_pq`, но для физики. */
  sum_phys_pq: string;

  shadow_maker_pnl: string;
  shadow_taker_pnl: string;
}

/**
 * Клетка сводки теневой покупки смещённого края: «длительность × порог × минута».
 *
 * Клетка, а не итог, и это предмет замера, а не оформление. Базовая линия сама
 * зависит от минуты окна — по одному знаку «цена против страйка» известно
 * 64–65 % на первой минуте, 69–70 % на второй, 80 % на третьей, 91–92 % на
 * четвёртой, — и итог по всем минутам сравнивался бы с планкой, которой не было
 * ни в одну минуту.
 *
 * Доли здесь НЕ приезжают: сервер шлёт числители со знаменателями, и это не
 * экономия. Доля без знаменателя нечитаема, а посчитанная на сервере приезжает
 * уже без него — клетка на трёх строках выглядела бы так же уверенно, как на
 * трёхстах.
 */
export interface ShiftCell {
  window_len_s: number;
  /** Ступень лесенки в базисных пунктах: 6300 = 0.63. */
  threshold_bp: number;
  /** Минута окна, на которой сработал вход: 1 — первая. */
  minute: number;

  signals: number;
  settled: number;
  won: number;
  /**
   * То же по строкам БЕЗ разворота, то есть по стратегии оператора в чистом
   * виде: одна ставка в окно, на первое смещение. Разница пар — цена разворотов.
   */
  first_settled: number;
  first_won: number;

  /**
   * Линия цены: Σ цены налива по сведённым и её знаменатель. Линия физики — Σ
   * вероятности замка и СВОЙ знаменатель: замок бывает не наблюдён.
   *
   * Обе обязательны. Против первой отвечается «есть ли кромка», против второй —
   * «есть ли кромка СВЕРХ того, что и так выигрывало».
   */
  price_sum: string;
  priced: number;
  phys_sum: string;
  phys_n: number;

  /** Годность двух планов. Разница — цена гэпа через ступень. */
  market_ok: number;
  limit_ok: number;
  shares: string;
  /** Откаты и их знаменатель: ноль откатов бывает и от «мы не смотрели». */
  retraced: number;
  watched: number;

  pnl: string;
  pnl_priced: number;
  lead_median_ms: number;
}

/**
 * Корзина разреза клетки смещения: «подтверждение расхождением × разворот».
 *
 * Отвечает на вопрос, который клетка закрыть не может: равноценны ли входы
 * внутри неё. Замер 2026-09-01 (4 суток, BTC 5m, первое касание лесенки) развёл
 * их вдвое — с подтверждением модели 64.5 % угаданного при наливе 0.591, без
 * него 58.3 % при наливе 0.629.
 */
export interface ShiftSlice {
  /** Ключ РОДИТЕЛЬСКОЙ клетки: по нему корзина ложится под свою строку. */
  window_len_s: number;
  threshold_bp: number;
  minute: number;

  /**
   * Расхождение модели назвало ту же сторону не раньше чем за `confirm_s` до
   * входа. Односторонне: сигнал ПОСЛЕ входа — метка отката цены, а не
   * предсказание.
   */
  confirmed: boolean;
  /** Порог перешла эта сторона ПОСЛЕ противоположной. */
  reversal: boolean;

  signals: number;
  settled: number;
  won: number;
  /**
   * Линия цены КОРЗИНЫ, а не клетки. Обязательна: подтверждённые входы
   * наливаются дешевле, а безубыток целиком определяется наливом — доля,
   * сравнённая с общей планкой клетки, врала бы знаком.
   */
  price_sum: string;
  priced: number;
  /** Счёт корзины и его знаменатель. */
  pnl: string;
  pnl_priced: number;
}

/** Строка ленты теневых входов по смещению. */
export interface ShiftSignal {
  id: string;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset: string;
  window_end: string;
  window_s: number;
  window_len_s: number;
  lead_ms: number;

  asset_id: string;
  outcome_name?: string;
  threshold_bp: number;
  /** Порог перешла эта сторона ПОСЛЕ противоположной. */
  reversal?: boolean;

  mode?: string;
  budget_usd: string;

  /** Отсутствие цены означает «этой стороны в стакане нет», а не ноль. */
  book_bid?: string;
  book_ask?: string;
  book_age_ms?: number;
  /**
   * Ширина СЛЕПОГО ОКНА: сколько прошло с прошлого осмотра окна до
   * срабатывания. Пересечение порога случилось где-то внутри неё, поэтому она и
   * есть верхняя граница нашей ошибки в цене входа.
   *
   * Отсутствие означает «не знаем» (строка старого режима либо первый осмотр
   * окна), и это не ноль: ноль читался бы как «осмотрели мгновенно».
   */
  sample_ms?: number;
  /**
   * Режим съёма строки. Сводка и лента читаются только по book (по движению
   * вершины стакана): строки секундного обхода мерили наше запаздывание, а не
   * рынок, и с экрана убраны. Поле остаётся в контракте как снимок журнала.
   */
  sample_src?: string;

  strike?: string;
  projected?: string;
  cushion?: string;
  prob_flip?: string;
  sigma_left?: string;
  /**
   * Вероятность НАШЕЙ стороны по замку. Отсутствие означает «сторону назвать
   * было нечем», и это НЕ 0.5: такая строка в сравнение с физикой не входит.
   */
  p_phys?: string;
  state?: string;
  model_rev?: string;

  /** План «по рынку» — то, что заплатили бы; «по порогу» — что дал бы лимит. */
  market: LockPlan;
  limit: LockPlan;

  /** Отсутствие означает «не наблюдали до конца окна», и это не false. */
  retraced?: boolean;
  max_ask?: string;
  min_ask?: string;

  resolved: boolean;
  won: boolean;
  shadow_pnl?: string;
  settled_at?: string;
}

/**
 * Сводка теневой покупки пары дешевле доллара по одной РОЛИ.
 *
 * Роли отвечают на разные вопросы, и общий итог ответил бы на тот, которого
 * никто не задавал: `taker` берёт обе ноги сразу и от исхода не зависит вовсе,
 * `hybrid` платит за ожидание риском голой ноги.
 */
export interface PairRoleSummary {
  role: "taker" | "hybrid";
  /**
   * Наблюдалось против собралось. ГЛАВНАЯ пара сводки: «пара дешевле доллара»
   * на вершине в две share — не сделка, а показание прибора, и первое число без
   * второго читалось бы как число сделок, которых не было.
   */
  signals: number;
  feasible: number;
  max_attempt: number;

  edge_sum: string;
  edge_priced: number;
  /**
   * Кромка на share и во что обходится пара с комиссией. Читаются ВМЕСТЕ:
   * вторая объясняет первую, потому что комиссия максимальна у середины цены
   * (пара 0.50/0.50 отдаёт 3.5 ¢ на доллар выплаты, пара 0.02/0.98 — 0.28 ¢).
   */
  edge_median: string;
  cost_net_median: string;
  shares: string;

  /** У тейкера оба нуля по построению: наблюдать нечего, он берёт сразу. */
  watched: number;
  filled: number;
  /**
   * Снос — ГЛАВНОЕ число роли hybrid: насколько подорожала вторая нога, пока
   * наливали первую. Мейкера наливают ровно тогда, когда рынок пошёл против
   * него, и эта величина ставит цену такому исполнению.
   */
  slip_median: string;
  slip_n: number;
  /** Что вышло бы, если вторую ногу не добирать вовсе. Контроль к сносу. */
  naked_pnl: string;
  naked_priced: number;

  lead_median_ms: number;
}

/** Строка ленты теневых пар. */
export interface PairSignal {
  id: string;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset: string;
  window_end: string;
  window_s: number;
  /** Знаковое: у гибрида наблюдение живёт и после конца окна. */
  lead_ms: number;

  role: "taker" | "hybrid";
  anchor_asset_id: string;
  attempt: number;
  mode?: string;
  enter_margin: string;
  budget_usd: string;

  up_asset_id: string;
  up_bid?: string;
  up_ask?: string;
  down_asset_id: string;
  down_bid?: string;
  down_ask?: string;
  book_age_ms?: number;

  /** Отсутствие комиссии означает «расписание незнакомо», и тогда кромки нет. */
  cost_gross?: string;
  fee_up?: string;
  fee_down?: string;
  cost_net?: string;
  edge?: string;

  size?: string;
  size_up?: string;
  size_down?: string;
  quote_up?: string;
  quote_down?: string;
  partial?: boolean;
  /** Почему пары не собралось. Пусто у годного плана. */
  reason?: string;

  maker_price?: string;
  maker_size?: string;
  maker_queue_ahead?: string;
  other_ask_at_plan?: string;
  other_ask_at_fill?: string;
  cost_planned?: string;
  cost_real?: string;
  slip?: string;
  filled_at?: string;
  tape_shares?: string;
  fill_size?: string;

  resolved: boolean;
  won: boolean;
  naked_pnl?: string;
  settled_at?: string;
}

/**
 * Форма счёта одной группы: из чего сложилась сумма.
 *
 * # Зачем это рядом с колонкой «счёт»
 *
 * Сумма отвечает «сколько», и на теневых журналах этого мало до бесполезности.
 * Замер 2026-08-28: у расхождения модели и стакана счёт +$1542 по 717 сделкам
 * выглядит уверенным плюсом, но медиана сделки там −$1.16, а без десяти лучших
 * остаётся −$3.88 — весь результат в 1.4 % строк. У почти решённого счёт втрое
 * меньше (+$110), зато медиана +$1.77 и без трёх лучших остаётся +$59.74.
 *
 * Это ПРОТИВОПОЛОЖНЫЕ утверждения о том, стоит ли ставить деньги, и по колонке
 * «счёт» они неразличимы.
 */
export interface ShadowShape {
  /** Ключ строки, рядом с которой форма встаёт. Собран как key самой строки. */
  key: string;
  /** Сколько сделок посчитано и каков их счёт. */
  n: number;
  sum: string;
  /**
   * Медианная сделка. Главное число: отрицательная медиана при положительном
   * счёте означает, что типичная сделка убыточна, а счёт вытянут хвостом.
   */
  median: string;
  /**
   * Счёт без трёх лучших сделок и сама лучшая. Проверка на хрупкость: если трёх
   * сделок из семисот хватает, чтобы плюс стал минусом, ставка держится не на
   * кромке.
   */
  without_top3: string;
  top1: string;
  /**
   * Гистограмма по ФИКСИРОВАННЫМ границам, десять корзин:
   * &lt;−25, −25…−10, −10…−5, −5…−2, −2…0, 0…2, 2…5, 5…10, 10…25, ≥25.
   *
   * Границы одни на все журналы намеренно — чтобы формы сравнивались глазами.
   */
  bins: number[];
  /**
   * СРОК НАБЛЮДЕНИЯ группы: сколько строк она набрала и между какими моментами.
   *
   * Ради удельного счёта. Подсистемы заводились в разные дни, и суммы у них
   * несравнимы по построению: +$189 у почти решённого против +$12 у соседа —
   * это разница возрастов, а не кромок. Час и сутки работы приводят их к одной
   * мере (`perHour`, `perDay` в lib/shape).
   *
   * Срок считается по ВСЕМ строкам группы, а не по её сделкам: наблюдение — это
   * время, когда подсистема смотрела на рынок. Возьми мы время между сделками,
   * редко торгующая подсистема получила бы крошечный знаменатель и невероятную
   * ставку в час.
   *
   * `observed` стои́т рядом не для полноты: 47 строк за 34 часа и 1036 строк за
   * 34 часа — это работавшая с перерывами подсистема и работавшая непрерывно, а
   * срок у них одинаковый.
   */
  observed: number;
  first_seen: string;
  last_seen: string;
}

/**
 * Клетка сетки теневой ставки по КОТИРОВКЕ когорты плюсовых тейкеров.
 *
 * # Клетки ВЛОЖЕНЫ, и складывать их нельзя
 *
 * Строка с котировкой 0.72 попала в каждую клетку до 0.70 включительно, и сумма
 * по столбцу «уверенность» посчитает её трижды. Контракт помечает это полем
 * `nested` в ответе, а панель обязана сказать словами: то же правило и та же
 * причина, что у сетки порогов расхождения.
 */
export interface ConsensusCell {
  /** Уверенность в базисных пунктах: 6500 = 0.65. */
  consensus_bp: number;
  /** Ступень ставки целыми долларами. */
  stake_step_usd: number;
  /** Сколько крупных согласных филлов требовалось. */
  agree_n: number;
  /**
   * Клетка, назначенная главной ДО первых данных.
   *
   * Читатель обязан видеть её без похода в конфиг: при 36 гипотезах «лучшая»
   * клетка выглядит кромкой ПО ПОСТРОЕНИЮ, и панель показывает главную, а не
   * максимум по сетке.
   */
  main_cell: boolean;
  bucket: string;

  /**
   * Зачётные строки и погашенные правилом — ПОРОЗНЬ. Вопросы у них разные:
   * первые отвечают «есть ли кромка», вторые — «сколько мы пропустили и почему».
   */
  signals: number;
  suppressed: number;
  settled: number;
  won: number;
  /**
   * Победы в знаменателях priced и phys_known. Z против цены и физики сервер
   * считает по ним, а не по won: общий счётчик приписывал бы сравнению победы
   * строк, у которых цены не было (так нота прода показала z 5.2 вместо 1.9).
   */
  won_priced: number;
  won_phys: number;

  /**
   * Три сравнения, и все три обязательны.
   *
   * Против ЦЕНЫ — «есть ли кромка». Против ФИЗИКИ — «есть ли она сверх замка»:
   * один знак «цена против страйка» уже даёт 64–65 % на первой минуте окна и
   * 91–92 % на четвёртой. Против САМОЙ КОТИРОВКИ — «калибрована ли группа», то
   * есть сбывается ли то, что она обещала своими деньгами; это сравнение
   * специфично для журнала и есть тот вопрос, ради которого он заведён.
   *
   * `*_z` отсутствует, когда считать не из чего. Это НЕ ноль: ноль читался бы
   * как «измерили и получили ровно среднее».
   */
  price_sum: string;
  price_z?: number;
  priced: number;
  phys_sum: string;
  phys_z?: number;
  phys_known: number;
  cons_sum: string;
  cons_z?: number;
  cons_known: number;

  /**
   * Разрез по ПРОЧТЕНИЮ крупных филлов: подтверждение согласно не только по оси,
   * но и стороной. Ответ на вопрос, какое из двух прочтений право, — переиграть
   * его задним числом нельзя, поэтому оба счётчика пишутся с первого дня.
   */
  by_side: number;
  won_by_side: number;
  side_sum: string;
  side_z?: number;

  /** Разрез «подтверждение пришло ИЗВНЕ группы»: иначе оно пересказывает котировку. */
  outside: number;
  won_outside: number;
  outside_sum: string;
  outside_z?: number;

  /** Разрез по доле крупнейшего участника выше половины ставки. */
  concentrated: number;
  won_concentrated: number;
  conc_sum: string;
  conc_z?: number;

  /**
   * Мейкер: ПРЕСЛЕДОВАНИЕ против СТОЯНИЯ, обе гипотезы из одной строки.
   *
   * `observed` — знаменатель: прочерк в исполнении означает «ленту не
   * наблюдали», а это не «не налили». `drift` — Σ разности между аском и ценой
   * налива: если она стремится к нулю, преследование подтянуло цену к аску, и
   * экономия мейкера свелась к комиссии.
   */
  filled: number;
  won_filled: number;
  standing: number;
  observed: number;
  drift: string;
  drift_known: number;
  reprices: number;

  stake_median: string;
  consensus_median: string;
  top_share_median: string;
  wallets_median: number;
  window_gross_median: string;
  reversals: number;
  maker_pnl: string;
  taker_pnl: string;
  /** Σ потраченного тейкером по строкам с посчитанным счётом и ROI к нему. */
  taker_cost: string;
  taker_roi?: number;
}

/**
 * Тейкер ГЛАВНОЙ клетки на одной корзине цены входа: винрейт, потраченное, счёт
 * и ROI по сигналу. «Стакан по 0.7» из вопроса оператора — корзина [0.70, 0.80).
 * ROI едет с z против цены входа: на десятках строк знак счёта решают хвосты
 * множителя выплаты.
 */
export interface ConsensusTakerBucket {
  bucket: string;
  bucket_bp: number;
  entries: number;
  won: number;
  price_sum: string;
  price_z?: number;
  cost: string;
  pnl: string;
  pnl_known: number;
  roi?: number;
}

/** Имя и число: правило отсева или класс отказа тейкера. */
export interface ConsensusCount {
  key: string;
  n: number;
}

/** Корзина цены входа тейкера шириной 0.05 в отчёте клетки. */
export interface ConsensusPriceBucket {
  bucket: string;
  bucket_bp: number;
  entries: number;
  resolved: number;
  won: number;
  price_sum: string;
  price_z?: number;
  cost: string;
  pnl: string;
  fees: string;
  pnl_known: number;
  roi?: number;
}

/**
 * Клетка сетки с краткой сводкой тейкера — ячейка матрицы режимов перед
 * переключателем. Клетки вложены: читать по ячейкам, а не складывать столбцы.
 */
export interface ConsensusCellKey {
  code: string;
  consensus_bp: number;
  stake_step_usd: number;
  agree_n: number;
  main_cell: boolean;
  entries: number;
  won: number;
  price_sum: string;
  price_z?: number;
  cost: string;
  pnl: string;
  roi?: number;
}

/**
 * Отчёт о тейкерской гипотезе ОДНОЙ клетки воронкой.
 *
 * Пять вопросов оператора в порядке чтения: барьеры (из нот трейдера —
 * `barriers_known: false` означает «трейдер недоступен», а не «барьеров нет»),
 * сколько окон было подходящих, в каких можно было зайти тейкером, распределение
 * цены входа, итог с комиссией. Каждая ступень — знаменатель следующей.
 */
export interface ConsensusTakerReport {
  hours: number;
  cell: string;
  main_cell: boolean;
  /** Область отчёта; пустое поле — без ограничения. */
  asset: string;
  window: string;
  barriers_known: boolean;
  barriers: Record<string, string>;
  windows_total: number;
  windows_signal: number;
  windows_cell: number;
  rows: number;
  clean: number;
  rules: ConsensusCount[];
  takeable: number;
  reasons: ConsensusCount[];
  /** Зачётных строк без мейкерского плана: до 2026-09-03 их гасило min_size. */
  no_maker_plan: number;
  resolved: number;
  won: number;
  price_sum: string;
  price_z?: number;
  cost: string;
  pnl: string;
  fees: string;
  pnl_known: number;
  roi?: number;
  buckets: ConsensusPriceBucket[];
  cells: ConsensusCellKey[];
}

/**
 * Калибровка котировки на одной корзине её цены.
 *
 * Ответ на вопрос «группа говорит 0.60 — сбывается в 60 % или в 80 %». Единица
 * — ОКНО × корзина (первое попадание), а не строка сетки: клетки вложены, и одно
 * окно лежало бы в корзине восемнадцать раз. Правило отсева не учитывается: оно
 * говорит о нашем входе, а не об обещании группы.
 *
 * Рядом с победами едут Σ аска и Σ физики ТОЙ ЖЕ секунды, и без них доля читать
 * нельзя: 83 % на корзине 0.60 выглядят кромкой ровно до тех пор, пока не видно,
 * что аск в тот момент стоял 0.785.
 */
export interface ConsensusBin {
  /** Нижняя граница корзины словом («0.60») и в базисных пунктах. */
  bin: string;
  bin_bp: number;
  windows: number;
  won: number;
  cons_sum: string;
  cons_z?: number;
  ask_sum: string;
  ask_z?: number;
  ask_known: number;
  phys_sum: string;
  phys_z?: number;
  phys_known: number;
}

/**
 * Строка ленты теневых ставок по котировке группы.
 *
 * ЛОВУШКА, которую панель обязана называть вслух: `asset_id` — сторона,
 * предсказанная КОТИРОВКОЙ, и она законно указывает против того, куда группа
 * встала по нетто. Группа, купившая только Down по 0.2, стоит в лонге Down и
 * котирует Up 0.800.
 */
export interface ConsensusSignal {
  id: string;
  detected_at: string;
  condition_id: string;
  slug?: string;
  asset: string;
  window_end: string;
  window_s: number;
  lead_ms: number;
  asset_id: string;
  outcome_name?: string;

  /** Уверенность группы, всегда не ниже половины: сторона вынесена в asset_id. */
  consensus_price: string;
  /** Та же величина на оси первого исхода. Сырьё сверки с панелью участников. */
  p_first: string;
  stake_usd: string;
  /** Ставка долей оборота окна, базисные пункты. */
  stake_share_bp: number;
  window_gross_usd: string;
  top_wallet_usd: string;
  wallets: number;

  /**
   * Крупные филлы в ДВУХ прочтениях. `big_side_500` — подмножество
   * `big_axis_500`, и разность равна числу дешёвых покупок противоположного
   * токена.
   */
  big_axis_500: number;
  big_side_500: number;
  big_group_500: number;

  consensus_bp: number;
  stake_step_usd: number;
  agree_n: number;
  main_cell: boolean;
  cells_total: number;

  roster_size: number;
  roster_plus: number;

  /** Прочерк означает «замка не наблюдали», и это НЕ половина. */
  p_phys?: string;
  book_bid?: string;
  book_ask?: string;
  book_age_ms?: number;
  book_held_ms?: number;

  taker_price?: string;
  taker_reason?: string;

  /** Цена преследующей заявки при ПОСТАНОВКЕ. */
  plan_price: string;
  /**
   * Налили бы. Прочерк — ленту не наблюдали; ЛОЖЬ — «стояли, переставлялись и
   * не налили», и это настоящий ответ, а не его отсутствие.
   */
  maker_filled?: boolean;
  maker_fill_price?: string;
  /** Аск в момент налива: против цены налива он и отвечает на вопрос вехи. */
  maker_fill_ask?: string;
  maker_reprices: number;
  min_print_price?: string;

  rule?: string;
  reversal?: boolean;
  resolved: boolean;
  won: boolean;
  shadow_maker_pnl?: string;
  shadow_taker_pnl?: string;
}

// --- ИИ-судья (ADR-0038) ---------------------------------------------------

/** Тейкер-план из lock.Plan; деньги — строками контракта. */
export interface AIPlan {
  kind: string;
  price?: string;
  size?: string;
  quote?: string;
  fee?: string;
  fee_known: boolean;
  pnl?: string;
  levels?: number;
  partial?: boolean;
  reason?: string;
}

/** Ответ на один вопрос в общей форме (aiprovider.Answer). */
export interface AIAnswer {
  name: string;
  kind: "predicate" | "choice" | "score";
  refused?: boolean;
  refusal?: string;
  unknown?: boolean;
  p?: number;
  choice?: string;
  score?: number;
  probs?: Record<string, number>;
  confidence: number;
}

/** Ответ одного провайдера. */
export interface AIVerdict {
  ask_id: string;
  provider: string;
  model?: string;
  started_at: string;
  latency_ms: number;
  attempts: number;
  status: "ok" | "error" | "timeout" | "refusal" | "unknown_shape" | "interrupted";
  http_status?: number;
  error?: string;
  p_up?: number;
  action?: "buy_up" | "buy_down" | "skip" | "";
  confidence?: number;
  entered: boolean;
  /** Все ответы по имени вопроса; только в полном чтении запроса. */
  answers?: Record<string, AIAnswer>;
  input_tokens: number;
  output_tokens: number;
  answer_plan?: AIPlan;
  answer_pnl?: string;
}

/** Снимок рынка, на котором судили запрос. */
export interface AISnapshot {
  ask_id: string;
  built_at: string;
  state: "built" | "expired" | "rejected";
  reason?: string;
  context_rev?: string;
  inputs_missing: string[];
  providers: string[];
  input_text?: string;
  window_end: string;
  up_asset_id?: string;
  down_asset_id?: string;
  mid_up?: number;
  phys_p_up?: number;
  budget_usd: string;
  min_confidence: number;
  plan_up?: AIPlan;
  plan_down?: AIPlan;
  resolved?: boolean;
  winner_asset?: string;
  pnl_up?: string;
  pnl_down?: string;
  settled_at?: string;
}

/** Запрос к судье с производным состоянием. */
export interface AIAskView {
  id: string;
  condition_id: string;
  requested_at: string;
  budget_usd: string;
  issued_by: string;
  trigger: string;
  status: string;
  error?: string;
  view_status: "pending" | "no_response" | "answering" | "done" | "failed" | "expired" | "rejected";
  terminal: boolean;
  snapshot?: AISnapshot;
  verdicts: AIVerdict[];
}

/** Сводка одного провайдера. */
export interface AIProviderSummary {
  provider: string;
  asks: number;
  ok: number;
  errors: number;
  timeouts: number;
  refusals: number;
  unknown_shape: number;
  interrupted: number;
  latency_p50_ms?: number;
  latency_p95_ms?: number;
  settled: number;
  brier?: number;
  log_loss?: number;
  actions: number;
  action_hits: number;
  entered: number;
  won: number;
  pnl_snapshot?: string;
  pnl_answer?: string;
  inconsistent: number;
  input_tokens: number;
}

/** Базовая строка: та же калибровка у рынка и у физики замка. */
export interface AIBaseline {
  name: "market_mid" | "physics";
  settled: number;
  brier?: number;
  log_loss?: number;
}

export interface AISummary {
  since: string;
  min_confidence: number;
  asks: number;
  built: number;
  expired: number;
  rejected: number;
  no_snapshot: number;
  both_ok: number;
  agreed: number;
  providers: AIProviderSummary[];
  baselines: AIBaseline[];
}

/**
 * Сводка фида рыночных данных crypto-console по активу (ADR-0039) — зеркало
 * bus.CCFeedSummary.
 *
 * Числа — строки, и это не небрежность: цены вершин и объёмы считает
 * pm_scalper (money.Amount), а производные величины crypto-console приходят
 * как есть, до пятнадцати знаков. Пустая строка или пропавшее поле — «не
 * знаем», и причина тогда лежит в соседнем `*_why`: «нет» и «ноль» на экране
 * обязаны различаться.
 */
export interface CCFeedSummary {
  asset: string;
  at: string;
  connected: boolean;
  /** У скольких пар вершина пригодна сейчас — из venues_total. */
  venues_live: number;
  venues_total: number;
  consolidated?: CCFeedConsolidated;
  consolidated_why?: string;
  flow?: CCFeedFlow;
  flow_why?: string;
  /** Разложение изменения цены по окнам 10s, 1m, 5m, 15m. */
  moves: CCFeedMove[] | null;
  impact?: CCFeedImpact;
  impact_why?: string;
  venues: CCFeedVenue[] | null;
  /** История за 15 минут из памяти клиента трейдера, а не из crypto-console. */
  price: CCFeedPricePoint[] | null;
  taker: CCFeedTakerMinute[] | null;
  liquidations: CCFeedLiquidation[] | null;
  oi_change: CCFeedOIChange[] | null;
  /**
   * Признаки для ИИ-судьи из той же истории. Ликвидации, склеенные в серии
   * одной стороны (последние 10); liq_venues из liq_perps — у скольких перпов
   * был хоть один принт: сумма серий — нижняя граница.
   */
  liq_runs?: CCFeedLiqRun[];
  liq_runs_omitted?: number;
  liq_venues: number;
  liq_perps: number;
  burst?: CCFeedBurst;
  burst_why?: string;
  swing?: CCFeedSwing;
  swing_why?: string;
  positioning?: CCFeedPositioning;

  /**
   * Величины, которые считает сам crypto-console (его issues #2–#8):
   * позиционирование по OI в USD, модельная карта ликвидаций, стены сводного
   * стакана перпов у вершины и крупным шагом, свёртки минутных свечей.
   */
  console_positioning?: CCFeedConsolePositioning;
  console_positioning_why?: string;
  liq_map?: CCFeedLiqMap;
  liq_map_why?: string;
  walls_near?: CCFeedWalls;
  walls_near_why?: string;
  walls_wide?: CCFeedWalls;
  walls_wide_why?: string;
  ranges?: CCFeedRange[];
  ranges_why?: string;
}

/** Позиционирование по расчёту crypto-console; пустое число — «не знаем». */
export interface CCFeedConsolePositioning {
  as_of: string;
  oi_total_usd?: string;
  oi_venues: number;
  oi_why?: string;
  oi_change: { span_s: number; pct?: string; why?: string }[];
  /** Взвешенный по OI funding, б.п. за 8 ч, «+» — платят лонги. */
  funding_bp8h?: string;
  funding_venues: number;
  funding_why?: string;
  funding_next_at?: string;
  basis_bp?: string;
  basis_why?: string;
  crowded?: 'long' | 'short' | 'neutral';
  crowded_why?: string;
  crowded_threshold_bp8h?: string;
  venues: {
    venue: string;
    inst: string;
    oi_usd?: string;
    oi_why?: string;
    funding_bp8h?: string;
    funding_why?: string;
    funding_kind?: string;
  }[];
  excluded: { venue: string; inst: string; part: string; reason: string }[];
}

/** Корзина модельной карты ликвидаций: расстояние до центра от середины. */
export interface CCFeedLiqCluster {
  px_from: string;
  px_to: string;
  dist_bp: string;
  usd: string;
}

/** Модельная карта ликвидаций crypto-console — модель, а не позиции. */
export interface CCFeedLiqMap {
  as_of: string;
  source: string;
  mid: string;
  long_total_usd?: string;
  short_total_usd?: string;
  long_outside_usd?: string;
  short_outside_usd?: string;
  nearest_below?: CCFeedLiqCluster;
  nearest_below_why?: string;
  nearest_above?: CCFeedLiqCluster;
  nearest_above_why?: string;
  largest_below?: CCFeedLiqCluster;
  largest_above?: CCFeedLiqCluster;
  cluster_usd?: string;
  buckets: { px_from: string; px_to: string; long_usd: string; short_usd: string }[] | null;
  history_from?: string;
  minutes_used: number;
  partial: boolean;
  partial_why?: string;
  venues: string[];
  excluded: number;
}

/** До трёх крупнейших строк сводного стакана перпов на сторону. */
export interface CCFeedWalls {
  set: string;
  step: string;
  as_of: string;
  venues: number;
  bids: CCFeedWall[];
  asks: CCFeedWall[];
  why?: string;
}

export interface CCFeedWall {
  price: string;
  qty: string;
  usd: string;
  /** От середины вершины сводки: биды < 0, аски > 0. */
  dist_bp: string;
  venues: number;
  coverage: number;
}

/** Свёртка минутных свечей за span_min минут (сводный VWAP по площадкам). */
export interface CCFeedRange {
  span_min: number;
  from: string;
  minutes: number;
  price_minutes: number;
  open: string;
  high: string;
  high_at: string;
  low: string;
  low_at: string;
  close: string;
  change_bp: number;
  buy_usd: string;
  sell_usd: string;
  volume_minutes: number;
  /** Минуты, чья сумма собрана не со всех венью дельты (венью без курса). */
  usd_partial_minutes?: number;
  /** Последняя причина неполного или пустого долларового объёма в окне. */
  usd_why?: string;
  liq_long_usd: string;
  liq_short_usd: string;
  liq_complete: boolean;
}

/** Серия ликвидаций одной стороны без пауз длиннее 30 с. */
export interface CCFeedLiqRun {
  start: string;
  end: string;
  side: string;
  prints: number;
  qty: string;
  usd: string;
  price_lo: string;
  price_hi: string;
  venues: string[];
}

/** Крупнейший всплеск середины за историю и его откат к моменту сводки. */
export interface CCFeedBurst {
  end: string;
  span_s: number;
  from: string;
  to: string;
  now: string;
  move_usd: string;
  move_bp: number;
  x_median: number;
  taker_delta: string;
  taker_delta_usd: string;
  /** Доля хода, отыгранная назад: 0 — нет, 1 — вернулся к from. */
  retraced: number;
}

export interface CCFeedSwing {
  high: string;
  high_at: string;
  low: string;
  low_at: string;
  slope_usd_min: number;
  vol: { span_s: number; usd_min: number }[] | null;
}

/** Позиционирование перпов: funding в б.п. за 8 ч («+» — платят лонги), Δ% OI, базис. */
export interface CCFeedPositioning {
  funding: { venue: string; bp_8h: number; sign_assumed?: boolean; kind?: string; stale?: boolean }[] | null;
  funding_median_bp?: number;
  next_funding?: string;
  crowded?: string;
  funding_off?: string[];
  oi: { venue: string; pct_5m?: number; pct_15m?: number }[] | null;
  oi_median_5m?: number;
  oi_median_15m?: number;
  basis?: { venue: string; bp: number }[];
}

/**
 * Сводная вершина по перпам. Только перпы сознательно: со спотом в наборе
 * лучший bid спота стоял бы выше ask перпа на базис, и перекрёст читался бы
 * аномалией рынка.
 */
export interface CCFeedConsolidated {
  set: string;
  bid: string;
  bid_venue: string;
  ask: string;
  ask_venue: string;
  mid: string;
  /** Отрицательный — перекрёст. */
  spread_bp: string;
  confidence: string;
  as_of: string;
}

export interface CCFeedFlow {
  set: string;
  as_of: string;
  windows: CCFeedFlowWindow[] | null;
  liquidations: CCFeedLiqWindow[] | null;
  liq_why?: string;
  /** Книга бирж в полосах ±5/10/25 б.п. от своих середин, базовый актив. */
  imbalance?: { band_bp: number; bid?: string; ask?: string; why?: string }[];
  /** Доля рынка (по OI в USD) с полным потоком ликвидаций и роли площадок. */
  liq_coverage?: {
    oi_covered_pct?: string;
    oi_known_pct?: string;
    oi_why?: string;
    full: string[];
    sampled: string[];
    partial: string[];
    not_published: string[];
    in_trades: string[];
    unknown: string[];
    oi_unknown: string[];
  };
}

/** Объёмы тейкеров за окно, в базовом активе. */
export interface CCFeedFlowWindow {
  span_s: number;
  buy: string;
  sell: string;
  delta: string;
  notional_usd: string;
  complete: boolean;
}

/** Ликвидации за окно: объём закрытых лонгов и шортов. */
export interface CCFeedLiqWindow {
  span_s: number;
  long: string;
  short: string;
  complete: boolean;
  reason?: string;
  /** Закрытый код неполноты: sampled, window_partial, gap, … */
  reason_code?: string;
}

/** Из чего сложилось изменение середины за окно, $. */
export interface CCFeedMove {
  window: string;
  mid_change?: string;
  takers_buy?: string;
  takers_sell?: string;
  removal_bids?: string;
  removal_asks?: string;
  addition_bids?: string;
  addition_asks?: string;
  undefined?: string;
  confidence?: string;
  reason?: string;
  /** Разложения нет — почему. */
  why?: string;
}

/** Импакт потока на цену по минутам: λ — б.п. на $1M дельты, E — эффективность. */
export interface CCFeedImpact {
  as_of: string;
  points: CCFeedImpactPoint[] | null;
  lambda_ema25?: string;
  lambda_ema99?: string;
  lambda_rel?: string;
  /** Нормы окна, по которому оценена последняя минута. */
  med_abs_ret_bp?: string;
  med_vol_usd?: string;
  med_abs_delta_usd?: string;
  /** Свёртка всех пришедших минут: сколько меток поглощения и вакуума. */
  minutes: number;
  absorptions: number;
  vacuums: number;
}

export interface CCFeedImpactPoint {
  t: string;
  provisional: boolean;
  ret_bp?: string;
  delta_usd?: string;
  vol_usd?: string;
  lambda?: string;
  e?: string;
  /** absorption | vacuum */
  mark?: string;
}

/**
 * Строка биржи. У mark, funding и OI значение и `*_why` могут прийти ВМЕСТЕ:
 * значение старше порога сервера, но показывается с пометкой — сервер шлёт
 * их только при изменении, и верная ставка финансирования «стареет» за 10 с.
 */
export interface CCFeedVenue {
  venue: string;
  inst: string;
  /** perp | spot | perp_inverse */
  kind: string;
  bid?: string;
  ask?: string;
  spread_bp?: string;
  age_ms: number;
  top_why?: string;
  mark?: string;
  mark_why?: string;
  funding_rate?: string;
  /** fraction | percent | undeclared */
  funding_unit?: string;
  funding_interval_s?: number;
  funding_why?: string;
  oi?: string;
  /** base | base_observed | quote | unconfirmed */
  oi_unit?: string;
  oi_why?: string;
}

/** Медиана середин вершин бирж на конец 10-секундного шага. */
export interface CCFeedPricePoint {
  t: string;
  mid: string;
  venues: number;
}

/** Объём тейкеров за минуту по всем биржам, в базовом активе. */
export interface CCFeedTakerMinute {
  t: string;
  buy: string;
  sell: string;
}

export interface CCFeedLiquidation {
  t: string;
  venue: string;
  inst: string;
  /** Сторона ЗАКРЫТОЙ позиции: long | short. */
  side: string;
  price: string;
  qty: string;
}

/** Открытый интерес биржи в начале и в конце окна истории. */
export interface CCFeedOIChange {
  venue: string;
  inst: string;
  unit: string;
  from: string;
  to: string;
  since: string;
}

/** Ответ /ccfeed/{asset}. Сводки нет — null и причина словами, а не ошибка. */
export interface CCFeedResponse {
  summary: CCFeedSummary | null;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Проба predict.fun (ADR-0040)
// ---------------------------------------------------------------------------

/** Стаканы окна на predict.fun. Down — зеркало Up: площадка ведёт одну книгу. */
export interface PFBook {
  slug: string;
  market_id: number;
  title: string;
  trading_status: string;
  network: "testnet" | "mainnet";
  up: Book;
  down: Book;
  /** ws — лента (mainnet), rest — опрос (testnet). */
  source: "ws" | "rest";
  received_at: string;
}

export interface PFBookResponse {
  book: PFBook | null;
  reason?: string;
}

export interface PFWindow {
  slug: string;
  market_id?: number;
  title?: string;
  trading_status?: string;
  fee_rate_bps?: number;
  tick?: string;
}

export interface PFStatus {
  network: "testnet" | "mainnet";
  chain_id: number;
  /** Адрес maker: депозит аккаунта predict.fun или сам EOA. */
  address?: string;
  /** eoa | predict (аккаунт сайта, подпись ключом Privy через Kernel). */
  wallet_kind?: "eoa" | "predict";
  /** Ключ владельца аккаунта predict — только у wallet_kind=predict. */
  owner?: string;
  trading: boolean;
  trading_note: string;
  session: boolean;
  max_order_usdt: string;
  balance_usdt?: string;
  balance_reason?: string;
  feed: { source: string; live: boolean; messages: number; reconnects: number; last_error?: string };
  windows: PFWindow[];
}

export interface PFOrder {
  id: string;
  hash: string;
  market_id: number;
  slug?: string;
  title?: string;
  outcome: string;
  side: Side;
  price: string;
  size: string;
  filled: string;
  /** OPEN | FILLED | EXPIRED | CANCELLED | INVALIDATED */
  status: string;
  strategy: string;
}

export interface PFPlaceRequest {
  slug: string;
  outcome: "Up" | "Down";
  side: Side;
  price: string;
  size: string;
  post_only: boolean;
}

export interface PFPlaceResult {
  order_id: string;
  order_hash: string;
  code: string;
  notional: string;
  /**
   * Всегда ACCEPTED: 201 площадки — «принят к рассмотрению», а не «стоит».
   * Итог — `pfResult` по order_id (ADR-0041).
   */
  status: "ACCEPTED";
  filled: string;
}

/**
 * Итог ордера predict.fun после 201 (ADR-0041). ACCEPTED — итога ещё нет;
 * OPEN — стоит в стакане; FILLED — исполнен (filled долей, reason — если
 * расчёт в сети ещё идёт); PENDING — пересекает стакан, но площадка его не
 * свела; UNCONFIRMED — статус не появился; REJECTED — площадка не поставила
 * ордер (reason — почему, например post-only пересёк стакан).
 */
export interface PFOutcome {
  order_id: string;
  status: "ACCEPTED" | "OPEN" | "FILLED" | "PENDING" | "UNCONFIRMED" | "REJECTED";
  filled: string;
  reason?: string;
  /** ws — итог принесла лента событий кошелька, rest — опрос по хешу. */
  source?: "ws" | "rest";
  /** От отправки ордера сервисом до итога: задержка площадки. */
  after_ms?: number;
}

/** Позиция кошелька predict.fun по исходу окна (строка PF быстрых действий). */
export interface PFPosition {
  outcome: string;
  shares: string;
  avg_price?: string;
  value_usd?: string;
}

/**
 * Итог снятия ордеров predict.fun. filled — площадка ответила «снят», но
 * ордер успел исполниться: позиция есть, денег в стакане нет.
 */
export interface PFCancelResult {
  removed: string[];
  noop: string[];
  filled: string[];
}
